import * as i0 from '@angular/core';
import { EventEmitter, Injectable, ViewContainerRef, Component, ViewChild, Input, Directive, ViewEncapsulation, Output, ViewChildren, NgModule, HostListener } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime, delay } from 'rxjs/operators';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i4 from 'axis-components';
import { Logger, UtilsModule, CheckboxModule, InputModule, RadioButtonModule, LoadingModule, ButtonsModule, IconModule, TooltipModule, Version, TooltipDirective, AxisComponentsAllModule } from 'axis-components';
import { AxisTruncateDirective } from 'axis-components/truncate';
import * as i4$1 from '@angular/cdk/scrolling';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';

const utils = {
    checkObjectProperty,
};
function checkObjectProperty(obj, props) {
    if (props.length === 1) {
        return obj.hasOwnProperty(props[0]);
    }
    if (!obj.hasOwnProperty(props[0])) {
        return false;
    }
    return checkObjectProperty(obj[props[0]], props.slice(1, props.length));
}

class AxisDatagridService {
    constructor() {
        this.grids = {};
        this.defaultConfig = {
            rowHeight: 30,
            cssGridClass: [],
            viewportCellCount: 10,
            bufferRows: 5,
            scrollType: 'normal',
            columnsOrder: [],
            useApiOnScroll: false,
        };
        this.isContentClicked = new BehaviorSubject(false);
        this.customComponentsList = {};
        this.utils = utils;
        this.resizeMethods = [];
    }
    registerNewGrid(name) {
        this.grids[name] = {
            model: { readyToShow: false },
            subscriptions: [],
            listeners: [],
            viewRows: [],
            dataPipeLine: {},
            hasFullDataDump: false,
            lastChanged: 0,
            cells: {},
            _outputEmitter: new EventEmitter(),
            oldScrollPos: -1,
            isEditModeOn: false,
            changeDetection: null,
            firstElement: null,
            lastElement: null,
            totalHeight: null,
            showFrom: 0,
            showTo: 999999999,
            window,
            isFirstLoad: true,
            table: null,
            tbody: null,
            virtualFrame: null,
            registeredPlugins: {
                _sequence: [],
            },
        };
        this.createActionsForGrid(name);
        this.addSubscriptions(name);
    }
    createActionsForGrid(name) {
        this.grids[name]['ACTIONS'] = {
            STAGE_1: new BehaviorSubject(false),
            STAGE_2: new BehaviorSubject(false),
            SCROLL: new BehaviorSubject(false),
            WINDOW_RESIZE: new BehaviorSubject(false),
            UPDATE_GRID: new BehaviorSubject(false),
            GRID_HAS_CHANGED: new BehaviorSubject(false),
            EVENT: new BehaviorSubject(false),
            ROW_SELECTED: new BehaviorSubject(false),
            ROW_UNSELECTED: new BehaviorSubject(false),
            ROWS_SELECTED_ALL: new BehaviorSubject(false),
            ROWS_UNSELECTED_ALL: new BehaviorSubject(false),
            SELECT_ALL_COMPONENT: new BehaviorSubject(false),
            SELECT_ALL_DIRECTIVE: new BehaviorSubject(false),
            INSERT_ROW: new BehaviorSubject(false),
            UPDATE_ROW: new BehaviorSubject(false),
            ROWS_CLICKED: new BehaviorSubject(false),
            REDRAW: new BehaviorSubject(false),
            VIRTUAL_SCROLL_STAGE_3: new BehaviorSubject(false),
            DELETED_ROWS: new BehaviorSubject(false),
            CUSTOM: new BehaviorSubject(false),
            COL_DRAGGED: new BehaviorSubject(false),
            GRID_CHANGED: new BehaviorSubject(false),
            HEADER_CLICKED: new BehaviorSubject(false),
            EXPORT: new BehaviorSubject(false)
        };
    }
    addSubscriptions(name) {
        // On Window Resize (only one event bind)
        const RESIZE = fromEvent(window, 'resize')
            .pipe(debounceTime(400))
            .subscribe(() => {
            this.sendAction(name, 'WINDOW_RESIZE', true);
        });
        this.grids[name].subscriptions.push(RESIZE);
    }
    isClicked() {
        this.isContentClicked.next({});
    }
    addFullDataDump(res) {
        const name = res.name;
        this.grids[name].dataPipeLine[20] = this.fullDataDump.bind(this);
        if (this.grids[name].model.config.scrollType === 'normal') {
            this.changeLastChanged(name);
            this.getRows(name);
        }
    }
    gridReady(name) {
        const model = this.grids[name].model;
        model.readyToShow = true;
        model.lastChanged = 0;
    }
    mergeDefaultConfig(name) {
        const grid = this.grids[name];
        Object.keys(this.defaultConfig).forEach((k) => {
            if (!grid.model.config.hasOwnProperty(k)) {
                if (Array.isArray(this.defaultConfig[k])) {
                    grid.model.config[k] = [...this.defaultConfig[k]];
                }
                else {
                    grid.model.config[k] = this.defaultConfig[k];
                }
            }
        });
    }
    typeOf(c) {
        return typeof c;
    }
    update(rows, name, totalCount) {
        const grid = this.grids[name];
        grid.model.lastChanged = new Date();
        grid.model.body.rows = JSON.parse(JSON.stringify(rows));
        grid.model.body.config.totalCount = totalCount || grid.model.body.rows.length;
        this.getRows(name);
    }
    getRows(name) {
        const grid = this.grids[name];
        // No change, send last viewRows
        if (grid.model.lastChanged === grid.lastChanged) {
            return grid.viewRows;
        }
        const keys = Object.keys(grid.dataPipeLine)
            .map((x) => x * 1)
            .sort((a, b) => a - b);
        keys.forEach((k) => {
            grid.dataPipeLine[k](name);
        });
        this.columnOrder(name);
        if (grid.model.config.hasOwnProperty('scrollType') && grid.model.config.scrollType === 'virtual') {
            grid.viewRows = [...grid.viewRows];
        }
        return grid.viewRows;
    }
    columnOrder(name) {
        const grid = this.grids[name];
        grid.model.config.columnsOrder = [];
        const count = grid.model.body.rows[0]?.cells?.length;
        for (let i = 0; i < count; i++) {
            grid.model.config.columnsOrder[i] =
                grid.model.body.rows[0].cells[i].mapTo;
        }
    }
    currentTopRow(name) {
        const grid = this.grids[name];
        if (grid.tbody) {
            return Math.floor(grid.tbody.nativeElement.scrollTop / (grid.model.config.rowHeight + 36));
        }
        return 0;
    }
    calculate(name, preDefTop = null) {
        const grid = this.grids[name];
        if (!grid) {
            return;
        }
        const topRow = preDefTop ? preDefTop : this.currentTopRow(name);
        // FROM
        let tempShowFrom = -grid.model.config.bufferRows;
        if (tempShowFrom < 0) {
            tempShowFrom = 0;
        }
        grid.showFrom = tempShowFrom;
        // TO
        let tempShowTo = topRow +
            grid.model.config.viewportCellCount +
            grid.model.config.bufferRows;
        if (tempShowTo > grid.model.body.config.totalCount) {
            tempShowTo = grid.model.body.config.totalCount;
        }
        if (!grid.model.config.useApiOnScroll) {
            grid.showTo = tempShowTo;
        }
        // Window
        grid.window =
            grid.model.config.viewportCellCount + grid.model.config.bufferRows * 2;
    }
    registerCustomComponent(name, component) {
        this.customComponentsList[name] = component;
    }
    sendUpdate(name, data) {
        this.changeLastChanged(name);
        this.grids[name]._outputEmitter.emit(data);
    }
    changeLastChanged(name) {
        const grid = this.grids[name];
        grid.lastChanged = Date.now();
    }
    sendAction(name, action, payload) {
        if (this.grids[name] && action) {
            this.grids[name].ACTIONS[action].next({ action, payload });
            this.grids[name]._outputEmitter.emit({ name, action, payload });
        }
    }
    getAction(name, action) {
        return this.grids[name].ACTIONS[action];
    }
    fullDataDump(name) {
        const grid = this.grids[name];
        const newRows = [];
        if (grid.lastChanged && grid.model.lastChanged !== grid.lastChanged) {
            grid.viewRows = grid.model.body.rows.slice(0);
        }
        grid.viewRows.forEach((r) => newRows.push(r.rowIndex));
        setTimeout(() => {
            this.sendAction(name, 'GRID_HAS_CHANGED', {
                name,
                grid: this.grids[name],
                newRows,
            });
        }, 10);
        grid.changeDetection.detectChanges();
        return grid.viewRows;
    }
    deleteRow(rowsData, gridName) {
        let totalCount = 0;
        const grid = this.grids[gridName];
        if (!rowsData) {
            return;
        }
        else if (!Array.isArray(rowsData)) {
            rowsData = [rowsData];
            totalCount = rowsData.length;
        }
        rowsData.forEach((row) => {
            const rowIndex = grid.model.body.rows.findIndex((r) => r.rowIndex === row.rowIndex);
            if (rowIndex !== -1) {
                grid.model.body.rows.splice(rowIndex, 1);
            }
        });
        grid.model.body.config.totalCount = grid.model.body.config.totalCount - totalCount;
        this.getRows(gridName);
        this.sendAction(gridName, 'DELETED_ROWS', { name: gridName, data: rowsData });
    }
    updateRow(rowData, gridName) {
        const grid = this.grids[gridName];
        if (!rowData) {
            return;
        }
        if (!Array.isArray(rowData)) {
            rowData = [rowData];
        }
        rowData.forEach((row) => {
            const rowIndex = grid.model.body.rows.findIndex((r) => r.rowIndex === row.rowIndex);
            if (rowIndex !== -1) {
                grid.model.body.rows[rowIndex] = row;
            }
        });
        this.getRows(gridName);
        this.sendAction(gridName, 'UPDATE_ROW', { name: gridName, data: rowData });
    }
    addRow(rowData, gridName) {
        let totalCount = 0;
        const grid = this.grids[gridName];
        if (!rowData) {
            return;
        }
        if (!Array.isArray(rowData)) {
            rowData = [rowData];
            totalCount = rowData.length;
        }
        rowData.forEach((row) => {
            row.rowIndex = grid.model.body.rows.length;
            grid.model.body.rows.push(row);
        });
        grid.model.body.config.totalCount = grid.model.body.config.totalCount + totalCount;
        this.getRows(gridName);
        this.sendAction(gridName, 'INSERT_ROW', { name: gridName, data: rowData });
    }
    toggleColumn(name, columnName) {
        const grid = this.grids[name];
        grid.model.header.rows[0].cells.forEach((element) => {
            if (element.alias === columnName) {
                if (!element.hideColumn) {
                    element.hideColumn = true;
                    if (grid.model.config.hasOwnProperty('columnsCount')) {
                        grid.model.config.columnsCount += 1;
                    }
                }
                else {
                    element.hideColumn = false;
                    if (grid.model.config.hasOwnProperty('columnsCount')) {
                        grid.model.config.columnsCount -= 1;
                    }
                    this.sendAction(name, 'GRID_CHANGED', { name, column: columnName });
                }
            }
        });
        const payload = {
            name
        };
        this.sendAction(name, 'REDRAW', payload);
    }
    export(name, type, table) {
        if (type === 'excel') {
            const transposedData = [];
            const data = this.getRows(name);
            data.forEach(element => {
                const obj = {};
                element.cells.forEach((cell) => {
                    const value = cell.pureContent || cell.content;
                    if (value) {
                        obj[cell.mapTo] = value;
                    }
                    if (cell.subColumns) {
                        const value = cell.subColumns[0].pureContent || cell.subColumns[0].content;
                        if (value) {
                            obj[cell.subColumns[0].mapTo] = value;
                        }
                    }
                });
                transposedData.push(obj);
            });
            this.sendAction(name, 'EXPORT', { name, data: transposedData, type });
        }
        else if (type === 'pdf') {
            this.sendAction(name, 'EXPORT', { name, data: table, type });
        }
    }
    destroyGrid(name) {
        this.grids[name].subscriptions.forEach((u) => {
            if (u) {
                u.unsubscribe();
            }
        });
        this.grids[name].listeners.forEach((_u) => {
            // Remove listener
            // SHERIN
        });
        delete this.grids[name];
    }
}
AxisDatagridService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AxisDatagridService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AxisDatagridCustomComponent {
    constructor(gridService) {
        this.gridService = gridService;
        this.uiStructure = null;
        this.uiData = null;
        this.name = null;
    }
    ngAfterViewInit() {
        if (this.uiStructure && this.uiStructure.content && this.uiStructure.content.indexOf('Component') > -1) {
            this.addElement(this.uiStructure.content);
        }
        if (this.uiStructure && this.uiStructure.content && this.uiStructure.content.indexOf('Component') === -1) {
            this.addHtml(this.uiStructure.content);
        }
    }
    addElement(elementName) {
        const componentRef = this.elementContainer.createComponent(this.gridService.customComponentsList[elementName]);
        componentRef.instance['uiStructure'] = this.uiStructure;
        componentRef.instance['uiData'] = this.uiData;
        componentRef.instance['name'] = this.name;
        componentRef.changeDetectorRef.detectChanges();
    }
    addHtml(htmlContent) {
        this.htmlContainer.nativeElement.innerHTML = htmlContent;
    }
}
AxisDatagridCustomComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridCustomComponent, deps: [{ token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Component });
AxisDatagridCustomComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: AxisDatagridCustomComponent, selector: "axis-datagrid-custom", inputs: { uiStructure: "uiStructure", uiData: "uiData", name: "name" }, viewQueries: [{ propertyName: "elementContainer", first: true, predicate: ["elementContainer"], descendants: true, read: ViewContainerRef, static: true }, { propertyName: "htmlContainer", first: true, predicate: ["htmlContainer"], descendants: true }], ngImport: i0, template: "<div class=\"axis-datagrid-custom-control\">\n  <ng-container #elementContainer></ng-container>\n  <span #htmlContainer></span>\n</div>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridCustomComponent, decorators: [{
            type: Component,
            args: [{ selector: 'axis-datagrid-custom', template: "<div class=\"axis-datagrid-custom-control\">\n  <ng-container #elementContainer></ng-container>\n  <span #htmlContainer></span>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: AxisDatagridService }]; }, propDecorators: { elementContainer: [{
                type: ViewChild,
                args: ['elementContainer', { read: ViewContainerRef, static: true }]
            }], htmlContainer: [{
                type: ViewChild,
                args: ['htmlContainer']
            }], uiStructure: [{
                type: Input
            }], uiData: [{
                type: Input
            }], name: [{
                type: Input
            }] } });

class AxisGridGenericCellDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
    }
    ngOnInit() {
        this.name = this.el.nativeElement.getAttribute('gridName');
        const gs = this.gridService;
        this.grid = gs.grids[this.name];
        this.startDirective();
    }
    startDirective() {
        const index = this.el.nativeElement.getAttribute('cell-index');
        this.grid.cells[index] = this.el;
    }
}
AxisGridGenericCellDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisGridGenericCellDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AxisGridGenericCellDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AxisGridGenericCellDirective, selector: "[axisDatagridGenericCell]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisGridGenericCellDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[axisDatagridGenericCell]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; } });

class AxisDatagridTableComponent {
    constructor(gridService, cd, router) {
        this.gridService = gridService;
        this.cd = cd;
        this.router = router;
        this.model = null;
        this.output = new EventEmitter();
        this.allSelected = false;
        this._bs = new BehaviorSubject(false);
        this.BS = this._bs.asObservable();
        this.tableLoading = true;
        this.columnsFromAutoWidth = {};
        Logger.log('TABLE - Constructor');
    }
    ngOnInit() {
        Logger.log('TABLE - Init');
        // STEP 1
        this.prepareGrid();
        // STEP 2
        this.addSubscriptions();
        // STEP 3
        this.startGrid();
    }
    prepareGrid() {
        // As soon as data comes in, prepare model and variables
        const gs = this.gridService;
        this.defineName();
        this.name = this.model.config.name;
        this.model.config['grid-type'] = 'table-grid';
        gs.registerNewGrid(this.name);
        gs.grids[this.name].model = this.model;
        gs.grids[this.name]._outputEmitter = this.output;
        gs.grids[this.name].changeDetection = this.cd;
        this.grid = gs.grids[this.name];
        gs.mergeDefaultConfig(this.name);
        if (this.model.useApiOnScroll) {
            this.grid.showTo = this.model.body.rows.length;
        }
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
                if (res && this.name === res.payload.name) {
                    this.init(res);
                }
            });
            gs.subscriptions.push(STAGE_1);
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                this.handleSubResult(res);
            });
            gs.subscriptions.push(STAGE_2);
            const REDRAW = gs.ACTIONS.REDRAW.pipe(delay(0)).subscribe((res) => {
                this.handleSubResult(res);
            });
            gs.subscriptions.push(REDRAW);
            const SCROLL = gs.ACTIONS.SCROLL.pipe(delay(0)).subscribe((res) => {
                if (res && res.payload.name === this.name) {
                    const isAllSelected = this.grid.viewRows.every(x => x.selected);
                    if (!isAllSelected && this.checkAll && this.checkAll.checked) {
                        this.checkAll.unCheck();
                    }
                }
            });
            gs.subscriptions.push(SCROLL);
            const COL_DRAGGED = gs.ACTIONS.COL_DRAGGED.pipe(delay(0)).subscribe((res) => {
                this.handleSubResult(res);
            });
            gs.subscriptions.push(COL_DRAGGED);
        }
    }
    handleSubResult(res) {
        if (res && res.payload.name === this.name) {
            this.afterInit(res);
        }
    }
    startGrid() {
        const gs = this.gridService;
        const data = this.getData();
        // STEP 4
        Logger.log('TABLE - addFullDataDump');
        gs.addFullDataDump(data);
    }
    ngAfterViewInit() {
        Logger.log('TABLE - After Init');
        this.getElementsRefs();
        const gs = this.gridService;
        const data = this.getData();
        // STEP 5
        Logger.log('TABLE - send STAGE_1');
        gs.sendAction(this.name, 'STAGE_1', data);
        setTimeout(() => {
            // STEP 6
            Logger.log('TABLE - send STAGE_2');
            this.gridService.sendAction(this.name, 'STAGE_2', data);
            let totalWidth = 0;
            let minWidth = 99999999;
            if (this.thisTable) {
                this.thisTable.nativeElement.querySelectorAll('thead th').forEach(th => {
                    const x = th.clientWidth;
                    this.columnsFromAutoWidth[th.getAttribute('col-index')] = x;
                    totalWidth += x;
                    if (x < minWidth) {
                        minWidth = x;
                    }
                });
            }
            const tableStyle = document.createElement('style');
            tableStyle.id = `axis-grid-${this.name}-style`;
            Object.keys(this.columnsFromAutoWidth).forEach(keyIndex => {
                const thisColumnWidth = this.columnsFromAutoWidth[keyIndex];
                const flex = (thisColumnWidth * 100) / totalWidth;
                tableStyle.innerHTML += `
        div[axis-grid-name='${this.name}'] th[col-index='${keyIndex}'],
        div[axis-grid-name='${this.name}'] td[col-index='${keyIndex}'] {
          flex: ${flex} !important;
        }
        `;
            });
            if (this.tableLoading) {
                this.tableLoading = false;
                if (this.thisTable) {
                    this.thisTable.nativeElement.parentElement.appendChild(tableStyle);
                }
            }
        }, 0);
    }
    getData() {
        const gs = this.gridService;
        return {
            name: this.name,
            type: 'table-grid',
            grid: gs.grids[this.name],
        };
    }
    getElementsRefs() {
        this.gridService.grids[this.name].thisGrid = this.thisTable;
        this.gridService.grids[this.name].tbody = this.tbody;
        this.gridService.grids[this.name].virtualFrame = this.virtualFrame;
    }
    changeGridCss(name) {
        const gs = this.gridService;
        const model = gs.grids[name].model;
        if (this.thisTable) {
            let gridWidth = this.thisTable.nativeElement.clientWidth;
            let minGridWidth = 0;
            let maxGridWidth = 0;
            const baseline = { min: 50, max: 250, width: 150, fraction: 1 };
            let totalFraction = 0;
            model.header.rows[0].cells.forEach((cw) => {
                if (!cw.hasOwnProperty('columnsWidth')) {
                    cw.columnsWidth = Object.assign({}, baseline);
                }
                if (!cw.hideColumn) {
                    minGridWidth += cw.columnsWidth.min ? parseInt(cw.columnsWidth.min.match(/(\d+)/)[0], 10) : 0;
                    maxGridWidth += cw.columnsWidth.max ? parseInt(cw.columnsWidth.max.match(/(\d+)/)[0], 10) : 0;
                    totalFraction += cw.columnsWidth.fraction;
                }
            });
            if (gridWidth < minGridWidth) {
                gridWidth = minGridWidth;
            }
            else if (gridWidth > maxGridWidth) {
                gridWidth = maxGridWidth;
            }
            const unit = Math.floor((gridWidth) / totalFraction);
            model.header.rows[0].cells.forEach((cw) => {
                let newWidth = unit * cw.columnsWidth.fraction;
                const minColWidth = cw.columnsWidth.min ? parseInt(cw.columnsWidth.min.match(/(\d+)/)[0], 10) : 0;
                const maxColWidth = cw.columnsWidth.max ? parseInt(cw.columnsWidth.max.match(/(\d+)/)[0], 10) : 0;
                if (newWidth < minColWidth) {
                    newWidth = minColWidth;
                }
                if (newWidth > maxColWidth) {
                    newWidth = maxColWidth;
                }
                cw.columnsWidth.realWidth = cw.columnsWidth.width ? newWidth + 'px' : null;
            });
        }
        this.gridService.gridReady(this.name);
    }
    defineName() {
        if (!this.model.config.hasOwnProperty('name')) {
            this.model.config.name =
                'NoNameGrid-' + Math.floor(Math.random() * 10000);
            Logger.warn('Axis Grid', 'Model does not have the property "config.name"', `Temporarily set to ${this.model.config.name}`);
        }
    }
    init(_res) {
        return _res;
    }
    afterInit(_res) {
        setTimeout(() => {
            this.changeGridCss(this.name);
            this.cd.detectChanges();
        }, 0);
    }
    selectAllRows(event, disabled) {
        if (!disabled) {
            this.allSelected = event;
            const selectedList = [];
            this.grid.viewRows.forEach((element) => {
                if (!element.cells[0].disabled && !element.cells[0].hide) {
                    element.selected = event;
                    if (event) {
                        selectedList.push(element);
                    }
                }
            });
            if (this.check && this.check.length) {
                this.check.forEach((element) => {
                    if (event) {
                        if (!element.checked && !element.disabled && !element.hide) {
                            element.check();
                        }
                    }
                    else {
                        element.unCheck();
                    }
                });
            }
            if (this.allSelected) {
                this.gridService.sendAction(this.name, 'ROWS_SELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
            else {
                this.gridService.sendAction(this.name, 'ROWS_UNSELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
        }
    }
    preventClickEmit(event) {
        if (event.clientX) {
            event.preventDefault();
        }
    }
    rowClicked(row) {
        const headerCell = this.model?.header?.rows[0].cells.find((cell) => (cell.type === 'checkbox' || cell.type === 'radio'));
        if (headerCell && ((headerCell.type === 'checkbox') || (headerCell.type === 'radio' && !row.selected))) {
            const checked = (row.hasOwnProperty('selected') && row.selected);
            const hide = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('hide') && cell.hide)));
            const disabled = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('disabled') && cell.disabled)));
            this.selectRow(checked, row, this.model?.config?.disableAllRadio || disabled, hide, headerCell.type);
        }
    }
    selectRow(event, row, disabled, hide, type) {
        if (!disabled && !hide) {
            const selectedList = [];
            this.allSelected = true;
            this.grid.viewRows.forEach((element) => {
                if (type == 'radio') {
                    element.selected = false;
                }
                if (element.rowIndex === row.rowIndex) {
                    element.selected = !event;
                }
                if (type === 'checkbox') {
                    if (element.selected) {
                        selectedList.push(element);
                    }
                    else {
                        this.allSelected = false;
                    }
                }
                if (element.rowIndex === row.rowIndex) {
                    if (element.selected) {
                        this.gridService.sendAction(this.name, 'ROW_SELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: type === 'checkbox' ? selectedList : row,
                            rowIndex: element.rowIndex
                        });
                    }
                    else {
                        this.gridService.sendAction(this.name, 'ROW_UNSELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: type === 'checkbox' ? selectedList : row,
                            rowIndex: element.rowIndex
                        });
                    }
                }
            });
            if (type === 'checkbox') {
                if (this.allSelected && this.checkAll && !this.checkAll.checked) {
                    this.checkAll.check();
                }
                else if (!this.allSelected && this.checkAll && this.checkAll.checked) {
                    this.checkAll.unCheck();
                }
            }
        }
    }
    contentClicked(row, columnName, cell) {
        if (cell.route) {
            this.router.navigate([cell.route], { ...cell });
        }
        const payload = {
            name: this.name,
            content: columnName,
            rowsData: row,
            cell
        };
        this.gridService.isClicked();
        this.gridService.sendAction(this.name, 'ROWS_CLICKED', payload);
    }
    headerColumnClicked(column, subColumn = null) {
        if ((column.hasOwnProperty('isHeaderClickable') && column['isHeaderClickable']) || (subColumn && subColumn.hasOwnProperty('isHeaderClickable') && subColumn['isHeaderClickable'])) {
            const payload = {
                name: this.name,
                column: column,
                subColumnName: subColumn ? subColumn.alias : ''
            };
            this.gridService.sendAction(this.name, 'HEADER_CLICKED', payload);
        }
    }
    toggleSubGrid(cell) {
        cell.showAll = !cell.showAll;
        this.grid.viewRows.forEach((v) => {
            v.showNestedGrid = cell.showAll;
        });
        this.cd.detectChanges();
    }
    expandSubRow(event, row) {
        event?.stopPropagation();
        if (!this.model?.config?.nestedGridOptions?.expandOthers) {
            this.grid.viewRows.forEach((v) => {
                v.showNestedGrid = row.rowIndex == v.rowIndex ? v.showNestedGrid : false;
            });
        }
        row.showNestedGrid = !row.showNestedGrid;
    }
    export(type) {
        this.gridService.export(this.name, type, this.thisTable);
    }
    ngOnDestroy() {
        this.gridService.destroyGrid(this.name);
    }
}
AxisDatagridTableComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridTableComponent, deps: [{ token: AxisDatagridService }, { token: i0.ChangeDetectorRef }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component });
AxisDatagridTableComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: AxisDatagridTableComponent, selector: "axis-datagrid-table", inputs: { model: "model" }, outputs: { output: "output" }, viewQueries: [{ propertyName: "thisTable", first: true, predicate: ["thisTable"], descendants: true }, { propertyName: "tbody", first: true, predicate: ["tbody"], descendants: true }, { propertyName: "virtualFrame", first: true, predicate: ["virtualFrame"], descendants: true, read: ViewContainerRef, static: true }, { propertyName: "checkAll", first: true, predicate: ["checkAll"], descendants: true }, { propertyName: "check", predicate: ["check"], descendants: true }], ngImport: i0, template: "<div\n  class=\"axis-grid-container\"\n  [ngClass]=\"{'axis-grid-container-fixed': grid.registeredPlugins.fixed && grid.model.config.gridHeight, 'axis-grid-container-loading': tableLoading}\"\n  [attr.axis-grid-name]=\"grid.model.config.name\"\n>\n  <div\n    class=\"axis-grid-loading\"\n    [style.visibility]=\"model.readyToShow ? 'hidden' : 'inherit'\"\n  >\n  <ng-container *ngIf=\"!model.config.loading\">Loading...</ng-container>\n    <ng-container *ngIf=\"model.config.loading\">\n      <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'loading', content: model.config.loading}\"\n            name=\"loading\">\n    </axis-datagrid-custom>\n    </ng-container>\n  </div>\n  <div [style.visibility]=\"!model.readyToShow ? 'hidden' : 'inherit'\">\n    <div #virtualFrame></div>\n    <ng-container *ngIf=\"model.config.scrollType !== 'virtual'\">\n      <div class=\"axis-grid\" *ngIf=\"grid?.model?.config?.download\">\n        <button *ngIf=\"grid?.model?.config?.download?.excel\" class=\"axis-btn\" (click)=\"export('excel')\">Export To Excel</button>\n        <button *ngIf=\"grid?.model?.config?.download?.pdf\" class=\"axis-btn\" (click)=\"export('pdf')\">Export To PDF</button>\n      </div>\n      <div\n        class=\"axis-table-responsive\"\n        [ngClass]=\"grid ? grid.model?.config?.cssGridParent : []\"\n        [attr.data-size]=\"grid?.model?.config?.dataSize ? grid?.model?.config.dataSize : null\"\n      >\n        <table\n          [attr.aria-describedby]=\"model.config.caption ? model.config.name + '_caption' : null\"\n          class=\"axis-table axis-table-basic\"\n          [ngClass]=\"grid ? grid.model?.config?.cssGridClass : []\"\n          [attr.aria-rowcount]=\"model.body.rows.length\"\n          role=\"grid\"\n          #thisTable\n        >\n          <caption *ngIf=\"model.config.caption\" id=\"{{model.config.name}}_caption\">\n            {{model.config.caption ? model.config.caption : ''}}\n          </caption>\n\n          <thead\n            role=\"rowgroup\"\n            *ngIf=\"model.header.rows.length > 0  && !model.header.config.hidden\"\n            class=\"axis-datagrid-head axis-grid-is-header\">\n            <tr\n              aria-rowindex=\"1\"\n              role=\"row\"\n              *ngFor=\"let row of model.header.rows\" [ngClass]=\"row.class\"\n            >\n              <ng-container  *ngFor=\"let cell of row.cells; let i = index\">\n                <th\n                  [attr.role]=\"'columnheader'\"\n                  [attr.aria-hidden]= \"(!cell.content?.trim('') && i!= 0) || (i === 0 && (cell.type === 'radio' || (cell.type === 'checkbox' && cell.showSelectAll === false)) || (!cell.content?.trim('') && cell.type != 'radio' && cell.type != 'checkbox'))  ? 'true' : null\"\n                  [ngClass]=\"cell.class\"\n                  class=\"axis-datagrid-cell axis-datagrid-cell-header\"\n                  scope=\"col\"\n                  *ngIf=\"!cell.hideColumn\"\n                  [ngStyle]=\"{\n                    'min-width': cell.columnsWidth?.min ? cell.columnsWidth.min : null,\n                    'max-width': cell.columnsWidth?.max ? cell.columnsWidth.max: null\n                  }\"\n                  [attr.col-index]=\"i\"\n                  [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n                >\n                  <ng-container *ngIf=\"cell.type !== 'checkbox' && cell.type !== 'radio' && cell.type !== 'expand'\">\n                    <span\n                      class=\"axis-grid-content\"\n                      [attr.alias]=\"cell.alias\"\n                      [attr.aria-label]=\"cell.ariaLabel\"\n                      role=\"status\"\n                      [attr.type]=\"cell.type\"\n                      [attr.initial-index]=\"i\"\n                      draggable=\"{{cell.draggable}}\"\n                      [ngClass]=\"[ cell.content ? '' : '_invisibility-md-up', cell?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                      (click)=\"headerColumnClicked(cell)\">\n                      <span [innerHtml]=\"cell.content || cell['data-heading']\"></span>\n                      <span *ngIf=\"cell.icon && cell.icon.name\" [attr.aria-label]=\"cell.icon.ariaLabel\" axis-tooltip [nested]=\"true\" [width]=\"cell.icon?.tooltipWidth\" [content]=\"(cell.icon && cell.icon.tooltipContent) ? cell.icon.tooltipContent : ''\">\n                        <axis-icon [icon]=\"cell.icon.name\" [data-size]=\"cell.icon?.iconSize\" [data-color]=\"cell.icon?.iconColor\" [font]=\"cell.icon?.iconFont || 'material'\"></axis-icon>\n                      </span>\n                      </span\n                    >\n                    <ng-container *ngIf=\"cell.subColumns\">\n                      <ng-container *ngFor=\"let sub of cell.subColumns\">\n                        <div\n                          class=\"axis-grid-content\"\n                          [attr.alias]=\"sub.alias\"\n                          [attr.type]=\"cell.type\"\n                          [attr.initial-index]=\"i\"\n                          role=\"status\"\n                          [attr.aria-label]=\"sub.ariaLabel\"\n                          [ngClass]=\"[sub?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                          (click)=\"headerColumnClicked(cell, sub)\">\n                          <span [innerHtml]=\"sub.content\"></span>\n                          <span *ngIf=\"sub.icon && sub.icon.name\" axis-tooltip [width]=\"sub.icon?.tooltipWidth\" [attr.aria-label]=\"sub.icon.ariaLabel\" [content]=\"(sub.icon && sub.icon.tooltipContent) ? sub.icon.tooltipContent : ''\">\n                            <axis-icon [icon]=\"sub.icon.name\" [data-size]=\"sub.icon?.iconSize\" [data-color]=\"sub.icon?.iconColor\" [font]=\"sub.icon?.iconFont || 'material'\"></axis-icon>\n                          </span>\n                          </div\n                        >\n                      </ng-container>\n                    </ng-container>\n                  </ng-container>\n                  <ng-container *ngIf=\"cell.type === 'checkbox' && cell.showSelectAll !== false\">\n                    <span class=\"axis-grid-content\">\n                      <axis-checkbox #checkAll\n                        [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n                        [attr.data-inline]=\"cell.inline ? true : null\"\n                        id=\"axis-grid-checkbox-header-{{ i }}\"\n                        [ariaLabel]=\"cell.content?.trim('') ? cell.content : 'Select Rows'\"\n                        (changeDetect)=\"selectAllRows($event, model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled)\"\n                        data-heading=\"{{ cell.content }}\"\n                      ></axis-checkbox>\n                    </span>\n                    </ng-container>\n                    <ng-container *ngIf=\"cell.type === 'checkbox' && !cell.showSelectAll && cell.content\">\n                      <span class=\"axis-grid-content\">\n                        {{cell.content}}\n                        </span>\n                    </ng-container>\n                    <ng-container *ngIf=\"cell.type === 'radio'\">\n                      <span class=\"axis-grid-content\">\n                        {{cell.content}}\n                      </span>\n                    </ng-container>\n                    <ng-container *ngIf=\"cell.type === 'expand' && model?.config?.nestedGridOptions?.expandOthers\">\n                      <span class=\"axis-grid-content\">\n                        <button axis-button (click)=\"toggleSubGrid(cell)\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n                          ? 'Expanded All'\n                          : 'Collapsed All'\">\n                          <axis-icon\n                              [icon]=\"cell.showAll\n                                ? (model?.config?.nestedGridOptions?.expandLessIcon ? model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter' )\n                                : (model?.config?.nestedGridOptions?.expandMoreIcon ? model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n                              [data-size]=\"model?.config?.nestedGridOptions?.iconSize\"\n                              [data-color]=\"model?.config?.nestedGridOptions?.iconColor\"\n                              [font]=\"model?.config?.nestedGridOptions?.iconFont || 'custom'\">\n                            </axis-icon>\n                        </button>\n                      </span>\n                    </ng-container>\n                </th>\n              </ng-container>\n            </tr>\n          </thead>\n\n          <tbody #tbody [ngStyle]=\"{'height': grid.registeredPlugins.fixed && grid.model.config.gridHeight ? grid.model.config.gridHeight : null}\">\n            <ng-container\n              *ngFor=\"\n                let row of grid.viewRows;\n                let r = index;\n              \"\n            >\n              <tr\n                #thisRow\n                [attr.aria-rowindex]=\"r + 2\"\n                [ngClass]=\"[(row.selected ? 'row-active': ''), (row.config.background ? 'row-' + row.config.background : ''),(model.header.rows[0].class ? model.header.rows[0].class : ''), ((row?.nestedGridComponent && row?.showNestedGrid) ? 'nested-row' : '')]\"\n                role=\"row\"\n                [attr.row-index]=\"r\"\n                [attr.row-real-index]=\"row.rowIndex\"\n                (click)=\"rowClicked(row)\"\n              >\n                <ng-container\n                  *ngFor=\"let cell of row.cells; let c = index\"\n                  #cell\n                >\n                  <ng-container *ngIf=\"!model?.header?.rows[0]?.cells[c]?.hideColumn\">\n                    <td\n                      #thisCell\n                      *ngIf=\"!model?.header?.rows[0]?.cells[c]?.type || model?.header?.rows[0]?.cells[c]?.type !== 'custom'\"\n                      [attr.row-index]=\"r\"\n                      [attr.no-truncate]=\"model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n                      axisDatagridGenericCell\n                      [attr.gridName]=\"model.config.name\"\n                      class=\"axis-datagrid-cell\"\n                      [ngClass]=\"[\n                        cell.cellClass ? cell.cellClass : '',\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model?.header?.rows[0]?.cells[c]?.class ? model.header.rows[0].cells[c].class : ''\n                      ]\"\n                      role=\"gridcell\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c] ? (model?.header?.rows[0]?.cells[c]['data-heading'] || model?.header?.rows[0]?.cells[c].content) : null\"\n                      [attr.data-content]=\"row.cells[c]['data-content'] || null\"\n                      [attr.cell-index]=\"r + '.' + c\"\n                      [ngStyle]=\"{\n                        'width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.width ? model?.header?.rows[0]?.cells[c].columnsWidth.width : null,\n                        'min-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.min ? model?.header?.rows[0]?.cells[c].columnsWidth.min : null,\n                        'max-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.max ? model?.header?.rows[0]?.cells[c].columnsWidth.max: null\n                      }\"\n                      [attr.col-index]=\"c\"\n                      [attr.cell-type]=\"cell.type\"\n                      [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n                    >\n                      <ng-container\n                        *ngTemplateOutlet=\"content; context: { c: cell, r: row, i: c, type: model?.header?.rows[0]?.cells[c]?.type, settings:model?.header?.rows[0]?.cells[c]?.settings  }\"\n                      ></ng-container>\n                    </td>\n                    <td\n                      #thisCell\n                      *ngIf=\"model?.header?.rows[0]?.cells[c]?.type && model?.header?.rows[0]?.cells[c]?.type === 'custom'\"\n                      axisDatagridGenericCell\n                      [attr.gridName]=\"model.config.name\"\n                      class=\"axis-datagrid-cell\"\n                      [attr.col-index]=\"c\"\n                      [ngClass]=\"[\n                        cell.cellClass ? cell.cellClass : '',\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model.header.rows[0].cells[c].class ? model.header.rows[0].cells[c].class : ''\n                      ]\"\n                      role=\"gridcell\"\n                      [ngStyle]=\"{\n                        'width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.width ? model?.header?.rows[0]?.cells[c].columnsWidth.width : null,\n                        'min-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.min ? model?.header?.rows[0]?.cells[c].columnsWidth.min : null,\n                        'max-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.max ? model?.header?.rows[0]?.cells[c].columnsWidth.max: null\n                      }\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c] ? (model?.header?.rows[0]?.cells[c]['data-heading'] || model?.header?.rows[0]?.cells[c].content) : null\"\n                      [attr.data-content]=\"row.cells[c]['data-content'] || null\"\n                      [attr.cell-index]=\"r + '.' + c\"\n                      [attr.cell-type]=\"cell.type\"\n                    >\n                      <axis-datagrid-custom\n                        [uiStructure]=\"cell\"\n                        [uiData]=\"row\"\n                        [name]=\"name\"\n                      ></axis-datagrid-custom>\n                    </td>\n                  </ng-container>\n                </ng-container>\n              </tr>\n\n              <tr class=\"nested-table\" *ngIf=\"(row?.nestedGridComponent && row?.showNestedGrid)\">\n                <td [attr.colspan]=\"row.cells.length\">\n                  <axis-datagrid-custom [uiData]=\"row?.nestedGridData\" [uiStructure]=\"{row, content: row.nestedGridComponent}\" [name]=\"row?.nestedGridData?.config?.name\"></axis-datagrid-custom>\n                </td>\n              </tr>\n\n\n            </ng-container>\n          </tbody>\n        </table>\n\n        <ng-container *ngIf=\"!model.body.rows.length || !grid?.viewRows?.length\">\n          <div class=\"axis-datagrid-row axis-datagrid-no-results\" role=\"row\">\n            <div *ngIf=\"!model.config.noResultsUi\">No results.</div>\n            <div *ngIf=\"model.config.noResultsUi\">\n              <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: model.config.noResultsUi}\"\n                name=\"NoResults\">\n              </axis-datagrid-custom>\n            </div>\n          </div>\n        </ng-container>\n      </div>\n    </ng-container>\n  </div>\n</div>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\" let-settings=\"settings\">\n  <ng-container *ngIf=\"type === 'expand' && row?.nestedGridComponent\">\n    <button axis-button (click)=\"expandSubRow($event, row);\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n    ? 'Expand'\n    : 'Collapse'\">\n      <axis-icon\n        [icon]=\"row.showNestedGrid ? (model?.config?.nestedGridOptions?.expandLessIcon ? model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter') : (model?.config?.nestedGridOptions?.expandMoreIcon ? model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n        [data-size]=\"model?.config?.nestedGridOptions?.iconSize\" [data-color]=\"model?.config?.nestedGridOptions?.iconColor\"\n        [font]=\"model?.config?.nestedGridOptions?.iconFont || 'custom'\"></axis-icon>\n    </button>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\"\n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\">  {{ cell.content | date: (cell.format ? cell.format : \"MM/dd/yyyy\") }} </span>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-checkbox #check\n        [attr.data-inline]=\"cell.inline ? true : null\"\n        [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n        id=\"axis-grid-checkbox-{{ row.rowIndex }}\"\n\t\t\t\tname=\"axis-grid-checkbox-{{name}}\"  (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\"\n      ></axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\"\n        [disabled]=\"model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\"\n        name=\"axis-grid-radio-{{name}}\"  (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"  [checked]=\"row.selected\"\n        >\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n      <ng-container *ngIf=\"model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n        <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n          <span [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n          </span>\n        </ng-container>\n        <ng-container *ngIf=\"cell.click\">\n          <a href (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n            [ngClass]=\"{ 'axis-grid-content': true }\">\n            <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n            </ng-container>\n          </a>\n        </ng-container>\n        <ng-container *ngIf=\"cell.route\">\n          <a routerLink=\"/\"\n            (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n            [ngClass]=\"{ 'axis-grid-content': true }\">\n            <ng-container *ngIf=\"settings\">\n              {{ cell.content | axisInputNumberPipe:settings}}\n              </ng-container>\n              <ng-container *ngIf=\"!settings\">\n              {{ cell.content}}\n              </ng-container>\n          </a>\n        </ng-container>\n        <ng-container *ngIf=\"cell.href\">\n          <a [attr.href]=\"cell.href\"\n            [attr.target]=\"cell.target ? cell.target : '_blank'\"\n            (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n            [ngClass]=\"{ 'axis-grid-content': true }\">\n            <ng-container *ngIf=\"settings\">\n              {{ cell.content | axisInputNumberPipe:settings}}\n              </ng-container>\n              <ng-container *ngIf=\"!settings\">\n              {{ cell.content}}\n            </ng-container>\n          </a>\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"!model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n\n        <ng-container *ngIf=\"!cell.badge && !model.header.rows[0].cells[index]?.badge\">\n          <div class=\"axis-grid-content\">\n            <span [ngClass]=\"[subClass ? subClass : '']\">\n              <ng-container *ngIf=\"settings\">\n                {{ cell.content | axisInputNumberPipe:settings}}\n                </ng-container>\n                <ng-container *ngIf=\"!settings\">\n                {{ cell.content}}\n              </ng-container>\n            </span>\n          </div>\n        </ng-container>\n        <ng-container *ngIf=\"cell.badge || model.header.rows[0].cells[index]?.badge\">\n          <div class=\"axis-grid-content\">\n            <span\n              [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n              [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n              [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n              axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n              [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n              <ng-container *ngIf=\"settings\">\n                {{ cell.content | axisInputNumberPipe:settings}}\n                </ng-container>\n                <ng-container *ngIf=\"!settings\">\n                {{ cell.content}}\n                </ng-container>\n            </span>\n          </div>\n        </ng-container>\n      </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type !== 'custom'\">\n        <ng-container\n        *ngTemplateOutlet=\"content; context: { c: sub, r: row, subClass: model.header.rows[0].cells[index].subColumns[subIndex].class, type: model.header.rows[0].cells[index].subColumns[subIndex].type, settings: model.header.rows[0].cells[index].subColumns[subIndex].settings }\"\n        ></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n", styles: [".axis-grid-container{position:relative;display:block;width:100%}.axis-grid-container .axis-grid-loading{display:block;width:300px;top:0;left:50%;margin-left:-150px;position:absolute;text-align:center}.axis-datagrid-cellBorder{border-style:dashed}div.axis-datagrid-row.axis-datagrid-no-results{display:block!important;box-sizing:border-box;max-width:100%;text-align:center;padding:20px;background-color:#fff;box-shadow:0 4px 8px #0057b8}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i3.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i4.PreventDefaultLinkDirective, selector: "[href]", inputs: ["href"] }, { kind: "component", type: i4.CheckboxComponent, selector: "axis-checkbox", inputs: ["ariaLabel", "tabIndex", "checked", "disabled", "heading", "viewonly", "cid", "name", "rel", "required", "value"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "component", type: i4.RadioButtonComponent, selector: "axis-radio", inputs: ["ariaLabel", "tabIndex", "heading", "cid", "disabled", "checked", "name", "value", "rel"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "component", type: i4.ButtonComponent, selector: "[axis-button]", inputs: ["title", "type"] }, { kind: "component", type: i4.IconComponent, selector: "axis-icon,[axis-icon]", inputs: ["data-color", "data-size", "icon", "viewonly", "type", "font"] }, { kind: "directive", type: i4.TooltipDirective, selector: "[axis-tooltip]", inputs: ["color", "placement", "closeOnOutsideClick", "isOpen", "nested", "tooltipContentHover", "content", "kind", "item", "items", "style", "axisIncludeComponent", "axisIncludeTooltipComponent", "axisInclude", "data", "tooltipTrigger", "minwidth", "width", "isToggleOnClick", "context", "changeDetectorRef", "data-content"], outputs: ["onClose"] }, { kind: "component", type: AxisDatagridCustomComponent, selector: "axis-datagrid-custom", inputs: ["uiStructure", "uiData", "name"] }, { kind: "directive", type: AxisGridGenericCellDirective, selector: "[axisDatagridGenericCell]" }, { kind: "pipe", type: i3.DatePipe, name: "date" }, { kind: "pipe", type: i4.AxisInputNumberPipe, name: "axisInputNumberPipe" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'axis-datagrid-table', encapsulation: ViewEncapsulation.None, template: "<div\n  class=\"axis-grid-container\"\n  [ngClass]=\"{'axis-grid-container-fixed': grid.registeredPlugins.fixed && grid.model.config.gridHeight, 'axis-grid-container-loading': tableLoading}\"\n  [attr.axis-grid-name]=\"grid.model.config.name\"\n>\n  <div\n    class=\"axis-grid-loading\"\n    [style.visibility]=\"model.readyToShow ? 'hidden' : 'inherit'\"\n  >\n  <ng-container *ngIf=\"!model.config.loading\">Loading...</ng-container>\n    <ng-container *ngIf=\"model.config.loading\">\n      <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'loading', content: model.config.loading}\"\n            name=\"loading\">\n    </axis-datagrid-custom>\n    </ng-container>\n  </div>\n  <div [style.visibility]=\"!model.readyToShow ? 'hidden' : 'inherit'\">\n    <div #virtualFrame></div>\n    <ng-container *ngIf=\"model.config.scrollType !== 'virtual'\">\n      <div class=\"axis-grid\" *ngIf=\"grid?.model?.config?.download\">\n        <button *ngIf=\"grid?.model?.config?.download?.excel\" class=\"axis-btn\" (click)=\"export('excel')\">Export To Excel</button>\n        <button *ngIf=\"grid?.model?.config?.download?.pdf\" class=\"axis-btn\" (click)=\"export('pdf')\">Export To PDF</button>\n      </div>\n      <div\n        class=\"axis-table-responsive\"\n        [ngClass]=\"grid ? grid.model?.config?.cssGridParent : []\"\n        [attr.data-size]=\"grid?.model?.config?.dataSize ? grid?.model?.config.dataSize : null\"\n      >\n        <table\n          [attr.aria-describedby]=\"model.config.caption ? model.config.name + '_caption' : null\"\n          class=\"axis-table axis-table-basic\"\n          [ngClass]=\"grid ? grid.model?.config?.cssGridClass : []\"\n          [attr.aria-rowcount]=\"model.body.rows.length\"\n          role=\"grid\"\n          #thisTable\n        >\n          <caption *ngIf=\"model.config.caption\" id=\"{{model.config.name}}_caption\">\n            {{model.config.caption ? model.config.caption : ''}}\n          </caption>\n\n          <thead\n            role=\"rowgroup\"\n            *ngIf=\"model.header.rows.length > 0  && !model.header.config.hidden\"\n            class=\"axis-datagrid-head axis-grid-is-header\">\n            <tr\n              aria-rowindex=\"1\"\n              role=\"row\"\n              *ngFor=\"let row of model.header.rows\" [ngClass]=\"row.class\"\n            >\n              <ng-container  *ngFor=\"let cell of row.cells; let i = index\">\n                <th\n                  [attr.role]=\"'columnheader'\"\n                  [attr.aria-hidden]= \"(!cell.content?.trim('') && i!= 0) || (i === 0 && (cell.type === 'radio' || (cell.type === 'checkbox' && cell.showSelectAll === false)) || (!cell.content?.trim('') && cell.type != 'radio' && cell.type != 'checkbox'))  ? 'true' : null\"\n                  [ngClass]=\"cell.class\"\n                  class=\"axis-datagrid-cell axis-datagrid-cell-header\"\n                  scope=\"col\"\n                  *ngIf=\"!cell.hideColumn\"\n                  [ngStyle]=\"{\n                    'min-width': cell.columnsWidth?.min ? cell.columnsWidth.min : null,\n                    'max-width': cell.columnsWidth?.max ? cell.columnsWidth.max: null\n                  }\"\n                  [attr.col-index]=\"i\"\n                  [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n                >\n                  <ng-container *ngIf=\"cell.type !== 'checkbox' && cell.type !== 'radio' && cell.type !== 'expand'\">\n                    <span\n                      class=\"axis-grid-content\"\n                      [attr.alias]=\"cell.alias\"\n                      [attr.aria-label]=\"cell.ariaLabel\"\n                      role=\"status\"\n                      [attr.type]=\"cell.type\"\n                      [attr.initial-index]=\"i\"\n                      draggable=\"{{cell.draggable}}\"\n                      [ngClass]=\"[ cell.content ? '' : '_invisibility-md-up', cell?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                      (click)=\"headerColumnClicked(cell)\">\n                      <span [innerHtml]=\"cell.content || cell['data-heading']\"></span>\n                      <span *ngIf=\"cell.icon && cell.icon.name\" [attr.aria-label]=\"cell.icon.ariaLabel\" axis-tooltip [nested]=\"true\" [width]=\"cell.icon?.tooltipWidth\" [content]=\"(cell.icon && cell.icon.tooltipContent) ? cell.icon.tooltipContent : ''\">\n                        <axis-icon [icon]=\"cell.icon.name\" [data-size]=\"cell.icon?.iconSize\" [data-color]=\"cell.icon?.iconColor\" [font]=\"cell.icon?.iconFont || 'material'\"></axis-icon>\n                      </span>\n                      </span\n                    >\n                    <ng-container *ngIf=\"cell.subColumns\">\n                      <ng-container *ngFor=\"let sub of cell.subColumns\">\n                        <div\n                          class=\"axis-grid-content\"\n                          [attr.alias]=\"sub.alias\"\n                          [attr.type]=\"cell.type\"\n                          [attr.initial-index]=\"i\"\n                          role=\"status\"\n                          [attr.aria-label]=\"sub.ariaLabel\"\n                          [ngClass]=\"[sub?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                          (click)=\"headerColumnClicked(cell, sub)\">\n                          <span [innerHtml]=\"sub.content\"></span>\n                          <span *ngIf=\"sub.icon && sub.icon.name\" axis-tooltip [width]=\"sub.icon?.tooltipWidth\" [attr.aria-label]=\"sub.icon.ariaLabel\" [content]=\"(sub.icon && sub.icon.tooltipContent) ? sub.icon.tooltipContent : ''\">\n                            <axis-icon [icon]=\"sub.icon.name\" [data-size]=\"sub.icon?.iconSize\" [data-color]=\"sub.icon?.iconColor\" [font]=\"sub.icon?.iconFont || 'material'\"></axis-icon>\n                          </span>\n                          </div\n                        >\n                      </ng-container>\n                    </ng-container>\n                  </ng-container>\n                  <ng-container *ngIf=\"cell.type === 'checkbox' && cell.showSelectAll !== false\">\n                    <span class=\"axis-grid-content\">\n                      <axis-checkbox #checkAll\n                        [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n                        [attr.data-inline]=\"cell.inline ? true : null\"\n                        id=\"axis-grid-checkbox-header-{{ i }}\"\n                        [ariaLabel]=\"cell.content?.trim('') ? cell.content : 'Select Rows'\"\n                        (changeDetect)=\"selectAllRows($event, model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled)\"\n                        data-heading=\"{{ cell.content }}\"\n                      ></axis-checkbox>\n                    </span>\n                    </ng-container>\n                    <ng-container *ngIf=\"cell.type === 'checkbox' && !cell.showSelectAll && cell.content\">\n                      <span class=\"axis-grid-content\">\n                        {{cell.content}}\n                        </span>\n                    </ng-container>\n                    <ng-container *ngIf=\"cell.type === 'radio'\">\n                      <span class=\"axis-grid-content\">\n                        {{cell.content}}\n                      </span>\n                    </ng-container>\n                    <ng-container *ngIf=\"cell.type === 'expand' && model?.config?.nestedGridOptions?.expandOthers\">\n                      <span class=\"axis-grid-content\">\n                        <button axis-button (click)=\"toggleSubGrid(cell)\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n                          ? 'Expanded All'\n                          : 'Collapsed All'\">\n                          <axis-icon\n                              [icon]=\"cell.showAll\n                                ? (model?.config?.nestedGridOptions?.expandLessIcon ? model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter' )\n                                : (model?.config?.nestedGridOptions?.expandMoreIcon ? model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n                              [data-size]=\"model?.config?.nestedGridOptions?.iconSize\"\n                              [data-color]=\"model?.config?.nestedGridOptions?.iconColor\"\n                              [font]=\"model?.config?.nestedGridOptions?.iconFont || 'custom'\">\n                            </axis-icon>\n                        </button>\n                      </span>\n                    </ng-container>\n                </th>\n              </ng-container>\n            </tr>\n          </thead>\n\n          <tbody #tbody [ngStyle]=\"{'height': grid.registeredPlugins.fixed && grid.model.config.gridHeight ? grid.model.config.gridHeight : null}\">\n            <ng-container\n              *ngFor=\"\n                let row of grid.viewRows;\n                let r = index;\n              \"\n            >\n              <tr\n                #thisRow\n                [attr.aria-rowindex]=\"r + 2\"\n                [ngClass]=\"[(row.selected ? 'row-active': ''), (row.config.background ? 'row-' + row.config.background : ''),(model.header.rows[0].class ? model.header.rows[0].class : ''), ((row?.nestedGridComponent && row?.showNestedGrid) ? 'nested-row' : '')]\"\n                role=\"row\"\n                [attr.row-index]=\"r\"\n                [attr.row-real-index]=\"row.rowIndex\"\n                (click)=\"rowClicked(row)\"\n              >\n                <ng-container\n                  *ngFor=\"let cell of row.cells; let c = index\"\n                  #cell\n                >\n                  <ng-container *ngIf=\"!model?.header?.rows[0]?.cells[c]?.hideColumn\">\n                    <td\n                      #thisCell\n                      *ngIf=\"!model?.header?.rows[0]?.cells[c]?.type || model?.header?.rows[0]?.cells[c]?.type !== 'custom'\"\n                      [attr.row-index]=\"r\"\n                      [attr.no-truncate]=\"model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n                      axisDatagridGenericCell\n                      [attr.gridName]=\"model.config.name\"\n                      class=\"axis-datagrid-cell\"\n                      [ngClass]=\"[\n                        cell.cellClass ? cell.cellClass : '',\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model?.header?.rows[0]?.cells[c]?.class ? model.header.rows[0].cells[c].class : ''\n                      ]\"\n                      role=\"gridcell\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c] ? (model?.header?.rows[0]?.cells[c]['data-heading'] || model?.header?.rows[0]?.cells[c].content) : null\"\n                      [attr.data-content]=\"row.cells[c]['data-content'] || null\"\n                      [attr.cell-index]=\"r + '.' + c\"\n                      [ngStyle]=\"{\n                        'width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.width ? model?.header?.rows[0]?.cells[c].columnsWidth.width : null,\n                        'min-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.min ? model?.header?.rows[0]?.cells[c].columnsWidth.min : null,\n                        'max-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.max ? model?.header?.rows[0]?.cells[c].columnsWidth.max: null\n                      }\"\n                      [attr.col-index]=\"c\"\n                      [attr.cell-type]=\"cell.type\"\n                      [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n                    >\n                      <ng-container\n                        *ngTemplateOutlet=\"content; context: { c: cell, r: row, i: c, type: model?.header?.rows[0]?.cells[c]?.type, settings:model?.header?.rows[0]?.cells[c]?.settings  }\"\n                      ></ng-container>\n                    </td>\n                    <td\n                      #thisCell\n                      *ngIf=\"model?.header?.rows[0]?.cells[c]?.type && model?.header?.rows[0]?.cells[c]?.type === 'custom'\"\n                      axisDatagridGenericCell\n                      [attr.gridName]=\"model.config.name\"\n                      class=\"axis-datagrid-cell\"\n                      [attr.col-index]=\"c\"\n                      [ngClass]=\"[\n                        cell.cellClass ? cell.cellClass : '',\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model.header.rows[0].cells[c].class ? model.header.rows[0].cells[c].class : ''\n                      ]\"\n                      role=\"gridcell\"\n                      [ngStyle]=\"{\n                        'width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.width ? model?.header?.rows[0]?.cells[c].columnsWidth.width : null,\n                        'min-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.min ? model?.header?.rows[0]?.cells[c].columnsWidth.min : null,\n                        'max-width': model?.header?.rows[0]?.cells[c]?.columnsWidth?.max ? model?.header?.rows[0]?.cells[c].columnsWidth.max: null\n                      }\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c] ? (model?.header?.rows[0]?.cells[c]['data-heading'] || model?.header?.rows[0]?.cells[c].content) : null\"\n                      [attr.data-content]=\"row.cells[c]['data-content'] || null\"\n                      [attr.cell-index]=\"r + '.' + c\"\n                      [attr.cell-type]=\"cell.type\"\n                    >\n                      <axis-datagrid-custom\n                        [uiStructure]=\"cell\"\n                        [uiData]=\"row\"\n                        [name]=\"name\"\n                      ></axis-datagrid-custom>\n                    </td>\n                  </ng-container>\n                </ng-container>\n              </tr>\n\n              <tr class=\"nested-table\" *ngIf=\"(row?.nestedGridComponent && row?.showNestedGrid)\">\n                <td [attr.colspan]=\"row.cells.length\">\n                  <axis-datagrid-custom [uiData]=\"row?.nestedGridData\" [uiStructure]=\"{row, content: row.nestedGridComponent}\" [name]=\"row?.nestedGridData?.config?.name\"></axis-datagrid-custom>\n                </td>\n              </tr>\n\n\n            </ng-container>\n          </tbody>\n        </table>\n\n        <ng-container *ngIf=\"!model.body.rows.length || !grid?.viewRows?.length\">\n          <div class=\"axis-datagrid-row axis-datagrid-no-results\" role=\"row\">\n            <div *ngIf=\"!model.config.noResultsUi\">No results.</div>\n            <div *ngIf=\"model.config.noResultsUi\">\n              <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: model.config.noResultsUi}\"\n                name=\"NoResults\">\n              </axis-datagrid-custom>\n            </div>\n          </div>\n        </ng-container>\n      </div>\n    </ng-container>\n  </div>\n</div>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\" let-settings=\"settings\">\n  <ng-container *ngIf=\"type === 'expand' && row?.nestedGridComponent\">\n    <button axis-button (click)=\"expandSubRow($event, row);\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n    ? 'Expand'\n    : 'Collapse'\">\n      <axis-icon\n        [icon]=\"row.showNestedGrid ? (model?.config?.nestedGridOptions?.expandLessIcon ? model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter') : (model?.config?.nestedGridOptions?.expandMoreIcon ? model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n        [data-size]=\"model?.config?.nestedGridOptions?.iconSize\" [data-color]=\"model?.config?.nestedGridOptions?.iconColor\"\n        [font]=\"model?.config?.nestedGridOptions?.iconFont || 'custom'\"></axis-icon>\n    </button>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\"\n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\">  {{ cell.content | date: (cell.format ? cell.format : \"MM/dd/yyyy\") }} </span>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-checkbox #check\n        [attr.data-inline]=\"cell.inline ? true : null\"\n        [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n        id=\"axis-grid-checkbox-{{ row.rowIndex }}\"\n\t\t\t\tname=\"axis-grid-checkbox-{{name}}\"  (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\"\n      ></axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\"\n        [disabled]=\"model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\"\n        name=\"axis-grid-radio-{{name}}\"  (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"  [checked]=\"row.selected\"\n        >\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n      <ng-container *ngIf=\"model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n        <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n          <span [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n          </span>\n        </ng-container>\n        <ng-container *ngIf=\"cell.click\">\n          <a href (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n            [ngClass]=\"{ 'axis-grid-content': true }\">\n            <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n            </ng-container>\n          </a>\n        </ng-container>\n        <ng-container *ngIf=\"cell.route\">\n          <a routerLink=\"/\"\n            (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n            [ngClass]=\"{ 'axis-grid-content': true }\">\n            <ng-container *ngIf=\"settings\">\n              {{ cell.content | axisInputNumberPipe:settings}}\n              </ng-container>\n              <ng-container *ngIf=\"!settings\">\n              {{ cell.content}}\n              </ng-container>\n          </a>\n        </ng-container>\n        <ng-container *ngIf=\"cell.href\">\n          <a [attr.href]=\"cell.href\"\n            [attr.target]=\"cell.target ? cell.target : '_blank'\"\n            (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n            [ngClass]=\"{ 'axis-grid-content': true }\">\n            <ng-container *ngIf=\"settings\">\n              {{ cell.content | axisInputNumberPipe:settings}}\n              </ng-container>\n              <ng-container *ngIf=\"!settings\">\n              {{ cell.content}}\n            </ng-container>\n          </a>\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"!model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n\n        <ng-container *ngIf=\"!cell.badge && !model.header.rows[0].cells[index]?.badge\">\n          <div class=\"axis-grid-content\">\n            <span [ngClass]=\"[subClass ? subClass : '']\">\n              <ng-container *ngIf=\"settings\">\n                {{ cell.content | axisInputNumberPipe:settings}}\n                </ng-container>\n                <ng-container *ngIf=\"!settings\">\n                {{ cell.content}}\n              </ng-container>\n            </span>\n          </div>\n        </ng-container>\n        <ng-container *ngIf=\"cell.badge || model.header.rows[0].cells[index]?.badge\">\n          <div class=\"axis-grid-content\">\n            <span\n              [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n              [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n              [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n              axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n              [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n              <ng-container *ngIf=\"settings\">\n                {{ cell.content | axisInputNumberPipe:settings}}\n                </ng-container>\n                <ng-container *ngIf=\"!settings\">\n                {{ cell.content}}\n                </ng-container>\n            </span>\n          </div>\n        </ng-container>\n      </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type !== 'custom'\">\n        <ng-container\n        *ngTemplateOutlet=\"content; context: { c: sub, r: row, subClass: model.header.rows[0].cells[index].subColumns[subIndex].class, type: model.header.rows[0].cells[index].subColumns[subIndex].type, settings: model.header.rows[0].cells[index].subColumns[subIndex].settings }\"\n        ></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n", styles: [".axis-grid-container{position:relative;display:block;width:100%}.axis-grid-container .axis-grid-loading{display:block;width:300px;top:0;left:50%;margin-left:-150px;position:absolute;text-align:center}.axis-datagrid-cellBorder{border-style:dashed}div.axis-datagrid-row.axis-datagrid-no-results{display:block!important;box-sizing:border-box;max-width:100%;text-align:center;padding:20px;background-color:#fff;box-shadow:0 4px 8px #0057b8}\n"] }]
        }], ctorParameters: function () { return [{ type: AxisDatagridService }, { type: i0.ChangeDetectorRef }, { type: i2.Router }]; }, propDecorators: { model: [{
                type: Input
            }], output: [{
                type: Output
            }], thisTable: [{
                type: ViewChild,
                args: ['thisTable']
            }], tbody: [{
                type: ViewChild,
                args: ['tbody']
            }], virtualFrame: [{
                type: ViewChild,
                args: ['virtualFrame', { read: ViewContainerRef, static: true }]
            }], checkAll: [{
                type: ViewChild,
                args: ['checkAll']
            }], check: [{
                type: ViewChildren,
                args: ['check']
            }] } });

class AxisDatagridDivComponent {
    constructor(gridService, cd, router) {
        this.gridService = gridService;
        this.cd = cd;
        this.router = router;
        this.model = null;
        this.output = new EventEmitter();
        this.allSelected = false;
        Logger.log('DIV - Constructor');
        console.warn('The Axis Datagrid with the DIV mode is being deprecated soon.');
    }
    ngOnInit() {
        Logger.log('DIV - Init', this.model);
        // STEP 1
        this.prepareGrid();
        // STEP 2
        this.addSubscriptions();
        // STEP 3
        this.startGrid();
    }
    prepareGrid() {
        // As soon as data comes in, prepare model and variables
        const gs = this.gridService;
        this.defineName();
        this.name = this.model.config.name;
        this.model.config['grid-type'] = 'div-grid';
        gs.registerNewGrid(this.name);
        gs.grids[this.name].model = this.model;
        gs.grids[this.name]._outputEmitter = this.output;
        gs.grids[this.name].changeDetection = this.cd;
        this.grid = gs.grids[this.name];
        gs.mergeDefaultConfig(this.name);
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
                if (res && res.payload.name === this.name) {
                    this.init(res);
                }
            });
            gs.subscriptions.push(STAGE_1);
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                this.handleSubResult(res);
            });
            gs.subscriptions.push(STAGE_2);
            const REDRAW = gs.ACTIONS.REDRAW.pipe(delay(0)).subscribe((res) => {
                this.handleSubResult(res);
            });
            gs.subscriptions.push(REDRAW);
            const SCROLL = gs.ACTIONS.SCROLL.pipe(delay(0)).subscribe((res) => {
                if (res && res.payload.name === this.name) {
                    const isAllSelected = this.grid.viewRows.every(x => x.selected);
                    if (!isAllSelected && this.checkAll && this.checkAll.checked) {
                        this.checkAll.unCheck();
                    }
                }
            });
            gs.subscriptions.push(SCROLL);
            const SELECT_ALL_DIRECTIVE = gs.ACTIONS.SELECT_ALL_DIRECTIVE.pipe(delay(1)).subscribe((res) => {
                if (res && res.payload.name === this.name) {
                    if (this.checkAll) {
                        if (res.payload.data && this.checkAll) {
                            if (!this.checkAll.checked) {
                                this.checkAll.check();
                            }
                            else {
                                this.checkAll.unCheck();
                            }
                        }
                    }
                    Logger.log('DIV - Is ready');
                }
            });
            gs.subscriptions.push(SELECT_ALL_DIRECTIVE);
            const ROWS_SELECTED_ALL = this.gridService.grids[this.name].ACTIONS.ROWS_SELECTED_ALL.subscribe((res) => {
                Logger.log('ROWS_SELECTED_ALL : ', res);
                if (res && res.payload.name === this.name) {
                    if (res.payload.hasOwnProperty('updatingCheckAll')) {
                        res.payload.updatingCheckAll ? this.checkAll.check() : this.checkAll.unCheck();
                    }
                }
            });
            gs.subscriptions.push(ROWS_SELECTED_ALL);
            const COL_DRAGGED = gs.ACTIONS.COL_DRAGGED.pipe(delay(0)).subscribe((res) => {
                this.handleSubResult(res);
            });
            gs.subscriptions.push(COL_DRAGGED);
        }
    }
    handleSubResult(res) {
        if (res && res.payload.name === this.name) {
            this.afterInit(res);
        }
    }
    startGrid() {
        const gs = this.gridService;
        const data = this.getData();
        // STEP 4
        Logger.log('DIV - addFullDataDump');
        gs.addFullDataDump(data);
    }
    ngAfterViewInit() {
        Logger.log('DIV - After Init');
        this.getElementsRefs();
        const gs = this.gridService;
        const data = this.getData();
        // STEP 5
        Logger.log('DIV - Send STAGE_1');
        gs.sendAction(this.name, 'STAGE_1', data);
        setTimeout(() => {
            // STEP 6
            Logger.log('DIV - Send STAGE_2');
            this.gridService.sendAction(this.name, 'STAGE_2', data);
        }, 0);
    }
    getData() {
        const gs = this.gridService;
        return {
            name: this.name,
            type: 'div-grid',
            grid: gs.grids[this.name],
        };
    }
    getElementsRefs() {
        this.gridService.grids[this.name].thisGrid = this.thisTable;
        this.gridService.grids[this.name].tbody = this.tbody;
        this.gridService.grids[this.name].virtualFrame = this.virtualFrame;
    }
    countColumns(model) {
        if (model.config.hasOwnProperty('columnsCount')) {
            return model.config.columnsCount;
        }
        if (model.hasOwnProperty('header') && model.header.rows[0].cells.length) {
            let count = 0;
            for (const item of model.header.rows[0].cells) {
                if (!item.hideColumn) {
                    count++;
                }
            }
            model.config.columnsCount = count;
            return count;
        }
        if (model.hasOwnProperty('body') && model.body.rows[0].cells.length) {
            return model.body.rows[0].cells.length;
        }
    }
    getColumnSizes(model, _numColumns) {
        let gridWidth = this.thisTable.nativeElement.clientWidth - ((model.config && (model.config.agpFixed || model.config.scrollType === 'virtual')) ? 18 : 0);
        let minGridWidth = 0;
        let maxGridWidth = 0;
        const baseline = { min: 50, max: 250, width: 150, fraction: 1 };
        let totalFraction = 0;
        model.header.rows[0].cells.forEach((cw) => {
            if (!cw.hasOwnProperty('columnsWidth')) {
                cw.columnsWidth = Object.assign({}, baseline);
            }
            if (!cw.hideColumn) {
                minGridWidth += cw.columnsWidth.min ? parseInt(cw.columnsWidth.min.match(/(\d+)/)[0], 10) : 0;
                maxGridWidth += cw.columnsWidth.max ? parseInt(cw.columnsWidth.max.match(/(\d+)/)[0], 10) : 0;
                totalFraction += cw.columnsWidth.fraction;
            }
        });
        if (gridWidth < minGridWidth) {
            gridWidth = minGridWidth;
        }
        else if (gridWidth > maxGridWidth) {
            gridWidth = maxGridWidth;
        }
        const unit = (gridWidth) / totalFraction;
        model.header.rows[0].cells.forEach((cw) => {
            let newWidth = unit * cw.columnsWidth.fraction;
            const minColWidth = cw.columnsWidth.min ? parseInt(cw.columnsWidth.min.match(/(\d+)/)[0], 10) : 0;
            const maxColWidth = cw.columnsWidth.max ? parseInt(cw.columnsWidth.max.match(/(\d+)/)[0], 10) : 0;
            if (newWidth < minColWidth) {
                newWidth = minColWidth;
            }
            if (newWidth > maxColWidth) {
                newWidth = maxColWidth;
            }
            cw.realWidth = newWidth + 'px';
        });
        return model.header.rows[0].cells;
    }
    changeGridCss(name) {
        const gs = this.gridService;
        const numColumns = this.countColumns(gs.grids[name].model);
        const sizes = this.getColumnSizes(gs.grids[name].model, numColumns);
        let width = '';
        sizes.forEach((s) => {
            if (!s.hideColumn) {
                width += s.realWidth + ' ';
            }
        });
        let css = `
      [axis-grid-name="${name}"] .axis-datagrid-row {
        display: grid !important;
        grid-template-rows: auto;
        grid-template-columns: ${width};
        width: 100%;
      }
    `;
        const inner = this.thisTable.nativeElement.querySelectorAll('.axis-datagrid-body [role="row"],.cdk-virtual-scroll-content-wrapper')[0];
        let scrollWidth;
        if (inner) {
            scrollWidth = inner.parentNode.offsetWidth - inner.offsetWidth;
        }
        else {
            scrollWidth = 0;
        }
        css += `
        [axis-grid-name="${name}"] .axis-datagrid-head [role="row"] {
          margin-right: ${scrollWidth}px;
        }
      `;
        const head = document.getElementsByTagName('head')[0];
        const previous = head.querySelector(`#${name}-style-id`);
        if (previous) {
            previous.innerHTML = css;
        }
        else {
            const styleElement = document.createElement('style');
            styleElement.setAttribute('id', name + '-style-id');
            styleElement.innerHTML = css;
            document.getElementsByTagName('head')[0].appendChild(styleElement);
        }
        this.gridService.gridReady(this.name);
    }
    defineName() {
        if (!this.model.config.hasOwnProperty('name')) {
            this.model.config.name =
                'NoNameGrid-' + Math.floor(Math.random() * 10000);
            Logger.warn('Axis Grid', 'Model does not have the property "config.name"', `Temporarily set to ${this.model.config.name}`);
        }
    }
    init(_res) {
        return _res;
    }
    afterInit(_res) {
        setTimeout(() => {
            this.changeGridCss(this.name);
            this.cd.detectChanges();
        }, 0);
    }
    selectAllRows(event, disabled) {
        if (!disabled) {
            this.allSelected = event;
            const selectedList = [];
            this.grid.viewRows.forEach((element) => {
                if (!element.cells[0].disabled && !element.cells[0].hide) {
                    element.selected = event;
                    if (event) {
                        selectedList.push(element);
                    }
                }
            });
            if (this.check && this.check.length) {
                this.check.forEach((element) => {
                    if (event) {
                        if (!element.checked && !element.disabled && !element.hide) {
                            element.check();
                        }
                    }
                    else {
                        element.unCheck();
                    }
                });
            }
            if (this.allSelected) {
                this.gridService.sendAction(this.name, 'ROWS_SELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
            else {
                this.gridService.sendAction(this.name, 'ROWS_UNSELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
        }
    }
    preventClickEmit(event) {
        if (event.clientX) {
            event.preventDefault();
        }
    }
    rowClicked(row) {
        const headerCell = this.model?.header?.rows[0].cells.find((cell) => (cell.type === 'checkbox' || cell.type === 'radio'));
        if (headerCell && ((headerCell.type === 'checkbox') || (headerCell.type === 'radio' && !row.selected))) {
            const checked = (row.hasOwnProperty('selected') && row.selected);
            const hide = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('hide') && cell.hide)));
            const disabled = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('disabled') && cell.disabled)));
            this.selectRow(checked, row, this.model?.config?.disableAllRadio || disabled, hide, headerCell.type);
        }
    }
    selectRow(event, row, disabled, hide, type) {
        if (!disabled && !hide) {
            const selectedList = [];
            this.allSelected = true;
            this.grid.viewRows.forEach((element) => {
                if (type == 'radio') {
                    element.selected = false;
                }
                if (element.rowIndex === row.rowIndex) {
                    element.selected = !event;
                }
                if (type === 'checkbox') {
                    if (element.selected) {
                        selectedList.push(element);
                    }
                    else {
                        this.allSelected = false;
                    }
                }
                if (element.rowIndex == row.rowIndex) {
                    if (element.selected) {
                        this.gridService.sendAction(this.name, 'ROW_SELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: type === 'checkbox' ? selectedList : row,
                            rowIndex: element.rowIndex
                        });
                    }
                    else {
                        this.gridService.sendAction(this.name, 'ROW_UNSELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: type === 'checkbox' ? selectedList : row,
                            rowIndex: element.rowIndex
                        });
                    }
                }
            });
            if (type === 'checkbox') {
                if (this.allSelected && this.checkAll && !this.checkAll.checked) {
                    this.checkAll.check();
                }
                else if (!this.allSelected && this.checkAll && this.checkAll.checked) {
                    this.checkAll.unCheck();
                }
            }
        }
    }
    contentClicked(row, columnName, cell) {
        if (cell.route) {
            this.router.navigate([cell.route], { ...cell });
        }
        const payload = {
            name: this.name,
            content: columnName,
            rowsData: row,
            cell
        };
        this.gridService.isClicked();
        this.gridService.sendAction(this.name, 'ROWS_CLICKED', payload);
    }
    headerColumnClicked(column, subColumn = null) {
        if ((column.hasOwnProperty('isHeaderClickable') && column['isHeaderClickable']) || (subColumn && subColumn.hasOwnProperty('isHeaderClickable') && subColumn['isHeaderClickable'])) {
            const payload = {
                name: this.name,
                column: column,
                subColumnName: subColumn ? subColumn.alias : ''
            };
            this.gridService.sendAction(this.name, 'HEADER_CLICKED', payload);
        }
        if (this.model.config.scrollType === "virtual") {
            this.gridService.sendAction(this.name, 'GRID_CHANGED', this.name);
        }
    }
    ngOnDestroy() {
        this.gridService.destroyGrid(this.name);
    }
}
AxisDatagridDivComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridDivComponent, deps: [{ token: AxisDatagridService }, { token: i0.ChangeDetectorRef }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component });
AxisDatagridDivComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: AxisDatagridDivComponent, selector: "axis-datagrid-div", inputs: { model: "model" }, outputs: { output: "output" }, viewQueries: [{ propertyName: "thisTable", first: true, predicate: ["thisTable"], descendants: true }, { propertyName: "tbody", first: true, predicate: ["tbody"], descendants: true }, { propertyName: "virtualFrame", first: true, predicate: ["virtualFrame"], descendants: true, read: ViewContainerRef, static: true }, { propertyName: "checkAll", first: true, predicate: ["checkAll"], descendants: true }, { propertyName: "check", predicate: ["check"], descendants: true }], ngImport: i0, template: "<div class=\"axis-grid-container\" [attr.axis-grid-name]=\"model.config.name\">\n  <div class=\"axis-grid-loading\" *ngIf=\"!model.readyToShow\">\n    <ng-container *ngIf=\"!model.config.loading\">Loading...</ng-container>\n    <ng-container *ngIf=\"model.config.loading\">\n      <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'loading', content: model.config.loading}\"\n            name=\"loading\">\n          </axis-datagrid-custom>\n    </ng-container>\n    <br />\n    <axis-loading [isInline]=\"true\"></axis-loading>\n  </div>\n  <div class=\"\" [style.visibility]=\"!model.readyToShow ? 'hidden' : 'inherit'\" [ngClass]=\"grid ? grid.model?.config?.cssGridParent : []\">\n    <div class=\"axis-datagrid\" [ngClass]=\"grid.model?.config?.cssGridClass\" [attr.data-size]=\"grid?.model?.config?.dataSize ? grid?.model?.config.dataSize : null\" role=\"grid\"\n      [attr.aria-rowcount]=\"model.body.rows.length\" [attr.aria-label]=\"model.config.ariaLabel\" #thisTable>\n      <div class=\"axis-datagrid-head axis-grid-is-header\" *ngIf=\"model.header.rows.length && !model.header.config.hidden\" role=\"rowgroup\">\n        <div class=\"axis-datagrid-row\" role=\"row\" *ngFor=\"let row of model.header.rows\" [ngClass]=\"row.class\">\n          <ng-container *ngFor=\"let cell of row.cells; let i = index\">\n            <div class=\"axis-datagrid-cell axis-datagrid-cell-header\"  [attr.role]=\"'columnheader'\"\n            [attr.aria-hidden]=\"(!cell.content?.trim('') && i!= 0) || (i === 0 && (cell.type === 'radio' || (cell.type === 'checkbox' && cell.showSelectAll === false)) || (!cell.content?.trim('') && cell.type != 'radio' && cell.type != 'checkbox'))  ? 'true' : null\"   [ngClass]=\"cell.class\"\n              *ngIf=\"!cell.hideColumn\" scope=\"col\" [attr.col-index]=\"i\" [attr.has-subColumns]=\"cell.subColumns ? true : false\">\n              <ng-container *ngIf=\"cell.type !== 'checkbox' && cell.type !== 'radio'\">\n                <div class=\"axis-grid-content\"\n                  [attr.alias]=\"cell.alias\"\n                  [attr.type]=\"cell.type\"\n                  role=\"status\"\n                  [attr.initial-index]=\"i\"\n                  [attr.aria-label]=\"cell.ariaLabel\"\n                  [ngClass]=\"[cell?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                  (click)=\"headerColumnClicked(cell)\"\n                  draggable=\"{{cell.draggable}}\">{{ cell.content }}\n                  <span *ngIf=\"cell.icon && cell.icon.name\" [attr.aria-label]=\"cell.icon.ariaLabel\" [nested]=\"true\" axis-tooltip [width]=\"cell.icon?.tooltipWidth\" [content]=\"(cell.icon && cell.icon.tooltipContent) ? cell.icon.tooltipContent : ''\">\n                    <axis-icon [icon]=\"cell.icon.name\" [data-size]=\"cell.icon?.iconSize\" [data-color]=\"cell.icon?.iconColor\" [font]=\"cell.icon?.iconFont || 'material'\"></axis-icon>\n                  </span>\n                </div>\n                <ng-container *ngIf=\"cell.subColumns\">\n                  <ng-container *ngFor=\"let sub of cell.subColumns\">\n                    <div class=\"axis-grid-content _text-small\"\n                      [attr.alias]=\"sub.alias\"\n                      [attr.type]=\"sub.type\"\n                      role=\"status\"\n                      [attr.initial-index]=\"i\"\n                      [ngClass]=\"[sub?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                      (click)=\"headerColumnClicked(cell, sub)\"\n                      [attr.aria-label]=\"sub.ariaLabel\">\n                      {{ sub.content }}\n                      <span *ngIf=\"sub.icon && sub.icon.name\" [attr.aria-label]=\"sub.icon.ariaLabel\" axis-tooltip [width]=\"sub.icon?.tooltipWidth\" [content]=\"(sub.icon && sub.icon.tooltipContent) ? sub.icon.tooltipContent : ''\">\n                        <axis-icon [icon]=\"sub.icon.name\" [data-size]=\"sub.icon?.iconSize\" [data-color]=\"sub.icon?.iconColor\" [font]=\"sub.icon?.iconFont || 'material'\"></axis-icon>\n                      </span>\n                    </div>\n                  </ng-container>\n                </ng-container>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && cell.showSelectAll !== false\">\n                <span class=\"axis-grid-content\">\n                  <axis-checkbox #checkAll id=\"axis-grid-checkbox-header-{{ i }}\" \n                    [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"  \n                    [attr.data-inline]=\"cell.inline ? true : null\"\n                    [ariaLabel]=\"cell.content?.trim('') ? cell.content : 'Select Rows'\"\n                    (changeDetect)=\"selectAllRows($event, model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled)\"\n                    data-heading=\"{{ cell.content }}\"></axis-checkbox>\n                </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && !cell.showSelectAll && cell.content\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                  </span> \n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'radio'\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                </span>\n              </ng-container>\n            </div>\n          </ng-container>\n        </div>\n      </div>\n\n      <div #virtualFrame></div>\n      <ng-container *ngIf=\"model.config.scrollType !== 'virtual'\">\n        <div \n          class=\"axis-datagrid-body\" \n          role=\"rowgroup\" \n          #tbody>\n          <ng-container *ngIf=\"!model.body.rows.length || !grid?.viewRows?.length\">\n            <div class=\"axis-datagrid-row axis-datagrid-no-results\">\n              <ng-container *ngIf=\"!model.config.noResultsUi\">No results.</ng-container>\n              <ng-container *ngIf=\"model.config.noResultsUi\">\n                <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: model.config.noResultsUi}\"\n                  name=\"NoResults\">\n                </axis-datagrid-custom>\n              </ng-container>\n            </div>\n          </ng-container>\n          <ng-container *ngIf=\"model.body.rows.length\">\n            <ng-container *ngFor=\"\n                let row of grid.viewRows;\n                let r = index\n              \">\n              <div class=\"axis-datagrid-row\" (click)=\"rowClicked(row)\" \n                [ngClass]=\"[(row.selected ? 'row-active' : ''), (row.config.background ? 'row-' + row.config.background : ''),(model.header.rows[0].class ? model.header.rows[0].class : '')]\"\n                role=\"row\" [attr.row-index]=\"r\" [attr.row-real-index]=\"row.rowIndex\" #thisRow>\n                <ng-container *ngFor=\"let cell of row.cells; let c = index\">\n                  <ng-container *ngIf=\"!model.header.rows[0].cells[c].hideColumn\">\n                    <div *ngIf=\"!model?.header?.rows[0]?.cells[c]?.type || model?.header?.rows[0]?.cells[c]?.type !== 'custom'\" #thisCell axisDatagridGenericCell\n                      [attr.gridName]=\"model.config.name\" role=\"gridcell\" class=\"axis-datagrid-cell\"\n                      [attr.no-truncate]=\"model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c].content\"\n                      [attr.col-index]=\"c\" [attr.cell-index]=\"r + '.' + c\" [attr.row-index]=\"r\" [ngClass]=\"[\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model.header.rows[0].cells[c].class ? model.header.rows[0].cells[c].class : ''\n                      ]\" [attr.cell-type]=\"model?.header?.rows[0]?.cells[c]?.type\" [attr.has-subColumns]=\"cell.subColumns ? true : false\">\n                      <ng-container *ngTemplateOutlet=\"content; context: { c: cell, r: row , i: c, type: model?.header?.rows[0]?.cells[c]?.type  }\"></ng-container>\n                    </div>\n                    <div *ngIf=\"model?.header?.rows[0]?.cells[c]?.type && model?.header?.rows[0]?.cells[c]?.type === 'custom'\" #thisCell axisDatagridGenericCell\n                    [attr.col-index]=\"c\" [attr.gridName]=\"model.config.name\" role=\"gridcell\" class=\"axis-datagrid-cell\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c].content\"\n                      [ngClass]=\"[\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model.header.rows[0].cells[c].class ? model.header.rows[0].cells[c].class : ''\n                      ]\" [attr.cell-index]=\"r + '.' + c\" [attr.row-index]=\"r\" [attr.cell-type]=\"model?.header?.rows[0]?.cells[c]?.type\">\n                      <axis-datagrid-custom [uiStructure]=\"cell\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n                    </div>\n                  </ng-container>\n                </ng-container>\n              </div>\n            </ng-container>\n          </ng-container>\n        </div>\n      </ng-container>\n\n    </div>\n  </div>\n</div>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\">\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\" \n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"> {{ cell.content | date: (cell.format ? cell.format : \"MM/dd/yyyy\") }} </span> \n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-checkbox #check id=\"axis-grid-checkbox-{{ row.rowIndex }}\"\n        [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"  \n        [attr.data-inline]=\"cell.inline ? true : null\"\n        name=\"axis-grid-checkbox-{{name}}\" (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\" data-heading=\"{{ cell['data-heading'] }}\">\n      </axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio'  && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\"\n        [disabled]=\"model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\" (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        name=\"axis-grid-radio-{{name}}\" value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"\n        data-heading=\"{{ cell['data-heading'] }}\" [checked]=\"row.selected\">\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n    <ng-container *ngIf=\"model.header.rows[0].cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n        <span [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </span>\n      </ng-container>\n      <ng-container *ngIf=\"cell.click\">\n        <a href (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.route\">\n        <a routerLink=\"/\"\n          (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.href\">\n        <a [attr.href]=\"cell.href\" [attr.target]=\"cell.target ? cell.target : '_blank'\"\n          (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n\n    </ng-container>\n    <ng-container *ngIf=\"!model.header.rows[0].cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.badge && !model.header.rows[0].cells[index]?.badge\">\n        <div class=\"axis-grid-content\">\n          <span [ngClass]=\"[subClass ? subClass : '']\"> {{ cell.content }}</span>\n        </div>\n      </ng-container>\n      <ng-container *ngIf=\"cell.badge || model.header.rows[0].cells[index]?.badge\">\n        <div class=\"axis-grid-content\">\n          <span\n            [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n            [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n            [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n            axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n            [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\"> {{ cell.content }}\n          </span>\n        </div>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type !== 'custom'\">\n        <ng-container\n          *ngTemplateOutlet=\"content; context: { c: sub, r: row, subClass: model.header.rows[0].cells[index].subColumns[subIndex].class, type: model.header.rows[0].cells[index].subColumns[subIndex].type}\">\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n", styles: [".axis-grid-container{position:relative;display:block;width:100%}.axis-grid-container .axis-grid-loading{display:block;width:300px;top:0;left:50%;margin-left:-150px;position:absolute;text-align:center}.axis-datagrid-cellBorder{border-style:dashed}div.axis-datagrid-row.axis-datagrid-no-results{display:block!important;box-sizing:border-box;max-width:100%;text-align:center;padding:20px}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i4.PreventDefaultLinkDirective, selector: "[href]", inputs: ["href"] }, { kind: "component", type: i4.CheckboxComponent, selector: "axis-checkbox", inputs: ["ariaLabel", "tabIndex", "checked", "disabled", "heading", "viewonly", "cid", "name", "rel", "required", "value"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "component", type: i4.RadioButtonComponent, selector: "axis-radio", inputs: ["ariaLabel", "tabIndex", "heading", "cid", "disabled", "checked", "name", "value", "rel"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "component", type: i4.LoadingComponent, selector: "axis-loading,[axis-loading]", inputs: ["isInline"] }, { kind: "component", type: i4.IconComponent, selector: "axis-icon,[axis-icon]", inputs: ["data-color", "data-size", "icon", "viewonly", "type", "font"] }, { kind: "directive", type: i4.TooltipDirective, selector: "[axis-tooltip]", inputs: ["color", "placement", "closeOnOutsideClick", "isOpen", "nested", "tooltipContentHover", "content", "kind", "item", "items", "style", "axisIncludeComponent", "axisIncludeTooltipComponent", "axisInclude", "data", "tooltipTrigger", "minwidth", "width", "isToggleOnClick", "context", "changeDetectorRef", "data-content"], outputs: ["onClose"] }, { kind: "component", type: AxisDatagridCustomComponent, selector: "axis-datagrid-custom", inputs: ["uiStructure", "uiData", "name"] }, { kind: "directive", type: AxisGridGenericCellDirective, selector: "[axisDatagridGenericCell]" }, { kind: "pipe", type: i3.DatePipe, name: "date" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridDivComponent, decorators: [{
            type: Component,
            args: [{ selector: 'axis-datagrid-div', encapsulation: ViewEncapsulation.None, template: "<div class=\"axis-grid-container\" [attr.axis-grid-name]=\"model.config.name\">\n  <div class=\"axis-grid-loading\" *ngIf=\"!model.readyToShow\">\n    <ng-container *ngIf=\"!model.config.loading\">Loading...</ng-container>\n    <ng-container *ngIf=\"model.config.loading\">\n      <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'loading', content: model.config.loading}\"\n            name=\"loading\">\n          </axis-datagrid-custom>\n    </ng-container>\n    <br />\n    <axis-loading [isInline]=\"true\"></axis-loading>\n  </div>\n  <div class=\"\" [style.visibility]=\"!model.readyToShow ? 'hidden' : 'inherit'\" [ngClass]=\"grid ? grid.model?.config?.cssGridParent : []\">\n    <div class=\"axis-datagrid\" [ngClass]=\"grid.model?.config?.cssGridClass\" [attr.data-size]=\"grid?.model?.config?.dataSize ? grid?.model?.config.dataSize : null\" role=\"grid\"\n      [attr.aria-rowcount]=\"model.body.rows.length\" [attr.aria-label]=\"model.config.ariaLabel\" #thisTable>\n      <div class=\"axis-datagrid-head axis-grid-is-header\" *ngIf=\"model.header.rows.length && !model.header.config.hidden\" role=\"rowgroup\">\n        <div class=\"axis-datagrid-row\" role=\"row\" *ngFor=\"let row of model.header.rows\" [ngClass]=\"row.class\">\n          <ng-container *ngFor=\"let cell of row.cells; let i = index\">\n            <div class=\"axis-datagrid-cell axis-datagrid-cell-header\"  [attr.role]=\"'columnheader'\"\n            [attr.aria-hidden]=\"(!cell.content?.trim('') && i!= 0) || (i === 0 && (cell.type === 'radio' || (cell.type === 'checkbox' && cell.showSelectAll === false)) || (!cell.content?.trim('') && cell.type != 'radio' && cell.type != 'checkbox'))  ? 'true' : null\"   [ngClass]=\"cell.class\"\n              *ngIf=\"!cell.hideColumn\" scope=\"col\" [attr.col-index]=\"i\" [attr.has-subColumns]=\"cell.subColumns ? true : false\">\n              <ng-container *ngIf=\"cell.type !== 'checkbox' && cell.type !== 'radio'\">\n                <div class=\"axis-grid-content\"\n                  [attr.alias]=\"cell.alias\"\n                  [attr.type]=\"cell.type\"\n                  role=\"status\"\n                  [attr.initial-index]=\"i\"\n                  [attr.aria-label]=\"cell.ariaLabel\"\n                  [ngClass]=\"[cell?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                  (click)=\"headerColumnClicked(cell)\"\n                  draggable=\"{{cell.draggable}}\">{{ cell.content }}\n                  <span *ngIf=\"cell.icon && cell.icon.name\" [attr.aria-label]=\"cell.icon.ariaLabel\" [nested]=\"true\" axis-tooltip [width]=\"cell.icon?.tooltipWidth\" [content]=\"(cell.icon && cell.icon.tooltipContent) ? cell.icon.tooltipContent : ''\">\n                    <axis-icon [icon]=\"cell.icon.name\" [data-size]=\"cell.icon?.iconSize\" [data-color]=\"cell.icon?.iconColor\" [font]=\"cell.icon?.iconFont || 'material'\"></axis-icon>\n                  </span>\n                </div>\n                <ng-container *ngIf=\"cell.subColumns\">\n                  <ng-container *ngFor=\"let sub of cell.subColumns\">\n                    <div class=\"axis-grid-content _text-small\"\n                      [attr.alias]=\"sub.alias\"\n                      [attr.type]=\"sub.type\"\n                      role=\"status\"\n                      [attr.initial-index]=\"i\"\n                      [ngClass]=\"[sub?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                      (click)=\"headerColumnClicked(cell, sub)\"\n                      [attr.aria-label]=\"sub.ariaLabel\">\n                      {{ sub.content }}\n                      <span *ngIf=\"sub.icon && sub.icon.name\" [attr.aria-label]=\"sub.icon.ariaLabel\" axis-tooltip [width]=\"sub.icon?.tooltipWidth\" [content]=\"(sub.icon && sub.icon.tooltipContent) ? sub.icon.tooltipContent : ''\">\n                        <axis-icon [icon]=\"sub.icon.name\" [data-size]=\"sub.icon?.iconSize\" [data-color]=\"sub.icon?.iconColor\" [font]=\"sub.icon?.iconFont || 'material'\"></axis-icon>\n                      </span>\n                    </div>\n                  </ng-container>\n                </ng-container>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && cell.showSelectAll !== false\">\n                <span class=\"axis-grid-content\">\n                  <axis-checkbox #checkAll id=\"axis-grid-checkbox-header-{{ i }}\" \n                    [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"  \n                    [attr.data-inline]=\"cell.inline ? true : null\"\n                    [ariaLabel]=\"cell.content?.trim('') ? cell.content : 'Select Rows'\"\n                    (changeDetect)=\"selectAllRows($event, model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled)\"\n                    data-heading=\"{{ cell.content }}\"></axis-checkbox>\n                </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && !cell.showSelectAll && cell.content\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                  </span> \n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'radio'\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                </span>\n              </ng-container>\n            </div>\n          </ng-container>\n        </div>\n      </div>\n\n      <div #virtualFrame></div>\n      <ng-container *ngIf=\"model.config.scrollType !== 'virtual'\">\n        <div \n          class=\"axis-datagrid-body\" \n          role=\"rowgroup\" \n          #tbody>\n          <ng-container *ngIf=\"!model.body.rows.length || !grid?.viewRows?.length\">\n            <div class=\"axis-datagrid-row axis-datagrid-no-results\">\n              <ng-container *ngIf=\"!model.config.noResultsUi\">No results.</ng-container>\n              <ng-container *ngIf=\"model.config.noResultsUi\">\n                <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: model.config.noResultsUi}\"\n                  name=\"NoResults\">\n                </axis-datagrid-custom>\n              </ng-container>\n            </div>\n          </ng-container>\n          <ng-container *ngIf=\"model.body.rows.length\">\n            <ng-container *ngFor=\"\n                let row of grid.viewRows;\n                let r = index\n              \">\n              <div class=\"axis-datagrid-row\" (click)=\"rowClicked(row)\" \n                [ngClass]=\"[(row.selected ? 'row-active' : ''), (row.config.background ? 'row-' + row.config.background : ''),(model.header.rows[0].class ? model.header.rows[0].class : '')]\"\n                role=\"row\" [attr.row-index]=\"r\" [attr.row-real-index]=\"row.rowIndex\" #thisRow>\n                <ng-container *ngFor=\"let cell of row.cells; let c = index\">\n                  <ng-container *ngIf=\"!model.header.rows[0].cells[c].hideColumn\">\n                    <div *ngIf=\"!model?.header?.rows[0]?.cells[c]?.type || model?.header?.rows[0]?.cells[c]?.type !== 'custom'\" #thisCell axisDatagridGenericCell\n                      [attr.gridName]=\"model.config.name\" role=\"gridcell\" class=\"axis-datagrid-cell\"\n                      [attr.no-truncate]=\"model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c].content\"\n                      [attr.col-index]=\"c\" [attr.cell-index]=\"r + '.' + c\" [attr.row-index]=\"r\" [ngClass]=\"[\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model.header.rows[0].cells[c].class ? model.header.rows[0].cells[c].class : ''\n                      ]\" [attr.cell-type]=\"model?.header?.rows[0]?.cells[c]?.type\" [attr.has-subColumns]=\"cell.subColumns ? true : false\">\n                      <ng-container *ngTemplateOutlet=\"content; context: { c: cell, r: row , i: c, type: model?.header?.rows[0]?.cells[c]?.type  }\"></ng-container>\n                    </div>\n                    <div *ngIf=\"model?.header?.rows[0]?.cells[c]?.type && model?.header?.rows[0]?.cells[c]?.type === 'custom'\" #thisCell axisDatagridGenericCell\n                    [attr.col-index]=\"c\" [attr.gridName]=\"model.config.name\" role=\"gridcell\" class=\"axis-datagrid-cell\"\n                      [attr.data-heading]=\"model?.header?.rows[0]?.cells[c].content\"\n                      [ngClass]=\"[\n                        (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                        (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                        cell.background ? 'cell-' + cell.background : '',\n                        model.header.rows[0].cells[c].class ? model.header.rows[0].cells[c].class : ''\n                      ]\" [attr.cell-index]=\"r + '.' + c\" [attr.row-index]=\"r\" [attr.cell-type]=\"model?.header?.rows[0]?.cells[c]?.type\">\n                      <axis-datagrid-custom [uiStructure]=\"cell\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n                    </div>\n                  </ng-container>\n                </ng-container>\n              </div>\n            </ng-container>\n          </ng-container>\n        </div>\n      </ng-container>\n\n    </div>\n  </div>\n</div>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\">\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\" \n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"> {{ cell.content | date: (cell.format ? cell.format : \"MM/dd/yyyy\") }} </span> \n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-checkbox #check id=\"axis-grid-checkbox-{{ row.rowIndex }}\"\n        [disabled]=\"model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"  \n        [attr.data-inline]=\"cell.inline ? true : null\"\n        name=\"axis-grid-checkbox-{{name}}\" (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\" data-heading=\"{{ cell['data-heading'] }}\">\n      </axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio'  && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\"\n        [disabled]=\"model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\" (click)=\"preventClickEmit($event)\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        name=\"axis-grid-radio-{{name}}\" value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"\n        data-heading=\"{{ cell['data-heading'] }}\" [checked]=\"row.selected\">\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n    <ng-container *ngIf=\"model.header.rows[0].cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n        <span [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </span>\n      </ng-container>\n      <ng-container *ngIf=\"cell.click\">\n        <a href (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.route\">\n        <a routerLink=\"/\"\n          (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.href\">\n        <a [attr.href]=\"cell.href\" [attr.target]=\"cell.target ? cell.target : '_blank'\"\n          (click)=\"contentClicked(row, model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n\n    </ng-container>\n    <ng-container *ngIf=\"!model.header.rows[0].cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.badge && !model.header.rows[0].cells[index]?.badge\">\n        <div class=\"axis-grid-content\">\n          <span [ngClass]=\"[subClass ? subClass : '']\"> {{ cell.content }}</span>\n        </div>\n      </ng-container>\n      <ng-container *ngIf=\"cell.badge || model.header.rows[0].cells[index]?.badge\">\n        <div class=\"axis-grid-content\">\n          <span\n            [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || model.header.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n            [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n            [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n            axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n            [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\"> {{ cell.content }}\n          </span>\n        </div>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type !== 'custom'\">\n        <ng-container\n          *ngTemplateOutlet=\"content; context: { c: sub, r: row, subClass: model.header.rows[0].cells[index].subColumns[subIndex].class, type: model.header.rows[0].cells[index].subColumns[subIndex].type}\">\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"model.header.rows[0].cells[index].subColumns[subIndex].type && model.header.rows[0].cells[index].subColumns[subIndex].type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n", styles: [".axis-grid-container{position:relative;display:block;width:100%}.axis-grid-container .axis-grid-loading{display:block;width:300px;top:0;left:50%;margin-left:-150px;position:absolute;text-align:center}.axis-datagrid-cellBorder{border-style:dashed}div.axis-datagrid-row.axis-datagrid-no-results{display:block!important;box-sizing:border-box;max-width:100%;text-align:center;padding:20px}\n"] }]
        }], ctorParameters: function () { return [{ type: AxisDatagridService }, { type: i0.ChangeDetectorRef }, { type: i2.Router }]; }, propDecorators: { model: [{
                type: Input
            }], output: [{
                type: Output
            }], thisTable: [{
                type: ViewChild,
                args: ['thisTable']
            }], tbody: [{
                type: ViewChild,
                args: ['tbody']
            }], virtualFrame: [{
                type: ViewChild,
                args: ['virtualFrame', { read: ViewContainerRef, static: true }]
            }], checkAll: [{
                type: ViewChild,
                args: ['checkAll']
            }], check: [{
                type: ViewChildren,
                args: ['check']
            }] } });

class AxisDatagridSharedModule {
}
AxisDatagridSharedModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridSharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AxisDatagridSharedModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridSharedModule, declarations: [AxisDatagridCustomComponent], imports: [CommonModule], exports: [AxisDatagridCustomComponent] });
AxisDatagridSharedModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridSharedModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridSharedModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AxisDatagridCustomComponent],
                    imports: [CommonModule],
                    exports: [AxisDatagridCustomComponent]
                }]
        }] });

const declarations = [
    AxisDatagridDivComponent,
    AxisDatagridTableComponent,
    AxisGridGenericCellDirective
];
const imports = [
    CommonModule,
    RouterModule,
    UtilsModule,
    CheckboxModule,
    InputModule,
    RadioButtonModule,
    LoadingModule,
    ButtonsModule,
    IconModule,
    TooltipModule,
    AxisDatagridSharedModule,
    UtilsModule
];
const providers = [
    AxisDatagridService
];
class AxisDatagridModule {
    constructor() {
        const VERSION = new Version('15.0.11');
        document.body.setAttribute('grid-version', VERSION.version);
    }
}
AxisDatagridModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AxisDatagridModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridModule, declarations: [AxisDatagridDivComponent,
        AxisDatagridTableComponent,
        AxisGridGenericCellDirective], imports: [CommonModule,
        RouterModule,
        UtilsModule,
        CheckboxModule,
        InputModule,
        RadioButtonModule,
        LoadingModule,
        ButtonsModule,
        IconModule,
        TooltipModule,
        AxisDatagridSharedModule,
        UtilsModule], exports: [AxisDatagridDivComponent,
        AxisDatagridTableComponent,
        AxisGridGenericCellDirective] });
AxisDatagridModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridModule, providers: providers, imports: [imports] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AxisDatagridModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations,
                    imports,
                    providers,
                    exports: declarations
                }]
        }], ctorParameters: function () { return []; } });

// tslint:disable:no-string-literal
class AgpCellStyleDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpCellStyleDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (this.gridService.grids[this.name]) {
            this.name = this.model.config.name;
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['cellStyle'] = {};
                    this.grid.registeredPlugins._sequence.push('cellStyle');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpCellStyle']) {
            //
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpCellStyle')) {
            config.agpCellStyle = true;
        }
    }
}
AgpCellStyleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCellStyleDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpCellStyleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpCellStyleDirective, selector: "[agpCellStyle]", inputs: { AgpCellStyleDirective: "AgpCellStyleDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCellStyleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpCellStyle]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpCellStyleDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpCellStyleModule {
}
AgpCellStyleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCellStyleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpCellStyleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpCellStyleModule, declarations: [AgpCellStyleDirective], imports: [CommonModule], exports: [AgpCellStyleDirective] });
AgpCellStyleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCellStyleModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCellStyleModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpCellStyleDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpCellStyleDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpCondensedDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpCondensedDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (this.gridService.grids[this.name]) {
            this.name = this.model.config.name;
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['condensed'] = {};
                    this.grid.registeredPlugins._sequence.push('condensed');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_1);
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpCondensed')) {
            config.agpCondensed = true;
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpCondensed']) {
            this.grid.model.config.cssGridClass.push('axis-table-small');
            this.grid.changeDetection.detectChanges();
        }
    }
}
AgpCondensedDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCondensedDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpCondensedDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpCondensedDirective, selector: "[agpCondensed]", inputs: { AgpCondensedDirective: "AgpCondensedDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCondensedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpCondensed]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpCondensedDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpCondensedModule {
}
AgpCondensedModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCondensedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpCondensedModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpCondensedModule, declarations: [AgpCondensedDirective], imports: [CommonModule], exports: [AgpCondensedDirective] });
AgpCondensedModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCondensedModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpCondensedModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpCondensedDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpCondensedDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpDragColumnDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpDragColumnDirective = null;
        this.model = null;
    }
    ngOnInit() {
        this.name = this.model.config.name;
        setTimeout(() => {
            this.addSubscriptions();
        }, 0);
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['dragColumn'] = {};
                    this.grid.registeredPlugins._sequence.push('dragColumn');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
            const VIRTUAL_SCROLL_STAGE_3 = gs.ACTIONS.VIRTUAL_SCROLL_STAGE_3.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.addDragColumn();
                }
            });
            gs.subscriptions.push(VIRTUAL_SCROLL_STAGE_3);
        }
    }
    startDirective() {
        this.pluginConfig();
        if (!this.model.config.hasOwnProperty('scrollType') || this.model.config.scrollType !== 'virtual') {
            this.addDragColumn();
        }
    }
    addDragColumn() {
        if (this.grid.model.config['agpDragColumn']) {
            const columnHeaderCells = this.el.nativeElement.querySelectorAll('.axis-datagrid-cell-header');
            columnHeaderCells.forEach(c => {
                c.firstChild.addEventListener('dragstart', (ev) => {
                    this.dragStart(ev);
                });
                c.addEventListener('dragover', (ev) => {
                    this.allowDrop(ev);
                });
                c.addEventListener('dragleave', (ev) => {
                    this.dragLeave(ev);
                });
                c.addEventListener('drop', (ev) => {
                    this.drop(ev);
                    ev.stopPropagation();
                });
            });
        }
    }
    allowDrop(ev) {
        ev.preventDefault();
        ev.currentTarget.classList.add('axis-datagrid-cellBorder');
    }
    dragLeave(ev) {
        ev.currentTarget.classList.remove('axis-datagrid-cellBorder');
    }
    dragStart(ev) {
        this.dragElement = ev.currentTarget.innerText;
    }
    drop(ev) {
        this.dropElement = ev.target.innerText;
        const payload = {
            content: this.dragElement,
            name: this.name
        };
        if (this.dragElement && this.dropElement && this.dragElement !== this.dropElement) {
            this.dragDrop(this.dragElement, this.dropElement, this.name);
            this.columnReOrder();
            this.gridService.sendAction(this.name, 'COL_DRAGGED', payload);
        }
        ev.currentTarget.classList.remove('axis-datagrid-cellBorder');
        ev.stopPropagation();
    }
    dragDrop(dragColumnName, dropColumnName, name) {
        let dragIndex;
        let dropIndex;
        this.grid.model.header.rows[0].cells.forEach((element, index) => {
            if (element.content === dragColumnName) {
                dragIndex = index;
            }
            if (element.content === dropColumnName) {
                dropIndex = index;
            }
            else if (element.subColumns && element.subColumns.length) {
                const subColIndex = element.subColumns.findIndex((subCol) => subCol.content === dropColumnName);
                if (subColIndex !== -1) {
                    dropIndex = index;
                }
            }
        });
        if ((dragIndex || dragIndex === 0) && (dropIndex || dropIndex === 0) && dragIndex !== dropIndex) {
            const dragItem = this.grid.model.header.rows[0].cells[dragIndex];
            this.grid.model.header.rows[0].cells.splice(dragIndex, 1);
            this.grid.model.header.rows[0].cells.splice(dropIndex, 0, dragItem);
        }
        this.gridService.getRows(name);
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpDragColumn')) {
            config.agpDragColumn = true;
        }
    }
    columnReOrder() {
        if (this.grid.viewRows.length) {
            this.grid.viewRows.forEach((row) => {
                const customCells = [];
                row['cells'].forEach((_cell, cellIndex) => {
                    const alias = this.grid.model.header.rows[0].cells[cellIndex].alias;
                    customCells.push(row.cells.find((c) => c.mapTo === alias));
                });
                row.cells = customCells;
            });
        }
    }
    ngOnDestroy() {
        this.model = null;
        this.grid = null;
    }
}
AgpDragColumnDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpDragColumnDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpDragColumnDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpDragColumnDirective, selector: "[agpDragColumn]", inputs: { AgpDragColumnDirective: "AgpDragColumnDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpDragColumnDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpDragColumn]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpDragColumnDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpDragColumnModule {
}
AgpDragColumnModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpDragColumnModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpDragColumnModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpDragColumnModule, declarations: [AgpDragColumnDirective], imports: [CommonModule], exports: [AgpDragColumnDirective] });
AgpDragColumnModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpDragColumnModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpDragColumnModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpDragColumnDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpDragColumnDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpFilterDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpFilterDirective = null;
        this.model = null;
    }
    ngOnInit() {
        this.name = this.model.config.name;
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['filter'] = {};
                    this.grid.registeredPlugins._sequence.push('filter');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    startDirective() {
        const config = this.grid.model.config;
        this.pluginConfig();
        if (config['agpFilter'] && config.agpFilter.hasOwnProperty('filters')) {
            this.gridService.grids[this.name].dataPipeLine[50] = this.runFilter.bind(this);
            this.gridService.getRows(this.name);
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpFilter')) {
            config.agpFilter = {
                enable: true,
                filters: [],
            };
        }
    }
    regExpFromString(q) {
        let flags = q.replace(/.*\/([gimuy]*)$/, '$1');
        if (flags === q) {
            flags = '';
        }
        const pattern = flags
            ? q.replace(new RegExp('^/(.*?)/' + flags + '$'), '$1')
            : q;
        try {
            return new RegExp(pattern, flags);
        }
        catch (e) {
            return null;
        }
    }
    runFilter() {
        let viewRowsRef = this.gridService.grids[this.name].viewRows;
        let rowsRef = [];
        this.grid.model.config.agpFilter.filters.forEach((filter) => {
            const newReg = this.regExpFromString(filter.condition);
            const valueProperty = filter.hasOwnProperty('value_property')
                ? filter.value_property
                : 'content';
            const key = this.grid.model.config.columnsOrder.indexOf(filter.field);
            if (this.grid.model.config.agpFilter.orCondition) { // 'or' condition
                viewRowsRef.forEach((row) => {
                    let index = rowsRef.findIndex(x => x.rowIndex === row.rowIndex);
                    if (new RegExp(newReg).test(row.cells[key][valueProperty]) && index === -1) {
                        rowsRef.push(row);
                    }
                });
            }
            else { // 'and' condition
                viewRowsRef = viewRowsRef.filter((row) => {
                    return new RegExp(newReg).test(row.cells[key][valueProperty]);
                });
            }
        });
        if (this.grid.model.config.agpFilter.orCondition && this.grid.model.config.agpFilter.filters.length) {
            this.gridService.grids[this.name].viewRows = rowsRef;
        }
        else {
            this.gridService.grids[this.name].viewRows = viewRowsRef;
        }
    }
    ngOnDestroy() {
        this.model = null;
        this.grid = null;
    }
}
AgpFilterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFilterDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpFilterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpFilterDirective, selector: "[agpFilter]", inputs: { AgpFilterDirective: "AgpFilterDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFilterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpFilter]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpFilterDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpFilterModule {
}
AgpFilterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFilterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpFilterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpFilterModule, declarations: [AgpFilterDirective], imports: [CommonModule], exports: [AgpFilterDirective] });
AgpFilterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFilterModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFilterModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpFilterDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpFilterDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpFixedDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpFixedDirective = null;
        this.model = null;
    }
    ngOnInit() {
        this.name = this.model.config.name;
        if (!this.gridService.grids[this.name]) {
            return;
        }
        const gs = this.gridService.grids[this.name];
        this.grid = gs;
        const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
            if (res && this.grid.model.config['agpFixed']) {
                this.grid.registeredPlugins['fixed'] = {};
                this.grid.registeredPlugins._sequence.push('fixed');
                this.startDirective();
            }
        });
        gs.subscriptions.push(STAGE_1);
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpFixed')) {
            config.agpFixed = true;
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpFixed']) {
            if (this.grid.model.config['grid-type'] === 'div-grid') {
                this.grid.model.config.cssGridClass.push('axis-table-fixed');
            }
            this.grid.changeDetection.detectChanges();
        }
    }
}
AgpFixedDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpFixedDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpFixedDirective, selector: "[agpFixed]", inputs: { AgpFixedDirective: "AgpFixedDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpFixed]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpFixedDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpFixedModule {
}
AgpFixedModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpFixedModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedModule, declarations: [AgpFixedDirective], imports: [CommonModule], exports: [AgpFixedDirective] });
AgpFixedModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpFixedDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpFixedDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpInCellEditDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpInCellEditDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (this.gridService.grids[this.name]) {
            this.name = this.model.config.name;
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['inCellEdit'] = {};
                    this.grid.registeredPlugins._sequence.push('inCellEdit');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpInCellEdit']) {
            this.addInCellEdit();
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpInCellEdit')) {
            config.agpInCellEdit = true;
        }
    }
    listSelectors(types) {
        const res = [];
        types.forEach((t) => {
            res.push(`.axis-datagrid-cell[cell-type="${t}"]`);
        });
        return res.join(',');
    }
    addInCellEdit() {
        // Find all cells that can be edited and add double click
        const selectors = this.listSelectors(['text', 'number', 'boolean']);
        this.el.nativeElement.querySelectorAll(selectors).forEach((c) => {
            c.addEventListener('dblclick', () => {
                if (!this.grid.isEditModeOn) {
                    this.startEdit(c);
                }
            });
        });
    }
    startEdit(c) {
        // Turn edit on and off
        this.grid.isEditModeOn = true;
        const content = c.innerHTML;
        const input = document.createElement('input');
        const stopEdit = (e) => {
            if ((e && e.type && e.type === 'blur') ||
                (e.type === 'keyup' && e.key === 'Enter')) {
                input.removeEventListener('blur', stopEdit);
                input.removeEventListener('click', stopEdit);
                const value = input.value;
                input.parentNode.removeChild(input);
                c.innerHTML = value;
                this.gridService.sendUpdate(this.name, {
                    type: 'cellEdit',
                    name: this.name,
                    cell: c,
                    oldContent: content,
                    newContent: value,
                    isConcentChanged: content !== value,
                });
                this.grid.isEditModeOn = false;
            }
        };
        input.setAttribute('type', 'text');
        input.setAttribute('placeholder', content);
        input.setAttribute('value', content);
        input.classList.add('axis-form-control');
        input.addEventListener('keyup', stopEdit);
        input.addEventListener('blur', stopEdit);
        c.innerHTML = '';
        c.appendChild(input);
        input.focus();
    }
}
AgpInCellEditDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpInCellEditDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpInCellEditDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpInCellEditDirective, selector: "[agpInCellEdit]", inputs: { AgpInCellEditDirective: "AgpInCellEditDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpInCellEditDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpInCellEdit]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpInCellEditDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpInCellEditModule {
}
AgpInCellEditModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpInCellEditModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpInCellEditModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpInCellEditModule, declarations: [AgpInCellEditDirective], imports: [CommonModule], exports: [AgpInCellEditDirective] });
AgpInCellEditModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpInCellEditModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpInCellEditModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpInCellEditDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpInCellEditDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpIncrementalScrollDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.model = null;
        this.canStart = false;
        this.name = null;
        this.grid = null;
        this.allRowsRefs = null;
        this.config = null;
        this.isFullyLoaded = false;
        this.fullViewRows = [];
        this.setYoffset = true;
    }
    ngOnInit() {
        this.prepareDirective();
        setTimeout(() => { this.addSubscriptions(); }, 0);
    }
    ngOnDestroy() {
        if (this.bodyRef) {
            this.bodyRef.removeEventListener('scroll', (e) => {
                this.hasScrolled(e);
            });
        }
        else {
            window.removeEventListener('scroll', (e) => {
                this.hasScrolled(e);
            });
        }
        this.model = null;
        this.grid = null;
    }
    prepareDirective() {
        this.name = this.model.config.name;
        const gs = this.gridService;
        this.grid = gs.grids[this.name];
        if (this.grid.model.config.hasOwnProperty('scrollType') &&
            this.grid.model.config.scrollType === 'incremental') {
            this.grid.registeredPlugins['incrementalScroll'] = {};
            this.grid.registeredPlugins._sequence.push('incrementalScroll');
            this.grid.model.config.cssGridClass.push('axis-datagrid-incremental-scroll');
            this.canStart = true;
            gs.grids[this.name].dataPipeLine[555] = this.incrementalRows.bind(this);
            this.reviewConfig(this.grid.model);
            gs.changeLastChanged(this.name);
            gs.getRows(this.name);
        }
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    if (this.canStart) {
                        this.startDirective();
                    }
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    reviewConfig(model) {
        // Merge config from JSON into plugin's default config
        if (!model.hasOwnProperty('config')) {
            model.config = {};
        }
        if (!model.config.hasOwnProperty('rowHeight')) {
            model.config.rowHeight = 30;
        }
        if (!model.config.hasOwnProperty('scrollType')) {
            model.config.scrollType = 'incremental';
        }
        if (model.config.scrollType !== 'incremental') {
            Logger.error('Axis-grid:', 'You are using the Incremental plugin, but the config is set to another kind of scrollType.', model.config);
        }
        this.config = model.config;
    }
    startDirective() {
        // Bind On Scroll Event
        const isFixedColumn = Object.keys(this.grid.registeredPlugins).indexOf('fixedColumn');
        this.bodyRef = this.grid.tbody.nativeElement;
        if (this.model.config.agpFixed || this.model.config.agpFixedColum || isFixedColumn > -1) {
            this.bodyRef.addEventListener('scroll', (e) => {
                this.hasScrolled(e);
            });
        }
        else {
            window.addEventListener('scroll', (e) => {
                if (window.scrollY > 0) {
                    this.hasScrolled(e);
                }
            });
        }
        const userSelection = document.getElementsByClassName('axis-modal-body');
        for (let i = 0; i < userSelection.length; i++) {
            const isDatagridPresent = userSelection[i].getElementsByTagName('axis-datagrid-table').length || userSelection[i].getElementsByTagName('axis-datagrid-div').length;
            if (isDatagridPresent) {
                userSelection[i].addEventListener("scroll", (e) => {
                    this.sendScrollEvent(e);
                });
            }
        }
        // Listen for GRID_UPDATE event
        if (this.grid.model.config.useApiOnScroll) {
            const gs = this.gridService.grids[this.name];
            const UPDATE_GRID = gs.ACTIONS.UPDATE_GRID.subscribe((res) => {
                if (res) {
                    this.gridService.changeLastChanged(this.name);
                    this.incrementalRows(this.name, res);
                }
            });
            gs.subscriptions.push(UPDATE_GRID);
        }
    }
    sendScrollEvent(e) {
        const config = this.grid.model.config;
        if (e.target.scrollHeight <= (e.target.scrollTop + (config.rowHeight * (config.viewportCellCount + config.bufferRows)))) {
            const scrollInfo = {
                eventType: 'scroll',
                scrollType: this.grid.model.config.scrollType,
                eventData: e,
                scrollPos: this.bodyRef.scrollTop,
                from: this.grid.showFrom,
                to: this.grid.showTo,
                getMoreData: true,
                model: this.grid.model,
                name: this.name,
                renderedRowsLength: this.grid.viewRows.length
            };
            this.isFullyLoaded =
                this.grid.viewRows.length === this.grid.model.body.config.totalCount;
            scrollInfo.getMoreData = !this.isFullyLoaded;
            this.gridService.calculate(this.name, this.grid.showTo + config.bufferRows);
            this.gridService.sendAction(this.name, 'SCROLL', scrollInfo);
            if (!this.grid.model.config.useApiOnScroll) {
                this.checkIfChanged();
                this.gridService.grids[this.name].viewRows = this.fullViewRows.slice(0, this.grid.showTo);
            }
        }
    }
    fullPageTopRow() {
        const g = this.grid.model;
        if (this.setYoffset) {
            this.yOffset = window.scrollY;
            this.setYoffset = false;
        }
        let rowTop = Math.floor((window.scrollY - this.yOffset) / g.config.rowHeight);
        if (rowTop < 0) {
            rowTop = 0;
        }
        if (rowTop > g.body.config.totalCount) {
            rowTop = g.body.config.totalCount;
        }
        return rowTop;
    }
    hasScrolled(e) {
        if (this.isFullyLoaded) {
            return;
        }
        const gs = this.gridService;
        if (!gs.grids[this.name]) {
            if (this.fullPageScroll) {
                clearTimeout(this.fullPageScroll);
            }
            return;
        }
        if (this.fullPageScroll) {
            clearTimeout(this.fullPageScroll);
        }
        this.fullPageScroll = setTimeout(() => {
            if (this.grid) {
                this.goGetData(e);
                this.fullPageScroll = null;
            }
        }, 10);
    }
    goGetData(e) {
        const gs = this.gridService;
        const currentTopRow = this.model.config.agpFixed ? gs.currentTopRow(this.name) : this.fullPageTopRow();
        // If changed to a new row
        if (this.grid.oldScrollPos !== currentTopRow) {
            gs.calculate(this.name, currentTopRow);
            this.grid.oldScrollPos = currentTopRow;
            const scrollInfo = {
                eventType: 'scroll',
                scrollType: this.grid.model.config.scrollType,
                eventData: e,
                scrollPos: this.bodyRef.scrollTop,
                from: this.grid.showFrom,
                to: this.grid.showTo,
                getMoreData: true,
                model: this.grid.model,
                name: this.name,
                renderedRowsLength: this.grid.viewRows.length
            };
            // Incremental don't get rows again once it is loaded
            this.isFullyLoaded =
                this.grid.viewRows.length === this.grid.model.body.config.totalCount;
            scrollInfo.getMoreData = !this.isFullyLoaded;
            gs.sendAction(this.name, 'SCROLL', scrollInfo);
            if (!this.grid.model.config.useApiOnScroll) {
                this.checkIfChanged();
                this.gridService.grids[this.name].viewRows = this.fullViewRows.slice(0, this.grid.showTo);
            }
        }
    }
    checkIfChanged() {
        const range = this.grid.viewRows.length - this.grid.showTo;
        if (range > 0) {
            this.gridService.changeLastChanged(this.name);
        }
    }
    incrementalRows(name, res = null) {
        const gs = this.gridService;
        // If not trying to get new row, return normal grid
        const currentTopRow = gs.currentTopRow(this.name);
        this.fullViewRows = [...this.grid.viewRows];
        gs.calculate(name, currentTopRow);
        const range = this.grid.viewRows.length - this.grid.showTo;
        const isUsingApi = this.grid.model.config.useApiOnScroll;
        if (this.grid.isFirstLoad && range <= 0) {
            if (isUsingApi) {
                this.grid.isFirstLoad = false;
            }
            return this.grid.viewRows;
        }
        // Or continue
        let rows;
        if (!this.grid.isFirstLoad && isUsingApi && res) {
            this.fullViewRows = [...this.grid.viewRows, ...res.payload.body.rows];
            this.grid.showTo = this.fullViewRows.length;
            this.grid.model.body.rows = this.fullViewRows;
        }
        else {
            rows = this.fullViewRows;
            this.grid.isFirstLoad = false;
        }
        const newRows = [];
        if (this.grid.model.lastChanged === 0 ||
            this.grid.model.lastChanged !== this.grid.lastChanged) {
            let changed = false;
            if (this.grid.viewRows.length === 0) {
                const limit = isUsingApi ? rows.length : this.grid.showTo;
                for (let i = 0; i < limit; i++) {
                    this.grid.viewRows.push(Object.assign(rows[i], { show: true }));
                    newRows.push(rows[i].rowIndex);
                }
                changed = true;
            }
            else {
                this.grid.viewRows = this.fullViewRows.slice(0, this.grid.showTo);
                this.isFullyLoaded = false;
                changed = true;
            }
            if (changed) {
                this.grid.oldScrollPos = gs.currentTopRow(name);
            }
        }
        setTimeout(() => {
            this.gridService.sendAction(this.name, 'GRID_HAS_CHANGED', {
                name: this.name,
                grid: this.grid,
                newRows,
            });
        }, 1);
    }
}
AgpIncrementalScrollDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpIncrementalScrollDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpIncrementalScrollDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpIncrementalScrollDirective, selector: "[agpIncrementalScroll]", inputs: { model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpIncrementalScrollDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpIncrementalScroll]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { model: [{
                type: Input
            }] } });

class AgpIncrementalScrollModule {
}
AgpIncrementalScrollModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpIncrementalScrollModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpIncrementalScrollModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpIncrementalScrollModule, declarations: [AgpIncrementalScrollDirective], imports: [CommonModule], exports: [AgpIncrementalScrollDirective] });
AgpIncrementalScrollModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpIncrementalScrollModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpIncrementalScrollModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpIncrementalScrollDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpIncrementalScrollDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpKeyboardNavDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpKeyboardNavDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (this.gridService.grids[this.name]) {
            this.name = this.model.config.name;
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const GRID_HAS_CHANGED = gs.ACTIONS.GRID_HAS_CHANGED.pipe(delay(0)).subscribe((res) => {
                if (res && this.grid) {
                    this.startDirective();
                }
            });
            gs.subscriptions.push(GRID_HAS_CHANGED);
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res && this.grid) {
                    this.grid.registeredPlugins['keyboardNav'] = {};
                    this.grid.registeredPlugins._sequence.push('keyboardNav');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpKeyboardNav']) {
            this.addKeyboardNav();
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpKeyboardNav')) {
            config.agpKeyboardNav = true;
        }
    }
    addKeyboardNav() {
        // KeyboardNav code
        let i = 1;
        const cells = this.el.nativeElement.querySelectorAll('.axis-datagrid-cell:not([hasKeyboardPlugin="true"])');
        cells.forEach((c) => {
            if (c.classList.contains('axis-datagrid-cell-header')) {
                return;
            }
            c.classList.add('axis-datagrid-keyboard');
            c.setAttribute('hasKeyboardPlugin', true);
            c.tabIndex = i;
            i++;
            c.addEventListener('keydown', (e) => {
                if (!this.grid.isEditModeOn) {
                    const protectedKeys = [
                        'ArrowUp',
                        'ArrowDown',
                        'ArrowRight',
                        'ArrowLeft',
                    ];
                    if (protectedKeys.indexOf(e.key) > -1) {
                        e.preventDefault();
                    }
                }
            });
            c.addEventListener('keyup', (e) => {
                if (!this.grid.isEditModeOn) {
                    if (e.key === 'ArrowUp') {
                        e.cancelBubble = true;
                        e.stopPropagation();
                        e.preventDefault();
                        this.up(c);
                        return false;
                    }
                    if (e.key === 'ArrowRight' || e.key === 'Tab') {
                        e.cancelBubble = true;
                        e.stopPropagation();
                        e.preventDefault();
                        this.right(c);
                        return false;
                    }
                    if (e.key === 'ArrowDown') {
                        e.cancelBubble = true;
                        e.stopPropagation();
                        e.preventDefault();
                        this.down(c);
                        return false;
                    }
                    if (e.key === 'ArrowLeft') {
                        e.cancelBubble = true;
                        e.stopPropagation();
                        e.preventDefault();
                        this.left(c);
                        return false;
                    }
                }
            });
        });
    }
    up(c) {
        const current = c.getAttribute('cell-index').split('.');
        if (current[0] >= 1) {
            const pos = Math.floor(current[0] * 1 - 1) + '.' + current[1];
            document.querySelector(`[cell-index="${pos}"]`).focus();
        }
    }
    right(c) {
        const numberColumns = this.grid.model.body.rows[0].cells.length;
        const current = c.getAttribute('cell-index').split('.');
        if (current[1] < numberColumns - 1) {
            const pos = current[0] + '.' + Math.floor(current[1] * 1 + 1);
            document.querySelector(`[cell-index="${pos}"]`).focus();
        }
    }
    down(c) {
        const numberRows = this.grid.model.body.rows.length;
        const current = c.getAttribute('cell-index').split('.');
        if (current[0] < numberRows - 1) {
            const pos = Math.floor(current[0] * 1 + 1) + '.' + current[1];
            document.querySelector(`[cell-index="${pos}"]`).focus();
        }
    }
    left(c) {
        const current = c.getAttribute('cell-index').split('.');
        if (current[1] > 0) {
            const pos = current[0] + '.' + Math.floor(current[1] * 1 - 1);
            document.querySelector(`[cell-index="${pos}"]`).focus();
        }
    }
}
AgpKeyboardNavDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpKeyboardNavDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpKeyboardNavDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpKeyboardNavDirective, selector: "[agpKeyboardNav]", inputs: { AgpKeyboardNavDirective: "AgpKeyboardNavDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpKeyboardNavDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpKeyboardNav]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpKeyboardNavDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpKeyboardNavModule {
}
AgpKeyboardNavModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpKeyboardNavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpKeyboardNavModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpKeyboardNavModule, declarations: [AgpKeyboardNavDirective], imports: [CommonModule], exports: [AgpKeyboardNavDirective] });
AgpKeyboardNavModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpKeyboardNavModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpKeyboardNavModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpKeyboardNavDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpKeyboardNavDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpMouseColorDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpMouseColorDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (this.gridService.grids[this.name]) {
            this.name = this.model.config.name;
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['mouseColor'] = {};
                    this.grid.registeredPlugins._sequence.push('mouseColor');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpMouseColor']) {
            this.addMouseColor();
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpMouseColor')) {
            config.agpMouseColor = true;
        }
    }
    addMouseColor() {
        const rows = this.el.nativeElement.querySelectorAll(`*[role="row"]`);
        rows.forEach((r) => {
            r.classList.add('axis-datagrid-plugin-mouse-over');
        });
    }
}
AgpMouseColorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMouseColorDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpMouseColorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpMouseColorDirective, selector: "[agpMouseColor]", inputs: { AgpMouseColorDirective: "AgpMouseColorDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMouseColorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpMouseColor]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpMouseColorDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpMouseColorModule {
}
AgpMouseColorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMouseColorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpMouseColorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpMouseColorModule, declarations: [AgpMouseColorDirective], imports: [CommonModule], exports: [AgpMouseColorDirective] });
AgpMouseColorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMouseColorModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMouseColorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpMouseColorDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpMouseColorDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpResizeDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpResizeDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (this.gridService.grids[this.name]) {
            this.name = this.model.config.name;
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['resizeColumn'] = {};
                    this.grid.registeredPlugins._sequence.push('resizeColumn');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpResize']) {
            //
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpResize')) {
            config.agpResize = true;
        }
    }
}
AgpResizeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpResizeDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpResizeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpResizeDirective, selector: "[agpResize]", inputs: { AgpResizeDirective: "AgpResizeDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpResizeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpResize]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpResizeDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpResizeModule {
}
AgpResizeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpResizeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpResizeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpResizeModule, declarations: [AgpResizeDirective], imports: [CommonModule], exports: [AgpResizeDirective] });
AgpResizeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpResizeModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpResizeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpResizeDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpResizeDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpRowStyleDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AxisGridPluginRowStyleDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (this.gridService.grids[this.name]) {
            this.name = this.model.config.name;
            const gs = this.gridService.grids[this.name];
            this.grid = gs;
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['rowStyle'] = {};
                    this.grid.registeredPlugins._sequence.push('rowStyle');
                    this.startDirective();
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['axisGridPluginRowStyle']) {
            //
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('axisGridPluginRowStyle')) {
            config.axisGridPluginRowStyle = true;
        }
    }
}
AgpRowStyleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpRowStyleDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpRowStyleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpRowStyleDirective, selector: "[agpRowStyle]", inputs: { AxisGridPluginRowStyleDirective: "AxisGridPluginRowStyleDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpRowStyleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpRowStyle]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AxisGridPluginRowStyleDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpRowStyleModule {
}
AgpRowStyleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpRowStyleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpRowStyleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpRowStyleModule, declarations: [AgpRowStyleDirective], imports: [CommonModule], exports: [AgpRowStyleDirective] });
AgpRowStyleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpRowStyleModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpRowStyleModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpRowStyleDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpRowStyleDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpSortDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.model = null;
        this.sortDisabled = false;
        this.AgpSortDirective = null;
    }
    ngOnInit() {
        this.prepareDirective();
        this.addSubscriptions();
    }
    prepareDirective() {
        this.name = this.model.config.name;
        this.grid = this.gridService.grids[this.name];
        if (!this.grid.model.config.agpSort.serverSort) {
            this.grid.dataPipeLine[333] = this.sort.bind(this);
        }
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const STAGE_2 = this.grid.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.name = res.payload.name;
                    this.grid = res.payload.grid;
                    this.grid.registeredPlugins['sort'] = {};
                    this.grid.registeredPlugins._sequence.push('sort');
                    this.startDirective();
                }
            });
            this.grid.subscriptions.push(STAGE_2);
            const VIRTUAL_SCROLL_STAGE_3 = this.grid.ACTIONS.VIRTUAL_SCROLL_STAGE_3.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.addSort(null);
                }
            });
            this.grid.subscriptions.push(VIRTUAL_SCROLL_STAGE_3);
            const GRID_CHANGED = this.grid.ACTIONS.GRID_CHANGED.pipe(delay(0)).subscribe((res) => {
                if (res && res.payload && res.payload.column) {
                    this.addSort(res.payload.column);
                }
            });
            this.grid.subscriptions.push(GRID_CHANGED);
        }
    }
    startDirective() {
        this.pluginConfig();
        this.config = this.grid.model.config.agpSort;
        // This load property on the config can be used to programmatically disable sort plugin
        if (this.config.load) {
            this.grid.sortColumns = {};
            this.grid.sortColumnsSequence = [];
            if (!this.grid.model.config.hasOwnProperty('scrollType') || this.grid.model.config.scrollType !== 'virtual') {
                this.addSort(null);
            }
        }
    }
    pluginConfig() {
        // Create config to sort plugin if it does not exist
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpSort')) {
            config.agpSort = {
                load: true,
                concurrentSorting: false,
                columnNames: [],
            };
        }
        else if (!config.agpSort.hasOwnProperty('columnNames')) {
            config.agpSort.columnNames = [];
        }
        // Create columns property on config, if it does not exist
        if (!config.agpSort.hasOwnProperty('columns')) {
            const rows = this.grid.model.header.rows;
            config.agpSort.columns = {};
            if (rows.length > 0) {
                rows[0].cells.forEach((c) => {
                    if (c.type !== 'checkbox') {
                        if (config.agpSort.columnNames.indexOf(c.alias) > -1) {
                            config.agpSort.columns[c.alias] = c.type;
                        }
                        if (c.hasOwnProperty('subColumns')) {
                            c.subColumns.forEach((sub) => {
                                if (config.agpSort.columnNames.indexOf(sub.alias) > -1) {
                                    config.agpSort.columns[sub.alias] = sub.type;
                                }
                            });
                        }
                    }
                });
            }
        }
    }
    addSort(columnName) {
        const config = this.grid.model.config;
        const columnHeads = this.el.nativeElement.querySelectorAll('.axis-grid-is-header .axis-grid-content');
        columnHeads.forEach((c) => {
            const alias = c.getAttribute('alias');
            if (config.agpSort.columns.hasOwnProperty(alias) && config.agpSort.columnNames.indexOf(alias) > -1
                && (columnName === null || alias === columnName)) {
                c.parentElement.classList.add('axis-sortable');
                c.addEventListener('click', () => {
                    if (!this.sortDisabled) {
                        this.changeOrder(c);
                    }
                });
                c.classList.add('axis-sortable-text');
                c.classList.add('axis-sortable-btn');
                c.setAttribute('role', 'button');
                c.setAttribute('tabIndex', '0');
                if (config.agpSort.hasOwnProperty('sort') && config.agpSort.sort && config.agpSort.sort.length) {
                    if (config.agpSort.concurrentSorting) {
                        const sort = config.agpSort.sort.filter((x) => x.name === alias);
                        if (sort && sort.length) {
                            this.changeOrder(c, sort[0].order == 'ascending' ? 'descending' : 'ascending');
                        }
                    }
                    else if (config.agpSort.sort[0].name === alias) {
                        this.changeOrder(c, config.agpSort.sort[0].order == 'ascending' ? 'descending' : 'ascending');
                    }
                }
            }
        });
    }
    resetOtherSortIcons() {
        const config = this.grid.model.config;
        const columnHeads = this.el.nativeElement.querySelectorAll('.axis-grid-is-header .axis-grid-content');
        columnHeads.forEach((c) => {
            const alias = c.getAttribute('alias');
            if (config.agpSort.columns.hasOwnProperty(alias)) {
                c.parentElement.classList.add('axis-sortable');
                c.parentElement.classList.remove('axis-sort-asc');
                c.parentElement.classList.remove('_multiple-sort');
                c.parentElement.classList.remove('axis-sort-desc');
                c.classList.remove('axis-sort-asc');
                c.classList.remove('axis-sort-desc');
            }
        });
    }
    changeOrder(el, orderBy) {
        // This method gets called when sort icon is clicked
        // It changes the icons, aria-labels, etc,
        // Then call sorting evoker
        let status = orderBy ? orderBy : el.parentElement.getAttribute('aria-sort');
        const multiColumn = el.parentElement.getAttribute('has-subcolumns');
        if (multiColumn === "true") {
            status = orderBy ? orderBy : el.getAttribute('sort-by');
        }
        const alias = el.getAttribute('alias');
        const initialIndex = el.getAttribute('initial-index');
        if (!this.config.concurrentSorting) {
            this.grid.sortColumnsSequence = [];
            this.resetOtherSortIcons();
        }
        const index = this.grid.sortColumnsSequence.indexOf(alias);
        if (index === -1) {
            this.grid.sortColumnsSequence.push(alias);
        }
        if (status !== 'ascending') {
            if (multiColumn === "true") {
                if (!this.config.concurrentSorting) {
                    for (let i = 0; i < el.parentElement.childElementCount; i++) {
                        el.parentElement.children[i].removeAttribute('sort-by');
                    }
                }
                el.parentElement.setAttribute('aria-sort', 'other');
                el.setAttribute('sort-by', 'ascending');
            }
            else {
                el.parentElement.setAttribute('aria-sort', 'ascending');
            }
            el.classList.add('axis-sort-asc');
            this.grid.sortColumns[alias] = {
                columnName: alias,
                columnIndex: initialIndex,
                sort: 'asc',
            };
            if (multiColumn === "true") {
                el.parentElement.classList.add('_multiple-sort');
            }
            else {
                el.parentElement.classList.add('axis-sort-asc');
            }
            el.parentElement.classList.remove('axis-sort-desc');
        }
        else {
            if (multiColumn === "true") {
                el.parentElement.setAttribute('aria-sort', 'other');
                el.setAttribute('sort-by', 'descending');
            }
            else {
                el.parentElement.setAttribute('aria-sort', 'descending');
            }
            el.classList.add('axis-sort-desc');
            this.grid.sortColumns[alias] = {
                columnIndex: initialIndex,
                sort: 'desc',
            };
            if (multiColumn === "true") {
                el.parentElement.classList.add('_multiple-sort');
            }
            else {
                el.parentElement.classList.add('axis-sort-desc');
            }
            el.parentElement.classList.remove('axis-sort-asc');
        }
        this.callSort(alias);
    }
    callSort(columnName) {
        // This is the sort evoker
        // It send an action to listeners and call getRows from service
        // By now sort is registered on position 333 within dataPipeline
        this.gridService.sendAction(this.name, 'EVENT', {
            type: 'sort',
            name: this.name,
            grid: this.grid,
            columnName: columnName,
            columns: this.grid.sortColumns,
            renderedRowsLength: this.grid.viewRows.length
        });
        if (!this.grid.model.config.agpSort.serverSort) {
            this.gridService.getRows(this.name);
            this.grid.changeDetection.detectChanges();
        }
    }
    sort() {
        // This is the method that the dataPipeline will execute
        // It will mutate the viewRows object by sorting the elements
        const config = this.grid.model.config.agpSort;
        const sortInfo = {
            grid: this.grid,
            name: this.name,
            columnsToSort: this.grid.sortColumnsSequence,
            columsSortConfig: this.grid.sortColumns,
        };
        if (config.hasOwnProperty('customMethod')) {
            this.grid.viewRows.sort(config['customMethod'](sortInfo));
        }
        else {
            this.grid.viewRows.sort(this.compare());
        }
        this.gridService.changeLastChanged(this.name);
    }
    getColumnType(columnName) {
        let type = '';
        this.grid.model.header.rows[0].cells.forEach((element) => {
            const subColumn = element.subColumns && element.subColumns.find(sub => sub.alias === columnName);
            if (element.alias === columnName || subColumn) {
                type = subColumn ? subColumn.type : element.type;
            }
        });
        return type;
    }
    compare() {
        // Compare method injected into the native javascript sorting algorithm
        return (a, b) => {
            let order;
            let result = 0;
            if (!this.grid.hasOwnProperty('sortColumnsSequence')) {
                this.grid.sortColumnsSequence = [];
            }
            this.grid.sortColumnsSequence.forEach((colName) => {
                if (result === 0) {
                    const initialIndex = this.grid.sortColumns[colName].columnIndex;
                    order = this.grid.sortColumns[colName].sort;
                    let thisA = a.cells[initialIndex];
                    let thisB = b.cells[initialIndex];
                    if (a.cells[initialIndex].mapTo !== colName &&
                        a.cells[initialIndex].hasOwnProperty('subColumns')) {
                        let subIndex = 0;
                        const subLength = a.cells[initialIndex].subColumns.length;
                        for (let i = 0; i < subLength; i++) {
                            if (a.cells[initialIndex].subColumns[i].mapTo === colName) {
                                subIndex = i;
                            }
                        }
                        thisA = a.cells[initialIndex].subColumns[subIndex];
                        thisB = b.cells[initialIndex].subColumns[subIndex];
                    }
                    thisA.type = this.getColumnType(colName);
                    thisB.type = thisA.type;
                    const type = thisA.type;
                    const aVal = (thisA.type === 'custom' && thisA.hasOwnProperty('pureContent')) ? thisA.pureContent : thisA.content;
                    const bVal = (thisB.type === 'custom' && thisB.hasOwnProperty('pureContent')) ? thisB.pureContent : thisB.content;
                    const varA = ((type === 'text' || type === 'custom') && typeof aVal === 'string') ? aVal.toUpperCase() : aVal * 1;
                    const varB = ((type === 'text' || type === 'custom') && typeof bVal === 'string') ? bVal.toUpperCase() : bVal * 1;
                    if ((type === 'text' || type === 'custom') && typeof aVal === 'string') {
                        if (varA > varB) {
                            result = 1;
                        }
                        else if (varA < varB) {
                            result = -1;
                        }
                    }
                    else {
                        if (varA - varB !== 0) {
                            result = varA - varB;
                        }
                    }
                }
            });
            return order === 'desc' ? result * -1 : result;
        };
    }
    ngOnDestroy() {
        this.model = null;
        this.grid = null;
    }
}
AgpSortDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpSortDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpSortDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpSortDirective, selector: "[agpSort]", inputs: { model: "model", sortDisabled: "sortDisabled", AgpSortDirective: "AgpSortDirective" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpSortDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpSort]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { model: [{
                type: Input
            }], sortDisabled: [{
                type: Input
            }], AgpSortDirective: [{
                type: Input
            }] } });

class AgpSortModule {
}
AgpSortModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpSortModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpSortModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpSortModule, declarations: [AgpSortDirective], imports: [CommonModule], exports: [AgpSortDirective] });
AgpSortModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpSortModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpSortModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpSortDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpSortDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpStripedDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpStripedDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (!this.gridService.grids[this.name]) {
            return;
        }
        this.name = this.model.config.name;
        const gs = this.gridService.grids[this.name];
        this.grid = gs;
        const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
            if (res) {
                this.name = res.payload.name;
                this.grid = res.payload.grid;
                this.grid.registeredPlugins['striped'] = {};
                this.grid.registeredPlugins._sequence.push('striped');
                this.startDirective();
            }
        });
        gs.subscriptions.push(STAGE_1);
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpStriped')) {
            config.agpStriped = true;
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpStriped']) {
            this.grid.model.config.cssGridClass.push('axis-table-striped');
            this.grid.changeDetection.detectChanges();
        }
    }
}
AgpStripedDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStripedDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpStripedDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpStripedDirective, selector: "[agpStriped]", inputs: { AgpStripedDirective: "AgpStripedDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStripedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpStriped]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpStripedDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpStripedModule {
}
AgpStripedModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStripedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpStripedModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpStripedModule, declarations: [AgpStripedDirective], imports: [CommonModule], exports: [AgpStripedDirective] });
AgpStripedModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStripedModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStripedModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpStripedDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpStripedDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpTooltipDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.AgpTooltipDirective = null;
        this.model = null;
    }
    ngOnInit() {
        if (!this.gridService.grids[this.name]) {
            return;
        }
        this.name = this.model.config.name;
        const gs = this.gridService.grids[this.name];
        this.grid = gs;
        const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
            if (res) {
                this.name = res.payload.name;
                this.grid = res.payload.grid;
                this.grid.registeredPlugins['tooltip'] = {};
                this.grid.registeredPlugins._sequence.push('tooltip');
                this.startDirective();
            }
        });
        gs.subscriptions.push(STAGE_2);
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpTooltip']) {
            //
        }
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpTooltip')) {
            config.agpTooltip = true;
        }
    }
    ngOnDestroy() {
        this.model = null;
        this.grid = null;
    }
}
AgpTooltipDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTooltipDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpTooltipDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpTooltipDirective, selector: "[agpTooltip]", inputs: { AgpTooltipDirective: "AgpTooltipDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpTooltip]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpTooltipDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpTooltipModule {
}
AgpTooltipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpTooltipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpTooltipModule, declarations: [AgpTooltipDirective], imports: [CommonModule], exports: [AgpTooltipDirective] });
AgpTooltipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTooltipModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpTooltipDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpTooltipDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpTruncateDirective {
    constructor(el, gridService, renderer, viewContainerRef, tooltipService, cd, utilsService) {
        this.el = el;
        this.gridService = gridService;
        this.renderer = renderer;
        this.viewContainerRef = viewContainerRef;
        this.tooltipService = tooltipService;
        this.cd = cd;
        this.utilsService = utilsService;
        this.AgpTruncateDirective = null;
        this.model = null;
        this.lastItem = null;
        this.domListeners = [];
    }
    ngOnInit() {
        this.name = this.model.config.name;
        setTimeout(() => {
            this.addSubscriptions();
            this.gridService.isContentClicked.subscribe(() => {
                if (this.lastItem) {
                    this.lastItem.tooltipDirective.removeTooltipOnMouseout({});
                }
            });
        }, 50);
    }
    addSubscriptions() {
        if (!this.gridService.grids[this.name]) {
            return;
        }
        const gs = this.gridService.grids[this.name];
        this.grid = gs;
        const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(1)).subscribe((res) => {
            if (res) {
                this.name = res.payload.name;
                this.grid = res.payload.grid;
                this.grid.registeredPlugins['truncate'] = {};
                this.grid.registeredPlugins._sequence.push('truncate');
                this.grid.model.config.cssGridClass.push('axis-grid-will-have-truncate');
            }
        });
        gs.subscriptions.push(STAGE_1);
        // Add truncate after init
        const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(1)).subscribe((res) => {
            if (res) {
                this.startDirective();
            }
        });
        gs.subscriptions.push(STAGE_2);
        // Add truncate on scroll
        const SCROLL = gs.ACTIONS.SCROLL.pipe(delay(1)).subscribe((res) => {
            if (res) {
                this.handleSubResult(res);
                if (this.lastItem) {
                    this.lastItem.tooltipDirective.removeTooltipOnMouseout({});
                }
            }
        });
        gs.subscriptions.push(SCROLL);
        const COL_DRAGGED = gs.ACTIONS.COL_DRAGGED.pipe(delay(0)).subscribe((res) => {
            this.handleSubResult(res);
        });
        gs.subscriptions.push(COL_DRAGGED);
        const GRID_CHANGED = gs.ACTIONS.GRID_CHANGED.pipe(delay(0)).subscribe((res) => {
            this.handleSubResult(res);
        });
        gs.subscriptions.push(GRID_CHANGED);
    }
    handleSubResult(res) {
        if (res) {
            this.removeRendererListeners();
            this.startDirective();
        }
    }
    startDirective() {
        let keys = Object.keys(this.grid.cells);
        if (this.grid.model.config.scrollType === 'virtual') {
            if (!this.grid.tbody) {
                return;
            }
            keys = this.grid.tbody.nativeElement.querySelectorAll('.axis-grid-content');
        }
        keys.forEach((k) => {
            let cell;
            if (this.grid.model.config.scrollType === 'virtual') {
                cell = k.parentNode;
            }
            else {
                cell = this.grid.cells[k].nativeElement;
            }
            if (cell.getAttribute('cell-type') !== 'custom') {
                const rowIndex = cell.getAttribute('row-index');
                if (rowIndex >= this.grid.showFrom &&
                    rowIndex < this.grid.showTo &&
                    !cell.getAttribute('axis-truncate')
                    && !cell.getAttribute('no-truncate')) {
                    this.addTruncate(cell);
                }
            }
        });
    }
    addTruncate(cell) {
        if (cell.getAttribute('cell-type') !== 'custom' &&
            cell.getAttribute('cell-type') !== 'checkbox') {
            const contents = cell.querySelectorAll('.axis-grid-content');
            // Add truncate to each content item inside the cell
            contents.forEach((item) => {
                item.setAttribute('axis-truncate', true);
                const truncateDirective = new AxisTruncateDirective(item);
                truncateDirective.ngAfterViewInit();
                this.addTooltip(item);
            });
        }
    }
    addTooltip(item) {
        item.setAttribute('axis-tooltip', true);
        const tooltipDirective = new TooltipDirective(this.viewContainerRef, this.renderer, this.tooltipService, item, this.utilsService);
        tooltipDirective.content = item.innerHTML;
        // Find a way to register this listener to
        // this.gridService.grids[name].listeners.push()
        // SHERIN
        const unbindTooltipMouseEnterEvent = this.renderer.listen(item, 'mouseenter', (event) => {
            if (item.classList.contains('axis-truncate-should-tooltip')) {
                this.lastItem = { item, tooltipDirective };
                tooltipDirective.appendTooltipOnMouseover(event);
                this.cd.markForCheck();
            }
        });
        const unbindTooltipMouseLeaveEvent = this.renderer.listen(item, 'mouseleave', () => {
            this.lastItem = null;
            tooltipDirective.removeTooltipOnMouseout();
        });
        const unbindTooltipFocusOutEvent = this.renderer.listen(item, 'focusout', () => {
            this.lastItem = null;
            tooltipDirective.removeTooltipOnMouseout();
        });
        this.domListeners.push(unbindTooltipMouseEnterEvent);
        this.domListeners.push(unbindTooltipMouseLeaveEvent);
        this.domListeners.push(unbindTooltipFocusOutEvent);
    }
    onKeyDown() {
        if (this.lastItem) {
            this.lastItem.tooltipDirective.removeTooltipOnMouseout({});
        }
    }
    removeRendererListeners() {
        if (this.domListeners && this.domListeners.length) {
            this.domListeners.forEach(cbk => cbk());
        }
    }
    ngOnDestroy() {
        this.removeRendererListeners();
        if (this.cd) {
            this.cd.detach();
        }
        this.model = null;
        this.grid = null;
    }
}
AgpTruncateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTruncateDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }, { token: i0.Renderer2 }, { token: i0.ViewContainerRef }, { token: i4.TooltipDataSharedService }, { token: i0.ChangeDetectorRef }, { token: i4.AxisUtilsService }], target: i0.ɵɵFactoryTarget.Directive });
AgpTruncateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpTruncateDirective, selector: "[agpTruncate]", inputs: { AgpTruncateDirective: "AgpTruncateDirective", model: "model" }, host: { listeners: { "window:keydown": "onKeyDown()" } }, viewQueries: [{ propertyName: "cells", predicate: AxisGridGenericCellDirective, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTruncateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpTruncate]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }, { type: i0.Renderer2 }, { type: i0.ViewContainerRef }, { type: i4.TooltipDataSharedService }, { type: i0.ChangeDetectorRef }, { type: i4.AxisUtilsService }]; }, propDecorators: { AgpTruncateDirective: [{
                type: Input
            }], model: [{
                type: Input
            }], cells: [{
                type: ViewChildren,
                args: [AxisGridGenericCellDirective]
            }], onKeyDown: [{
                type: HostListener,
                args: ['window:keydown']
            }] } });

class AgpTruncateModule {
}
AgpTruncateModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTruncateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpTruncateModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpTruncateModule, declarations: [AgpTruncateDirective], imports: [CommonModule, TooltipModule], exports: [AgpTruncateDirective] });
AgpTruncateModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTruncateModule, providers: [AxisDatagridService], imports: [CommonModule, TooltipModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpTruncateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpTruncateDirective],
                    imports: [CommonModule, TooltipModule],
                    providers: [AxisDatagridService],
                    exports: [AgpTruncateDirective],
                }]
        }] });

class AgpVirtualScrollTableComponent {
    get inverseOfTranslation() {
        if (!this.viewPort || !this.viewPort['_renderedContentOffset']) {
            return '-1px';
        }
        const offset = this.viewPort['_renderedContentOffset'] + 1;
        return `-${offset}px`;
    }
    constructor(el, gridService, cd, router) {
        this.el = el;
        this.gridService = gridService;
        this.cd = cd;
        this.router = router;
        this.name = null;
        this.grid = false;
        this.allSelected = false;
    }
    ngOnInit() {
        this.grid = this.gridService.grids[this.name];
        this.addVirtualCss();
        setTimeout(() => {
            this.addSubscriptions();
        }, 0);
        this.createColDefinition();
    }
    ngOnDestroy() {
        this.grid = null;
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            const SCROLL = gs.ACTIONS.SCROLL.pipe(delay(0)).subscribe((res) => {
                if (res && res.payload.name === this.name) {
                    const isAllSelected = this.grid.viewRows.every(x => x.selected);
                    if (!isAllSelected && this.checkAll && this.checkAll.checked) {
                        this.checkAll.unCheck();
                    }
                }
            });
            gs.subscriptions.push(SCROLL);
            const COL_DRAGGED = gs.ACTIONS.COL_DRAGGED.pipe(delay(0)).subscribe((res) => {
                if (res && res.payload.name === this.name) {
                    this.createColDefinition();
                }
            });
            gs.subscriptions.push(COL_DRAGGED);
        }
    }
    ngAfterViewInit() {
        this.gridService.grids[this.name].tbody = this.tbody;
        this.grid.cdkViewPort = this.viewPort;
        this.grid.cdkScrollToIndex = this.cdkScrollToIndex.bind(this);
        this.grid.cdkChangeDetectorRef = this.cd;
    }
    countColumns(model) {
        if (model.config.hasOwnProperty('columnsCount')) {
            return model.config.columnsCount;
        }
        if (model.hasOwnProperty('header') && model.header.rows[0].cells.length) {
            return model.header.rows[0].cells.length;
        }
        if (model.hasOwnProperty('body') && model.body.rows[0].cells.length) {
            return model.body.rows[0].cells.length;
        }
    }
    getColumnWidth(model, _numColumns) {
        let gridWidth = this.thisTable.nativeElement.clientWidth;
        let minGridWidth = 0;
        let maxGridWidth = 0;
        const baseline = { min: 50, max: 250, width: 150, fraction: 1 };
        let totalFraction = 0;
        model.header.rows[0].cells.forEach((cw) => {
            if (!cw.hasOwnProperty('columnsWidth')) {
                cw.columnsWidth = Object.assign({}, baseline);
            }
            if (!cw.hideColumn) {
                minGridWidth += cw.columnsWidth.min ? parseInt(cw.columnsWidth.min.match(/(\d+)/)[0], 10) : 0;
                maxGridWidth += cw.columnsWidth.max ? parseInt(cw.columnsWidth.max.match(/(\d+)/)[0], 10) : 0;
                totalFraction += cw.columnsWidth.fraction;
            }
        });
        if (gridWidth < minGridWidth) {
            gridWidth = minGridWidth;
        }
        else if (gridWidth > maxGridWidth) {
            gridWidth = maxGridWidth;
        }
        const unit = Math.floor((gridWidth) / totalFraction);
        model.header.rows[0].cells.forEach((cw) => {
            let newWidth = unit * cw.columnsWidth.fraction;
            const minColWidth = cw.columnsWidth.min ? parseInt(cw.columnsWidth.min.match(/(\d+)/)[0], 10) : 0;
            const maxColWidth = cw.columnsWidth.max ? parseInt(cw.columnsWidth.max.match(/(\d+)/)[0], 10) : 0;
            if (newWidth < minColWidth) {
                newWidth = minColWidth;
            }
            if (newWidth > maxColWidth) {
                newWidth = maxColWidth;
            }
            cw.columnsWidth.realWidth = cw.columnsWidth.width ? newWidth + 'px' : null;
        });
        return model.header.rows[0].cells;
    }
    createColDefinition() {
        setTimeout(() => {
            const tableColgroup = this.el.nativeElement.querySelector('table').getElementsByTagName("colgroup");
            if (tableColgroup.length) {
                this.el.nativeElement.querySelector('table').removeChild(tableColgroup[0]);
            }
            const numColumns = this.countColumns(this.grid.model);
            const cells = this.getColumnWidth(this.grid.model, numColumns);
            const cols = document.createElement('colgroup');
            cells.forEach((c) => {
                const col = document.createElement('col');
                col.span = 1;
                col.style.width = c.columnsWidth.width;
                col.style.minWidth = c.columnsWidth.min;
                col.style.maxWidth = c.columnsWidth.max;
                cols.appendChild(col);
            });
            this.el.nativeElement.querySelector('table').appendChild(cols);
        }, 0);
    }
    addVirtualCss() {
        const css = `
      .axis-datagrid-cellBorder{
        border-style: dashed;
      }
      .axis-grid-virtual-container[axis-grid-name="${this.name}"] {
        height: ${this.grid.model.config.gridHeight ? this.grid.model.config.gridHeight : '300px'};
      }
      [axis-grid-name="${this.name}"] cdk-virtual-scroll-viewport {
        height: 100%;
      }
    `;
        const head = document.getElementsByTagName('head')[0];
        const previous = head.querySelector(`#${this.name}-style-id`);
        if (previous) {
            previous.innerHTML = css;
        }
        else {
            const styleElement = document.createElement('style');
            styleElement.setAttribute('id', this.name + '-style-id');
            styleElement.innerHTML = css;
            document.getElementsByTagName('head')[0].appendChild(styleElement);
        }
    }
    sendScrollEvent(e) {
        const config = this.grid.model.config;
        let from = e - config.bufferRows;
        if (from < 0) {
            from = 0;
        }
        const to = e + config.viewportCellCount + config.bufferRows;
        this.gridService.sendAction(this.name, 'SCROLL', {
            grid: this.grid,
            name: this.name,
            eventType: 'scroll',
            scrollType: this.grid.model.config.scrollType,
            eventData: e,
            scrollPos: this.tbody.nativeElement.scrollTop,
            from,
            to,
            getMoreData: true,
            model: this.grid.model,
        });
    }
    selectAllRows(event, disabled) {
        if (!disabled) {
            this.allSelected = event;
            const selectedList = [];
            this.grid.viewRows.forEach((element) => {
                if (!element.cells[0].disabled && !element.cells[0].hide) {
                    element.selected = event;
                    if (event) {
                        selectedList.push(element);
                    }
                }
            });
            if (this.check && this.check.length) {
                this.check.forEach((element) => {
                    if (event) {
                        if (!element.checked && !element.disabled && !element.hide) {
                            element.check();
                        }
                    }
                    else {
                        element.unCheck();
                    }
                });
            }
            if (this.allSelected) {
                this.gridService.sendAction(this.name, 'ROWS_SELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
            else {
                this.gridService.sendAction(this.name, 'ROWS_UNSELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
        }
    }
    preventClickEmit(event) {
        if (event.clientX) {
            event.preventDefault();
        }
    }
    rowClicked(row) {
        const headerCell = this.grid?.model?.header?.rows[0].cells.find((cell) => (cell.type === 'checkbox' || cell.type === 'radio'));
        if (headerCell && ((headerCell.type === 'checkbox') || (headerCell.type === 'radio' && !row.selected))) {
            const checked = (row.hasOwnProperty('selected') && row.selected);
            const hide = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('hide') && cell.hide)));
            const disabled = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('disabled') && cell.disabled)));
            this.selectRow(checked, row, this.grid?.model?.config?.disableAllRadio || disabled, hide, headerCell.type);
        }
    }
    selectRow(event, row, disabled, hide, type) {
        if (!disabled && !hide) {
            const selectedList = [];
            this.allSelected = true;
            this.grid.viewRows.forEach((element) => {
                if (type == 'radio') {
                    element.selected = false;
                }
                if (element.rowIndex === row.rowIndex) {
                    element.selected = !event;
                }
                if (type === 'checkbox') {
                    if (element.selected) {
                        selectedList.push(element);
                    }
                    else {
                        this.allSelected = false;
                    }
                }
                if (element.rowIndex === row.rowIndex) {
                    if (element.selected) {
                        this.gridService.sendAction(this.name, 'ROW_SELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: selectedList,
                            rowIndex: element.rowIndex
                        });
                    }
                    else {
                        this.gridService.sendAction(this.name, 'ROW_UNSELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: selectedList,
                            rowIndex: element.rowIndex
                        });
                    }
                }
            });
            if (type === 'checkbox') {
                if (this.allSelected && this.checkAll && !this.checkAll.checked) {
                    this.checkAll.check();
                }
                else if (!this.allSelected && this.checkAll && this.checkAll.checked) {
                    this.checkAll.unCheck();
                }
            }
        }
    }
    cdkScrollToIndex(n) {
        this.viewPort.scrollToIndex(n);
    }
    contentClicked(row, columnName, cell) {
        if (cell.route) {
            this.router.navigate([cell.route], { ...cell });
        }
        const payload = {
            content: columnName,
            rowsData: row,
            cell
        };
        this.gridService.isClicked();
        this.gridService.sendAction(this.name, 'ROWS_CLICKED', payload);
    }
    headerColumnClicked(column, subColumn = null) {
        if ((column.hasOwnProperty('isHeaderClickable') && column['isHeaderClickable']) || (subColumn && subColumn.hasOwnProperty('isHeaderClickable') && subColumn['isHeaderClickable'])) {
            const payload = {
                name: this.name,
                column: column,
                subColumnName: subColumn ? subColumn.alias : ''
            };
            this.gridService.sendAction(this.name, 'HEADER_CLICKED', payload);
        }
        this.gridService.sendAction(this.name, 'GRID_CHANGED', this.name);
    }
    export(type) {
        this.gridService.export(this.name, type, this.thisTable);
    }
    toggleSubGrid(cell) {
        cell.showAll = !cell.showAll;
        this.grid.viewRows.forEach((v) => {
            v.showNestedGrid = cell.showAll;
        });
        this.cd.detectChanges();
    }
    expandSubRow(event, row) {
        event?.stopPropagation();
        if (!this.grid?.model?.config?.nestedGridOptions?.expandOthers) {
            this.grid.viewRows.forEach((v) => {
                v.showNestedGrid = row.rowIndex == v.rowIndex ? v.showNestedGrid : false;
            });
        }
        row.showNestedGrid = !row.showNestedGrid;
    }
}
AgpVirtualScrollTableComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollTableComponent, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }, { token: i0.ChangeDetectorRef }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component });
AgpVirtualScrollTableComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: AgpVirtualScrollTableComponent, selector: "axis-datagrid-virtual-scroll-table", inputs: { name: "name" }, viewQueries: [{ propertyName: "thisTable", first: true, predicate: ["thisTable"], descendants: true }, { propertyName: "checkAll", first: true, predicate: ["checkAll"], descendants: true }, { propertyName: "tbody", first: true, predicate: ["tbody"], descendants: true }, { propertyName: "viewPort", first: true, predicate: CdkVirtualScrollViewport, descendants: true }, { propertyName: "check", predicate: ["check"], descendants: true }], ngImport: i0, template: "<div\n  class=\"axis-grid-virtual-container axis-table-responsive\"\n  [attr.axis-grid-name]=\"grid.model.config.name\"\n>\n<div class=\"axis-grid\" *ngIf=\"grid?.model?.config?.download\" >\n  <button *ngIf=\"grid?.model?.config?.download?.excel\" class=\"axis-btn\" (click)=\"export('excel')\">Export To Excel</button>\n  <button *ngIf=\"grid?.model?.config?.download?.pdf\" class=\"axis-btn\" (click)=\"export('pdf')\">Export To PDF</button>\n</div>\n  <cdk-virtual-scroll-viewport\n    #scrollViewport\n    *ngIf=\"grid\"\n    [ngClass]=\"grid ? grid.model?.config?.cssGridParent : []\"\n    itemSize=\"{{ grid.model.config.rowHeight }}\"\n    (scrolledIndexChange)=\"sendScrollEvent($event)\"\n  >\n    <table\n    [attr.aria-describedby]=\"grid.model.config.caption ? grid.model.config.name + '_caption' : null\"\n      class=\"axis-table axis-table-basic\"\n      [ngClass]=\"grid ? grid.model.config.cssGridClass : []\"\n      [attr.aria-rowcount]=\"grid.model.body.rows.length\"\n      role=\"grid\"\n      #thisTable\n      >\n      <caption *ngIf=\"grid.model.config.caption\" id=\"{{grid.model.config.name}}_caption\">\n        {{grid.model.config.caption ? grid.model.config.caption : ''}}\n      </caption>\n\n      <thead\n        role=\"rowgroup\"\n        *ngIf=\"grid.model.header.rows.length > 0 && !grid.model.header.config.hidden\"\n        class=\"axis-datagrid-head axis-grid-is-header\"\n        >\n        <tr\n          aria-rowindex=\"1\"\n          class=\"axis-datagrid-row\"\n          role=\"row\"\n          *ngFor=\"let row of grid.model.header.rows\" [ngClass]=\"grid.model.header.rows[0].class\"\n        >\n        <ng-container *ngFor=\"let cell of row.cells; let i = index\">\n          <th\n          [attr.aria-hidden]=\"(!cell.content?.trim('') && i!= 0) || (i === 0 && (cell.type === 'radio' || (cell.type === 'checkbox' && cell.showSelectAll === false)) || (!cell.content?.trim('') && cell.type != 'radio' && cell.type != 'checkbox')) ? 'true' : null\"\n            scope=\"col\"\n            [attr.role]=\"'columnheader'\"\n            [attr.col-index]=\"i\"\n            class=\"axis-datagrid-cell axis-datagrid-cell-header\"\n            [ngClass]=\"cell.class\"\n            [style.top]=\"inverseOfTranslation\"\n            *ngIf=\"!cell.hideColumn\"\n            [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n            [style.width.px]=\"cell.columnsWidth.realWidth\"\n            draggable=\"{{cell.draggable}}\"\n            >\n            <ng-container *ngIf=\"cell.type !== 'checkbox' && cell.type !== 'radio' && cell.showSelectAll !== false && cell.type !== 'expand'\">\n                <span\n                  class=\"axis-grid-content\"\n                  [attr.alias]=\"cell.alias\"\n                  role=\"status\"\n                  [attr.type]=\"cell.type\"\n                  [attr.initial-index]=\"i\"\n                  [attr.aria-label]=\"cell.ariaLabel\"\n                  draggable=\"{{cell.draggable}}\"\n                  [ngClass]=\"[cell.content ? '' : '_invisibility-md-up', cell?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                  (click)=\"headerColumnClicked(cell)\">\n                  <span [innerHtml]=\"cell.content || cell['data-heading']\"></span>\n                  <span *ngIf=\"cell.icon && cell.icon.name\" [attr.aria-label]=\"cell.icon.ariaLabel\" axis-tooltip [nested]=\"true\" [width]=\"cell.icon?.tooltipWidth\" [content]=\"(cell.icon && cell.icon.tooltipContent) ? cell.icon.tooltipContent : ''\">\n                    <axis-icon [icon]=\"cell.icon.name\" [data-size]=\"cell.icon?.iconSize\" [data-color]=\"cell.icon?.iconColor\" [font]=\"cell.icon?.iconFont || 'material'\"></axis-icon>\n                  </span>\n                  </span\n                >\n                <ng-container *ngIf=\"cell.subColumns\">\n                  <ng-container *ngFor=\"let sub of cell.subColumns\">\n                    <div\n                      class=\"axis-grid-content\"\n                      [attr.alias]=\"sub.alias\"\n                      [attr.type]=\"sub.type\"\n                      role=\"status\"\n                      [attr.initial-index]=\"i\"\n                      [attr.aria-label]=\"sub.ariaLabel\"\n                      [ngClass]=\"[sub?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                      (click)=\"headerColumnClicked(cell, sub)\">\n                      <span [innerHtml]=\"sub.content\"></span>\n                      <span *ngIf=\"sub.icon && sub.icon.name\" [attr.aria-label]=\"sub.icon.ariaLabel\" axis-tooltip [width]=\"sub.icon?.tooltipWidth\" [content]=\"(sub.icon && sub.icon.tooltipContent) ? sub.icon.tooltipContent : ''\">\n                        <axis-icon [icon]=\"sub.icon.name\" [data-size]=\"sub.icon?.iconSize\" [data-color]=\"sub.icon?.iconColor\" [font]=\"sub.icon?.iconFont || 'material'\"></axis-icon>\n                      </span>\n                      </div\n                    >\n                  </ng-container>\n                </ng-container>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && cell.showSelectAll !== false\">\n                <span class=\"axis-grid-content\">\n                  <axis-checkbox #checkAll\n                    [disabled]=\"grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n                    id=\"axis-grid-checkbox-header-{{ i }}\"\n                    [ariaLabel]=\"cell.content?.trim('') ? cell.content : 'Select Row'\"\n                    [attr.data-inline]=\"cell.inline ? true : null\"\n                    (changeDetect)=\"selectAllRows($event, grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled)\"\n                    data-heading=\"{{ cell.content }}\"\n                  ></axis-checkbox>\n                </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && !cell.showSelectAll && cell.content\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                  </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'radio'\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'expand' && grid?.model?.config?.nestedGridOptions?.expandOthers\">\n                <span class=\"axis-grid-content\">\n                  <button axis-button (click)=\"toggleSubGrid(cell)\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n                    ? 'Expanded All'\n                    : 'Collapsed All'\">\n                    <axis-icon\n                        [icon]=\"cell.showAll\n                          ? (grid?.model?.config?.nestedGridOptions?.expandLessIcon ? grid?.model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter' )\n                          : (grid?.model?.config?.nestedGridOptions?.expandMoreIcon ? grid?.model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n                        [data-size]=\"grid?.model?.config?.nestedGridOptions?.iconSize\"\n                        [data-color]=\"grid?.model?.config?.nestedGridOptions?.iconColor\"\n                        [font]=\"grid?.model?.config?.nestedGridOptions?.iconFont || 'custom'\">\n                      </axis-icon>\n                  </button>\n                </span>\n              </ng-container>\n            </th>\n          </ng-container>\n        </tr>\n      </thead>\n      <tbody #tbody>\n        <ng-container\n          *cdkVirtualFor=\"let row of grid.viewRows; let r = index\"\n        >\n          <tr\n            #thisRow\n            [attr.aria-rowindex]=\"r + 2\"\n            role=\"row\"\n            class=\"axis-datagrid-row\"\n            (click)=\"rowClicked(row)\"\n            [ngClass]=\"[(row.selected ? 'row-active': ''), (row.config.background ? 'row-' + row.config.background : ''),(grid.model.header.rows[0].class ? grid.model.header.rows[0].class : ''), ((row?.nestedGridComponent && row?.showNestedGrid) ? 'nested-row' : '')]\"\n            [attr.row-index]=\"r\"\n            [attr.row-real-index]=\"row.rowIndex\"\n          >\n            <ng-container\n              *ngFor=\"let cell of row.cells; let c = index\"\n              #cell\n            >\n              <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.hideColumn\">\n                <td\n                  #thisCell\n                  *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.type || grid?.model?.header?.rows[0]?.cells[c]?.type !== 'custom'\"\n                  [attr.row-index]=\"r\"\n                  [attr.no-truncate]=\"grid?.model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n                  axisDatagridGenericCell\n                  [attr.col-index]=\"c\"\n                  [attr.gridName]=\"grid.model.config.name\"\n                  role=\"gridcell\"\n                  class=\"axis-datagrid-cell\"\n                  [ngClass]=\"[\n                    cell.cellClass ? cell.cellClass : '',\n                    (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                    (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                    cell.background ? 'cell-' + cell.background : '',\n                    (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n                  ]\"\n                  [ngStyle]=\"{\n                    'min-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.min,\n                    'max-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.max\n                  }\"\n                  [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c]['data-heading'] || grid?.model?.header?.rows[0]?.cells[c].content\"\n                  [attr.data-content]=\"(row.cells[c] && row.cells[c]['data-content']) ? row.cells[c]['data-content'] : null\"\n                  [style.width.px]=\"grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.realWidth\"\n                  [attr.cell-index]=\"r + '.' + c\"\n                  [attr.cell-type]=\"grid?.model?.header?.rows[0]?.cells[c]?.type\"\n                  [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n                >\n                  <ng-container\n                    *ngTemplateOutlet=\"content; context: { c: cell, r: row, i:c, type: grid?.model?.header?.rows[0]?.cells[c]?.type, settings:grid?.model?.header?.rows[0]?.cells[c]?.settings }\"\n                  ></ng-container>\n                </td>\n                <td\n                  #thisCell\n                  *ngIf=\"grid?.model?.header?.rows[0]?.cells[c]?.type && grid?.model?.header?.rows[0]?.cells[c]?.type === 'custom'\"\n                  axisDatagridGenericCell\n                  [attr.gridName]=\"grid.model.config.name\"\n                  role=\"gridcell\"\n                  class=\"axis-datagrid-cell\"\n                  [attr.col-index]=\"c\"\n                  [ngClass]=\"[\n                    cell.cellClass ? cell.cellClass : '',\n                    (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                    (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                    cell.background ? 'cell-' + cell.background : '',\n                    (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n                  ]\"\n                  [ngStyle]=\"{\n                    'min-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.min,\n                    'max-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.max\n                  }\"\n                  [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c]['data-heading'] || grid?.model?.header?.rows[0]?.cells[c].content\"\n                  [attr.data-content]=\"(row.cells[c] && row.cells[c]['data-content']) ? row.cells[c]['data-content'] : null\"\n                  [style.width.px]=\"grid.model.header.rows[0].cells[c].columnsWidth.realWidth\"\n                  [attr.cell-index]=\"r + '.' + c\"\n                  [attr.cell-type]=\"grid?.model?.header?.rows[0]?.cells[c]?.type\"\n                >\n                  <axis-datagrid-custom\n                    [uiStructure]=\"cell\"\n                    [uiData]=\"row\"\n                    [name]=\"name\"\n                  ></axis-datagrid-custom>\n                </td>\n              </ng-container>\n            </ng-container>\n          </tr>\n\n          <tr class=\"nested-table\" *ngIf=\"(row?.nestedGridComponent && row?.showNestedGrid)\">\n            <td [attr.colspan]=\"row.cells.length\">\n              <axis-datagrid-custom [uiData]=\"row?.nestedGridData\" [uiStructure]=\"{row, content: row.nestedGridComponent}\" [name]=\"row?.nestedGridData?.config?.name\"></axis-datagrid-custom>\n            </td>\n          </tr>\n\n        </ng-container>\n      </tbody>\n    </table>\n\n    <ng-container *ngIf=\"!grid.model.body.rows.length || !grid?.viewRows?.length\">\n      <div class=\"axis-datagrid-row axis-datagrid-no-results\" role=\"row\">\n        <div class=\"axis-datagrid-cell\" *ngIf=\"!grid.model.config.noResultsUi\">No results.</div>\n        <div class=\"axis-datagrid-cell\" *ngIf=\"grid.model.config.noResultsUi\">\n          <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: grid.model.config.noResultsUi}\"\n            name=\"NoResults\">\n          </axis-datagrid-custom>\n        </div>\n      </div>\n    </ng-container>\n\n  </cdk-virtual-scroll-viewport>\n</div>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\" let-settings=\"settings\">\n  <ng-container *ngIf=\"type === 'expand' && row?.nestedGridComponent\">\n    <button axis-button (click)=\"expandSubRow($event, row);\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n    ? 'Expand'\n    : 'Collapse'\">\n      <axis-icon\n        [icon]=\"row.showNestedGrid ? (grid?.model?.config?.nestedGridOptions?.expandLessIcon ? grid?.model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter' ) : (grid?.model?.config?.nestedGridOptions?.expandMoreIcon ? grid?.model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n        [data-size]=\"grid?.model?.config?.nestedGridOptions?.iconSize\" [data-color]=\"grid?.model?.config?.nestedGridOptions?.iconColor\"\n        [font]=\"grid?.model?.config?.nestedGridOptions?.iconFont || 'custom'\"></axis-icon>\n    </button>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\"\n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\">  {{ cell.content | date: (cell.format ? cell.format : 'MM/dd/yyyy') }} </span>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-checkbox #check\n        [disabled]=\"grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n        id=\"axis-grid-checkbox-{{ row.rowIndex }}\" (click)=\"preventClickEmit($event)\"\n        [attr.data-inline]=\"cell.inline ? true : null\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\"\n      >\n      </axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\" (click)=\"preventClickEmit($event)\"\n        [disabled]=\"grid?.model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\"\n        name=\"axis-grid-radio-{{name}}\"  value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\">\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n    <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n        <span [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </span>\n      </ng-container>\n      <ng-container *ngIf=\"cell.click\">\n        <a href (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.route\">\n        <a routerLink=\"/\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.href\">\n        <a [attr.href]=\"cell.href\"\n          [attr.target]=\"cell.target ? cell.target : '_blank'\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </a>\n      </ng-container>\n    </ng-container>\n  <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n    <ng-container *ngIf=\"!cell.badge && !grid?.model.header.rows[0].cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span [ngClass]=\"[subClass ? subClass : '']\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </span>\n      </div>\n    </ng-container>\n    <ng-container *ngIf=\"cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span\n          [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge) ? 'axis-badge' : '']\"\n          [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n          [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n          axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n          [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </span>\n      </div>\n    </ng-container>\n  </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type !== 'custom'\">\n        <ng-container\n        *ngTemplateOutlet=\"content; context: {\n          c: sub,\n          r: row,\n          subClass: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.class,\n          type: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type,\n          settings: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.settings\n          }\"\n        ></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i3.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i4$1.CdkFixedSizeVirtualScroll, selector: "cdk-virtual-scroll-viewport[itemSize]", inputs: ["itemSize", "minBufferPx", "maxBufferPx"] }, { kind: "directive", type: i4$1.CdkVirtualForOf, selector: "[cdkVirtualFor][cdkVirtualForOf]", inputs: ["cdkVirtualForOf", "cdkVirtualForTrackBy", "cdkVirtualForTemplate", "cdkVirtualForTemplateCacheSize"] }, { kind: "component", type: i4$1.CdkVirtualScrollViewport, selector: "cdk-virtual-scroll-viewport", inputs: ["orientation", "appendOnly"], outputs: ["scrolledIndexChange"] }, { kind: "component", type: i4.ButtonComponent, selector: "[axis-button]", inputs: ["title", "type"] }, { kind: "component", type: i4.CheckboxComponent, selector: "axis-checkbox", inputs: ["ariaLabel", "tabIndex", "checked", "disabled", "heading", "viewonly", "cid", "name", "rel", "required", "value"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "component", type: i4.IconComponent, selector: "axis-icon,[axis-icon]", inputs: ["data-color", "data-size", "icon", "viewonly", "type", "font"] }, { kind: "component", type: i4.RadioButtonComponent, selector: "axis-radio", inputs: ["ariaLabel", "tabIndex", "heading", "cid", "disabled", "checked", "name", "value", "rel"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "directive", type: i4.TooltipDirective, selector: "[axis-tooltip]", inputs: ["color", "placement", "closeOnOutsideClick", "isOpen", "nested", "tooltipContentHover", "content", "kind", "item", "items", "style", "axisIncludeComponent", "axisIncludeTooltipComponent", "axisInclude", "data", "tooltipTrigger", "minwidth", "width", "isToggleOnClick", "context", "changeDetectorRef", "data-content"], outputs: ["onClose"] }, { kind: "directive", type: i4.PreventDefaultLinkDirective, selector: "[href]", inputs: ["href"] }, { kind: "component", type: AxisDatagridCustomComponent, selector: "axis-datagrid-custom", inputs: ["uiStructure", "uiData", "name"] }, { kind: "pipe", type: i3.DatePipe, name: "date" }, { kind: "pipe", type: i4.AxisInputNumberPipe, name: "axisInputNumberPipe" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'axis-datagrid-virtual-scroll-table', template: "<div\n  class=\"axis-grid-virtual-container axis-table-responsive\"\n  [attr.axis-grid-name]=\"grid.model.config.name\"\n>\n<div class=\"axis-grid\" *ngIf=\"grid?.model?.config?.download\" >\n  <button *ngIf=\"grid?.model?.config?.download?.excel\" class=\"axis-btn\" (click)=\"export('excel')\">Export To Excel</button>\n  <button *ngIf=\"grid?.model?.config?.download?.pdf\" class=\"axis-btn\" (click)=\"export('pdf')\">Export To PDF</button>\n</div>\n  <cdk-virtual-scroll-viewport\n    #scrollViewport\n    *ngIf=\"grid\"\n    [ngClass]=\"grid ? grid.model?.config?.cssGridParent : []\"\n    itemSize=\"{{ grid.model.config.rowHeight }}\"\n    (scrolledIndexChange)=\"sendScrollEvent($event)\"\n  >\n    <table\n    [attr.aria-describedby]=\"grid.model.config.caption ? grid.model.config.name + '_caption' : null\"\n      class=\"axis-table axis-table-basic\"\n      [ngClass]=\"grid ? grid.model.config.cssGridClass : []\"\n      [attr.aria-rowcount]=\"grid.model.body.rows.length\"\n      role=\"grid\"\n      #thisTable\n      >\n      <caption *ngIf=\"grid.model.config.caption\" id=\"{{grid.model.config.name}}_caption\">\n        {{grid.model.config.caption ? grid.model.config.caption : ''}}\n      </caption>\n\n      <thead\n        role=\"rowgroup\"\n        *ngIf=\"grid.model.header.rows.length > 0 && !grid.model.header.config.hidden\"\n        class=\"axis-datagrid-head axis-grid-is-header\"\n        >\n        <tr\n          aria-rowindex=\"1\"\n          class=\"axis-datagrid-row\"\n          role=\"row\"\n          *ngFor=\"let row of grid.model.header.rows\" [ngClass]=\"grid.model.header.rows[0].class\"\n        >\n        <ng-container *ngFor=\"let cell of row.cells; let i = index\">\n          <th\n          [attr.aria-hidden]=\"(!cell.content?.trim('') && i!= 0) || (i === 0 && (cell.type === 'radio' || (cell.type === 'checkbox' && cell.showSelectAll === false)) || (!cell.content?.trim('') && cell.type != 'radio' && cell.type != 'checkbox')) ? 'true' : null\"\n            scope=\"col\"\n            [attr.role]=\"'columnheader'\"\n            [attr.col-index]=\"i\"\n            class=\"axis-datagrid-cell axis-datagrid-cell-header\"\n            [ngClass]=\"cell.class\"\n            [style.top]=\"inverseOfTranslation\"\n            *ngIf=\"!cell.hideColumn\"\n            [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n            [style.width.px]=\"cell.columnsWidth.realWidth\"\n            draggable=\"{{cell.draggable}}\"\n            >\n            <ng-container *ngIf=\"cell.type !== 'checkbox' && cell.type !== 'radio' && cell.showSelectAll !== false && cell.type !== 'expand'\">\n                <span\n                  class=\"axis-grid-content\"\n                  [attr.alias]=\"cell.alias\"\n                  role=\"status\"\n                  [attr.type]=\"cell.type\"\n                  [attr.initial-index]=\"i\"\n                  [attr.aria-label]=\"cell.ariaLabel\"\n                  draggable=\"{{cell.draggable}}\"\n                  [ngClass]=\"[cell.content ? '' : '_invisibility-md-up', cell?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                  (click)=\"headerColumnClicked(cell)\">\n                  <span [innerHtml]=\"cell.content || cell['data-heading']\"></span>\n                  <span *ngIf=\"cell.icon && cell.icon.name\" [attr.aria-label]=\"cell.icon.ariaLabel\" axis-tooltip [nested]=\"true\" [width]=\"cell.icon?.tooltipWidth\" [content]=\"(cell.icon && cell.icon.tooltipContent) ? cell.icon.tooltipContent : ''\">\n                    <axis-icon [icon]=\"cell.icon.name\" [data-size]=\"cell.icon?.iconSize\" [data-color]=\"cell.icon?.iconColor\" [font]=\"cell.icon?.iconFont || 'material'\"></axis-icon>\n                  </span>\n                  </span\n                >\n                <ng-container *ngIf=\"cell.subColumns\">\n                  <ng-container *ngFor=\"let sub of cell.subColumns\">\n                    <div\n                      class=\"axis-grid-content\"\n                      [attr.alias]=\"sub.alias\"\n                      [attr.type]=\"sub.type\"\n                      role=\"status\"\n                      [attr.initial-index]=\"i\"\n                      [attr.aria-label]=\"sub.ariaLabel\"\n                      [ngClass]=\"[sub?.isHeaderClickable ? '_cursor-pointer' : '']\"\n                      (click)=\"headerColumnClicked(cell, sub)\">\n                      <span [innerHtml]=\"sub.content\"></span>\n                      <span *ngIf=\"sub.icon && sub.icon.name\" [attr.aria-label]=\"sub.icon.ariaLabel\" axis-tooltip [width]=\"sub.icon?.tooltipWidth\" [content]=\"(sub.icon && sub.icon.tooltipContent) ? sub.icon.tooltipContent : ''\">\n                        <axis-icon [icon]=\"sub.icon.name\" [data-size]=\"sub.icon?.iconSize\" [data-color]=\"sub.icon?.iconColor\" [font]=\"sub.icon?.iconFont || 'material'\"></axis-icon>\n                      </span>\n                      </div\n                    >\n                  </ng-container>\n                </ng-container>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && cell.showSelectAll !== false\">\n                <span class=\"axis-grid-content\">\n                  <axis-checkbox #checkAll\n                    [disabled]=\"grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n                    id=\"axis-grid-checkbox-header-{{ i }}\"\n                    [ariaLabel]=\"cell.content?.trim('') ? cell.content : 'Select Row'\"\n                    [attr.data-inline]=\"cell.inline ? true : null\"\n                    (changeDetect)=\"selectAllRows($event, grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled)\"\n                    data-heading=\"{{ cell.content }}\"\n                  ></axis-checkbox>\n                </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'checkbox' && !cell.showSelectAll && cell.content\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                  </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'radio'\">\n                <span class=\"axis-grid-content\">\n                  {{cell.content}}\n                </span>\n              </ng-container>\n              <ng-container *ngIf=\"cell.type === 'expand' && grid?.model?.config?.nestedGridOptions?.expandOthers\">\n                <span class=\"axis-grid-content\">\n                  <button axis-button (click)=\"toggleSubGrid(cell)\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n                    ? 'Expanded All'\n                    : 'Collapsed All'\">\n                    <axis-icon\n                        [icon]=\"cell.showAll\n                          ? (grid?.model?.config?.nestedGridOptions?.expandLessIcon ? grid?.model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter' )\n                          : (grid?.model?.config?.nestedGridOptions?.expandMoreIcon ? grid?.model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n                        [data-size]=\"grid?.model?.config?.nestedGridOptions?.iconSize\"\n                        [data-color]=\"grid?.model?.config?.nestedGridOptions?.iconColor\"\n                        [font]=\"grid?.model?.config?.nestedGridOptions?.iconFont || 'custom'\">\n                      </axis-icon>\n                  </button>\n                </span>\n              </ng-container>\n            </th>\n          </ng-container>\n        </tr>\n      </thead>\n      <tbody #tbody>\n        <ng-container\n          *cdkVirtualFor=\"let row of grid.viewRows; let r = index\"\n        >\n          <tr\n            #thisRow\n            [attr.aria-rowindex]=\"r + 2\"\n            role=\"row\"\n            class=\"axis-datagrid-row\"\n            (click)=\"rowClicked(row)\"\n            [ngClass]=\"[(row.selected ? 'row-active': ''), (row.config.background ? 'row-' + row.config.background : ''),(grid.model.header.rows[0].class ? grid.model.header.rows[0].class : ''), ((row?.nestedGridComponent && row?.showNestedGrid) ? 'nested-row' : '')]\"\n            [attr.row-index]=\"r\"\n            [attr.row-real-index]=\"row.rowIndex\"\n          >\n            <ng-container\n              *ngFor=\"let cell of row.cells; let c = index\"\n              #cell\n            >\n              <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.hideColumn\">\n                <td\n                  #thisCell\n                  *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.type || grid?.model?.header?.rows[0]?.cells[c]?.type !== 'custom'\"\n                  [attr.row-index]=\"r\"\n                  [attr.no-truncate]=\"grid?.model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n                  axisDatagridGenericCell\n                  [attr.col-index]=\"c\"\n                  [attr.gridName]=\"grid.model.config.name\"\n                  role=\"gridcell\"\n                  class=\"axis-datagrid-cell\"\n                  [ngClass]=\"[\n                    cell.cellClass ? cell.cellClass : '',\n                    (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                    (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                    cell.background ? 'cell-' + cell.background : '',\n                    (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n                  ]\"\n                  [ngStyle]=\"{\n                    'min-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.min,\n                    'max-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.max\n                  }\"\n                  [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c]['data-heading'] || grid?.model?.header?.rows[0]?.cells[c].content\"\n                  [attr.data-content]=\"(row.cells[c] && row.cells[c]['data-content']) ? row.cells[c]['data-content'] : null\"\n                  [style.width.px]=\"grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.realWidth\"\n                  [attr.cell-index]=\"r + '.' + c\"\n                  [attr.cell-type]=\"grid?.model?.header?.rows[0]?.cells[c]?.type\"\n                  [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n                >\n                  <ng-container\n                    *ngTemplateOutlet=\"content; context: { c: cell, r: row, i:c, type: grid?.model?.header?.rows[0]?.cells[c]?.type, settings:grid?.model?.header?.rows[0]?.cells[c]?.settings }\"\n                  ></ng-container>\n                </td>\n                <td\n                  #thisCell\n                  *ngIf=\"grid?.model?.header?.rows[0]?.cells[c]?.type && grid?.model?.header?.rows[0]?.cells[c]?.type === 'custom'\"\n                  axisDatagridGenericCell\n                  [attr.gridName]=\"grid.model.config.name\"\n                  role=\"gridcell\"\n                  class=\"axis-datagrid-cell\"\n                  [attr.col-index]=\"c\"\n                  [ngClass]=\"[\n                    cell.cellClass ? cell.cellClass : '',\n                    (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                    (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                    cell.background ? 'cell-' + cell.background : '',\n                    (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n                  ]\"\n                  [ngStyle]=\"{\n                    'min-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.min,\n                    'max-width':  grid?.model?.header?.rows[0]?.cells[c]?.columnsWidth?.max\n                  }\"\n                  [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c]['data-heading'] || grid?.model?.header?.rows[0]?.cells[c].content\"\n                  [attr.data-content]=\"(row.cells[c] && row.cells[c]['data-content']) ? row.cells[c]['data-content'] : null\"\n                  [style.width.px]=\"grid.model.header.rows[0].cells[c].columnsWidth.realWidth\"\n                  [attr.cell-index]=\"r + '.' + c\"\n                  [attr.cell-type]=\"grid?.model?.header?.rows[0]?.cells[c]?.type\"\n                >\n                  <axis-datagrid-custom\n                    [uiStructure]=\"cell\"\n                    [uiData]=\"row\"\n                    [name]=\"name\"\n                  ></axis-datagrid-custom>\n                </td>\n              </ng-container>\n            </ng-container>\n          </tr>\n\n          <tr class=\"nested-table\" *ngIf=\"(row?.nestedGridComponent && row?.showNestedGrid)\">\n            <td [attr.colspan]=\"row.cells.length\">\n              <axis-datagrid-custom [uiData]=\"row?.nestedGridData\" [uiStructure]=\"{row, content: row.nestedGridComponent}\" [name]=\"row?.nestedGridData?.config?.name\"></axis-datagrid-custom>\n            </td>\n          </tr>\n\n        </ng-container>\n      </tbody>\n    </table>\n\n    <ng-container *ngIf=\"!grid.model.body.rows.length || !grid?.viewRows?.length\">\n      <div class=\"axis-datagrid-row axis-datagrid-no-results\" role=\"row\">\n        <div class=\"axis-datagrid-cell\" *ngIf=\"!grid.model.config.noResultsUi\">No results.</div>\n        <div class=\"axis-datagrid-cell\" *ngIf=\"grid.model.config.noResultsUi\">\n          <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: grid.model.config.noResultsUi}\"\n            name=\"NoResults\">\n          </axis-datagrid-custom>\n        </div>\n      </div>\n    </ng-container>\n\n  </cdk-virtual-scroll-viewport>\n</div>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\" let-settings=\"settings\">\n  <ng-container *ngIf=\"type === 'expand' && row?.nestedGridComponent\">\n    <button axis-button (click)=\"expandSubRow($event, row);\" data-kind=\"icon\" [attr.aria-label]=\"cell.showAll\n    ? 'Expand'\n    : 'Collapse'\">\n      <axis-icon\n        [icon]=\"row.showNestedGrid ? (grid?.model?.config?.nestedGridOptions?.expandLessIcon ? grid?.model?.config?.nestedGridOptions?.expandLessIcon : 'do_not_enter' ) : (grid?.model?.config?.nestedGridOptions?.expandMoreIcon ? grid?.model?.config?.nestedGridOptions?.expandMoreIcon : 'add_circle_outline')\"\n        [data-size]=\"grid?.model?.config?.nestedGridOptions?.iconSize\" [data-color]=\"grid?.model?.config?.nestedGridOptions?.iconColor\"\n        [font]=\"grid?.model?.config?.nestedGridOptions?.iconFont || 'custom'\"></axis-icon>\n    </button>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\"\n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\">  {{ cell.content | date: (cell.format ? cell.format : 'MM/dd/yyyy') }} </span>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-checkbox #check\n        [disabled]=\"grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\"\n        id=\"axis-grid-checkbox-{{ row.rowIndex }}\" (click)=\"preventClickEmit($event)\"\n        [attr.data-inline]=\"cell.inline ? true : null\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\"\n      >\n      </axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\" (click)=\"preventClickEmit($event)\"\n        [disabled]=\"grid?.model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\"\n        name=\"axis-grid-radio-{{name}}\"  value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\">\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n    <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n        <span [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </span>\n      </ng-container>\n      <ng-container *ngIf=\"cell.click\">\n        <a href (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.route\">\n        <a routerLink=\"/\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.href\">\n        <a [attr.href]=\"cell.href\"\n          [attr.target]=\"cell.target ? cell.target : '_blank'\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </a>\n      </ng-container>\n    </ng-container>\n  <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n    <ng-container *ngIf=\"!cell.badge && !grid?.model.header.rows[0].cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span [ngClass]=\"[subClass ? subClass : '']\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </span>\n      </div>\n    </ng-container>\n    <ng-container *ngIf=\"cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span\n          [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge) ? 'axis-badge' : '']\"\n          [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n          [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n          axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n          [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n          <ng-container *ngIf=\"settings\">\n            {{ cell.content | axisInputNumberPipe:settings}}\n            </ng-container>\n            <ng-container *ngIf=\"!settings\">\n            {{ cell.content}}\n          </ng-container>\n        </span>\n      </div>\n    </ng-container>\n  </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type !== 'custom'\">\n        <ng-container\n        *ngTemplateOutlet=\"content; context: {\n          c: sub,\n          r: row,\n          subClass: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.class,\n          type: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type,\n          settings: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.settings\n          }\"\n        ></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }, { type: i0.ChangeDetectorRef }, { type: i2.Router }]; }, propDecorators: { name: [{
                type: Input
            }], thisTable: [{
                type: ViewChild,
                args: ['thisTable']
            }], check: [{
                type: ViewChildren,
                args: ['check']
            }], checkAll: [{
                type: ViewChild,
                args: ['checkAll']
            }], tbody: [{
                type: ViewChild,
                args: ['tbody']
            }], viewPort: [{
                type: ViewChild,
                args: [CdkVirtualScrollViewport, { static: false }]
            }] } });

class AgpVirtualScrollDivComponent {
    constructor(gridService, cd, router) {
        this.gridService = gridService;
        this.cd = cd;
        this.router = router;
        this.name = null;
        this.allSelected = false;
        this.grid = false;
    }
    ngOnInit() {
        const gs = this.gridService.grids[this.name];
        this.grid = gs;
        const SELECT_ALL_COMPONENT = gs.ACTIONS.SELECT_ALL_COMPONENT.pipe(delay(0)).subscribe((res) => {
            if (res && res.payload.name === this.name) {
                this.selectAllRows(res.payload.data);
            }
        });
        gs.subscriptions.push(SELECT_ALL_COMPONENT);
    }
    selectAllRows(event) {
        if (!this.grid?.model?.config?.disableAllCheckboxes) {
            this.allSelected = event;
            const selectedList = [];
            this.grid.viewRows.forEach((element) => {
                if (!element.cells[0].disabled && !element.cells[0].hide) {
                    element.selected = event;
                    if (event) {
                        selectedList.push(element);
                    }
                }
            });
            if (this.check && this.check.length) {
                this.check.forEach(element => {
                    if (event) {
                        if (!element.checked && !element.disabled && !element.hide) {
                            element.check();
                        }
                    }
                    else {
                        element.unCheck();
                    }
                });
            }
            if (this.allSelected) {
                this.gridService.sendAction(this.name, 'ROWS_SELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
            else {
                this.gridService.sendAction(this.name, 'ROWS_UNSELECTED_ALL', {
                    name: this.name,
                    data: this.allSelected,
                    selectedList: selectedList,
                });
            }
        }
    }
    ngAfterViewInit() {
        this.gridService.grids[this.name].tbody = this.tbody.elementRef;
        this.grid.cdkViewPort = this.viewPort;
        this.grid.cdkScrollToIndex = this.cdkScrollToIndex.bind(this);
        this.grid.cdkChangeDetectorRef = this.cd;
    }
    sendScrollEvent(e) {
        const config = this.grid.model.config;
        let from = e - config.bufferRows;
        if (from < 0) {
            from = 0;
        }
        const to = e + config.viewportCellCount + config.bufferRows;
        this.gridService.sendAction(this.name, 'SCROLL', {
            grid: this.grid,
            name: this.name,
            eventType: 'scroll',
            scrollType: this.grid.model.config.scrollType,
            eventData: e,
            scrollPos: this.tbody['elementRef'].nativeElement.scrollTop,
            from,
            to,
            getMoreData: true,
            model: this.grid.model,
        });
    }
    preventClickEmit(event) {
        if (event.clientX) {
            event.preventDefault();
        }
    }
    rowClicked(row) {
        const headerCell = this.grid?.model?.header?.rows[0].cells.find((cell) => (cell.type === 'checkbox' || cell.type === 'radio'));
        if (headerCell && ((headerCell.type === 'checkbox') || (headerCell.type === 'radio' && !row.selected))) {
            const checked = (row.hasOwnProperty('selected') && row.selected);
            const hide = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('hide') && cell.hide)));
            const disabled = (row.cells.some((cell) => (cell.mapTo === headerCell.alias) && (cell.hasOwnProperty('disabled') && cell.disabled)));
            this.selectRow(checked, row, this.grid?.model?.config?.disableAllRadio || disabled, hide, headerCell.type);
        }
    }
    selectRow(event, row, disabled, hide, type) {
        if (!disabled && !hide) {
            const selectedList = [];
            this.allSelected = true;
            this.grid.viewRows.forEach((element) => {
                if (type == 'radio') {
                    element.selected = false;
                }
                if (element.rowIndex === row.rowIndex) {
                    element.selected = !event;
                    if (element.selected) {
                        this.gridService.sendAction(this.name, 'ROW_SELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: type === 'checkbox' ? selectedList : row,
                            rowIndex: element.rowIndex
                        });
                    }
                    else {
                        this.gridService.sendAction(this.name, 'ROW_UNSELECTED', {
                            name: this.name,
                            data: row,
                            selectedList: type === 'checkbox' ? selectedList : row,
                            rowIndex: element.rowIndex
                        });
                    }
                }
                if (type === 'checkbox') {
                    if (element.selected) {
                        selectedList.push(element);
                    }
                    else {
                        this.allSelected = false;
                    }
                }
            });
            if (type === 'checkbox') {
                this.gridService.sendAction(this.name, 'ROWS_SELECTED_ALL', {
                    name: this.name,
                    updatingCheckAll: this.allSelected
                });
            }
        }
    }
    cdkScrollToIndex(n) {
        this.viewPort.scrollToIndex(n);
    }
    contentClicked(row, columnName, cell) {
        if (cell.route) {
            this.router.navigate([cell.route], { ...cell });
        }
        const payload = {
            content: columnName,
            rowsData: row,
            cell
        };
        this.gridService.isClicked();
        this.gridService.sendAction(this.name, 'ROWS_CLICKED', payload);
    }
}
AgpVirtualScrollDivComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollDivComponent, deps: [{ token: AxisDatagridService }, { token: i0.ChangeDetectorRef }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component });
AgpVirtualScrollDivComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: AgpVirtualScrollDivComponent, selector: "axis-datagrid-virtual-scroll-div", inputs: { name: "name" }, host: { classAttribute: "axis-datagrid-body" }, viewQueries: [{ propertyName: "tbody", first: true, predicate: ["tbody"], descendants: true }, { propertyName: "viewPort", first: true, predicate: CdkVirtualScrollViewport, descendants: true }, { propertyName: "check", predicate: ["check"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"!grid.model.body.rows.length || !grid?.viewRows?.length\">\n  <div class=\"axis-datagrid-row axis-datagrid-no-results\">\n    <ng-container *ngIf=\"!grid.model.config.noResultsUi\">No results.</ng-container>\n    <ng-container *ngIf=\"grid.model.config.noResultsUi\">\n      <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: grid.model.config.noResultsUi}\"\n        name=\"NoResults\">\n      </axis-datagrid-custom>\n    </ng-container>\n  </div>\n</ng-container>\n\n<ng-container *ngIf=\"grid.model.body.rows.length\">\n  <cdk-virtual-scroll-viewport\n    *ngIf=\"grid\"\n    itemSize=\"{{ grid.model.config.rowHeight }}\"\n    [style.height]=\"grid.model.config.gridHeight ? grid.model.config.gridHeight : '200px'\"\n    (scrolledIndexChange)=\"sendScrollEvent($event)\"\n    cdkObserveContent\n    #tbody\n  >\n    <ng-container *cdkVirtualFor=\"let row of grid.viewRows; let r = index\">\n      <div\n        class=\"axis-datagrid-row\"\n        (click)=\"rowClicked(row)\" \n        [ngClass]=\"[(row.selected ? 'row-active' : ''), (row.config.background ? 'row-' + row.config.background : ''),(grid?.model?.header?.rows[0]?.class ? grid?.model?.header?.rows[0]?.class : '')]\"\n        role=\"row\"\n        [attr.row-index]=\"r\"\n        [attr.row-real-index]=\"row.rowIndex\"\n        #thisRow\n      >\n        <ng-container *ngFor=\"let cell of row.cells; let c = index\">\n          <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.hideColumn\">\n            <div\n            [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c].content\"\n              *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.type || grid?.model?.header?.rows[0]?.cells[c]?.type !== 'custom'\"\n              #thisCell\n              axisDatagridGenericCell\n              [attr.no-truncate]=\"grid?.model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n              [attr.col-index]=\"c\"\n              [attr.gridName]=\"grid.model.config.name\"\n              role=\"gridcell\"\n              class=\"axis-datagrid-cell\"\n              [ngClass]=\"[\n                (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                cell.background ? 'cell-' + cell.background : '',\n                (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n              ]\"\n              [attr.cell-index]=\"r + '.' + c\"\n              [attr.row-index]=\"r\"\n              [attr.cell-type]=\"cell.type\"\n              [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n            >\n              <ng-container\n                *ngTemplateOutlet=\"content; context: { c: cell, r: row, i:c, type: grid?.model?.header?.rows[0]?.cells[c]?.type }\"\n              ></ng-container>\n            </div>\n            <div\n            [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c].content\"\n              *ngIf=\"grid?.model?.header?.rows[0]?.cells[c]?.type && grid?.model?.header?.rows[0]?.cells[c]?.type === 'custom'\"\n              #thisCell\n              axisDatagridGenericCell\n              [attr.gridName]=\"grid.model.config.name\"\n              role=\"gridcell\"\n              class=\"axis-datagrid-cell\"\n              [attr.col-index]=\"c\"\n              [ngClass]=\"[\n                (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                cell.background ? 'cell-' + cell.background : '',\n                (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n              ]\"\n              [attr.cell-index]=\"r + '.' + c\"\n              [attr.row-index]=\"r\"\n              [attr.cell-type]=\"grid?.model?.header?.rows[0]?.cells[c]?.type\"\n            >\n              <axis-datagrid-custom\n                [uiStructure]=\"cell\"\n                [uiData]=\"row\"\n                [name]=\"name\"\n              ></axis-datagrid-custom>\n            </div>\n          </ng-container>\n        </ng-container>\n      </div>\n    </ng-container>\n  </cdk-virtual-scroll-viewport>\n</ng-container>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\">\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\"  \n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', \n    (cell.badge || grid?.model?.header?.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\">  {{ cell.content | date: (cell.format ? cell.format : 'MM/dd/yyyy') }} </span>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div\n      class=\"axis-grid-content\"\n      >\n      <axis-checkbox #check\n        [disabled]=\"grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\" \n        id=\"axis-grid-checkbox-{{ row.rowIndex }}\"  (click)=\"preventClickEmit($event)\"\n        data-heading=\"{{ cell['data-heading'] }}\"\n        [attr.data-inline]=\"cell.inline ? true : null\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\"\n        >\n      </axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\"\n        [disabled]=\"grid?.model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\"  (click)=\"preventClickEmit($event)\"  \n        name=\"axis-grid-radio-{{name}}\"  value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        data-heading=\"{{ cell['data-heading'] }}\"  [checked]=\"row.selected\">\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n    <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n        <span [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </span>\n      </ng-container>\n      <ng-container *ngIf=\"cell.click\">\n        <a href (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.route\">\n        <a routerLink=\"/\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.href\">\n        <a [attr.href]=\"cell.href\"\n          [attr.target]=\"cell.target ? cell.target : '_blank'\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n    </ng-container>\n  <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n    <ng-container *ngIf=\"!cell.badge && !grid?.model.header.rows[0].cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span [ngClass]=\"[subClass ? subClass : '']\"> {{ cell.content }}</span>\n      </div>\n    </ng-container>\n    <ng-container *ngIf=\"cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span\n          [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge) ? 'axis-badge' : '']\"\n          [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n          [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n          axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n          [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\"> {{ cell.content }}\n        </span>\n      </div>\n    </ng-container>\n  </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type !== 'custom'\">\n        <ng-container\n        *ngTemplateOutlet=\"content; context: { c: sub, r: row, subClass: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.class, type: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type }\"\n        ></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n", styles: [""], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i4$1.CdkFixedSizeVirtualScroll, selector: "cdk-virtual-scroll-viewport[itemSize]", inputs: ["itemSize", "minBufferPx", "maxBufferPx"] }, { kind: "directive", type: i4$1.CdkVirtualForOf, selector: "[cdkVirtualFor][cdkVirtualForOf]", inputs: ["cdkVirtualForOf", "cdkVirtualForTrackBy", "cdkVirtualForTemplate", "cdkVirtualForTemplateCacheSize"] }, { kind: "component", type: i4$1.CdkVirtualScrollViewport, selector: "cdk-virtual-scroll-viewport", inputs: ["orientation", "appendOnly"], outputs: ["scrolledIndexChange"] }, { kind: "component", type: i4.CheckboxComponent, selector: "axis-checkbox", inputs: ["ariaLabel", "tabIndex", "checked", "disabled", "heading", "viewonly", "cid", "name", "rel", "required", "value"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "component", type: i4.RadioButtonComponent, selector: "axis-radio", inputs: ["ariaLabel", "tabIndex", "heading", "cid", "disabled", "checked", "name", "value", "rel"], outputs: ["changeDetect", "onChangeEvent", "emitBlurEvent"] }, { kind: "directive", type: i4.TooltipDirective, selector: "[axis-tooltip]", inputs: ["color", "placement", "closeOnOutsideClick", "isOpen", "nested", "tooltipContentHover", "content", "kind", "item", "items", "style", "axisIncludeComponent", "axisIncludeTooltipComponent", "axisInclude", "data", "tooltipTrigger", "minwidth", "width", "isToggleOnClick", "context", "changeDetectorRef", "data-content"], outputs: ["onClose"] }, { kind: "directive", type: i4.PreventDefaultLinkDirective, selector: "[href]", inputs: ["href"] }, { kind: "component", type: AxisDatagridCustomComponent, selector: "axis-datagrid-custom", inputs: ["uiStructure", "uiData", "name"] }, { kind: "pipe", type: i3.DatePipe, name: "date" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollDivComponent, decorators: [{
            type: Component,
            args: [{ selector: 'axis-datagrid-virtual-scroll-div', host: {
                        class: 'axis-datagrid-body',
                    }, template: "<ng-container *ngIf=\"!grid.model.body.rows.length || !grid?.viewRows?.length\">\n  <div class=\"axis-datagrid-row axis-datagrid-no-results\">\n    <ng-container *ngIf=\"!grid.model.config.noResultsUi\">No results.</ng-container>\n    <ng-container *ngIf=\"grid.model.config.noResultsUi\">\n      <axis-datagrid-custom [uiData]=\"{}\" [uiStructure]=\"{name:'NoResults', content: grid.model.config.noResultsUi}\"\n        name=\"NoResults\">\n      </axis-datagrid-custom>\n    </ng-container>\n  </div>\n</ng-container>\n\n<ng-container *ngIf=\"grid.model.body.rows.length\">\n  <cdk-virtual-scroll-viewport\n    *ngIf=\"grid\"\n    itemSize=\"{{ grid.model.config.rowHeight }}\"\n    [style.height]=\"grid.model.config.gridHeight ? grid.model.config.gridHeight : '200px'\"\n    (scrolledIndexChange)=\"sendScrollEvent($event)\"\n    cdkObserveContent\n    #tbody\n  >\n    <ng-container *cdkVirtualFor=\"let row of grid.viewRows; let r = index\">\n      <div\n        class=\"axis-datagrid-row\"\n        (click)=\"rowClicked(row)\" \n        [ngClass]=\"[(row.selected ? 'row-active' : ''), (row.config.background ? 'row-' + row.config.background : ''),(grid?.model?.header?.rows[0]?.class ? grid?.model?.header?.rows[0]?.class : '')]\"\n        role=\"row\"\n        [attr.row-index]=\"r\"\n        [attr.row-real-index]=\"row.rowIndex\"\n        #thisRow\n      >\n        <ng-container *ngFor=\"let cell of row.cells; let c = index\">\n          <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.hideColumn\">\n            <div\n            [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c].content\"\n              *ngIf=\"!grid?.model?.header?.rows[0]?.cells[c]?.type || grid?.model?.header?.rows[0]?.cells[c]?.type !== 'custom'\"\n              #thisCell\n              axisDatagridGenericCell\n              [attr.no-truncate]=\"grid?.model?.header?.rows[0]?.cells[c]?.noTruncate ? true : null\"\n              [attr.col-index]=\"c\"\n              [attr.gridName]=\"grid.model.config.name\"\n              role=\"gridcell\"\n              class=\"axis-datagrid-cell\"\n              [ngClass]=\"[\n                (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                cell.background ? 'cell-' + cell.background : '',\n                (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n              ]\"\n              [attr.cell-index]=\"r + '.' + c\"\n              [attr.row-index]=\"r\"\n              [attr.cell-type]=\"cell.type\"\n              [attr.has-subColumns]=\"cell.subColumns ? true : false\"\n            >\n              <ng-container\n                *ngTemplateOutlet=\"content; context: { c: cell, r: row, i:c, type: grid?.model?.header?.rows[0]?.cells[c]?.type }\"\n              ></ng-container>\n            </div>\n            <div\n            [attr.data-heading]=\"grid?.model?.header?.rows[0]?.cells[c].content\"\n              *ngIf=\"grid?.model?.header?.rows[0]?.cells[c]?.type && grid?.model?.header?.rows[0]?.cells[c]?.type === 'custom'\"\n              #thisCell\n              axisDatagridGenericCell\n              [attr.gridName]=\"grid.model.config.name\"\n              role=\"gridcell\"\n              class=\"axis-datagrid-cell\"\n              [attr.col-index]=\"c\"\n              [ngClass]=\"[\n                (row.config.highlight && c === 0) ? 'row-highlight-' + row.config.highlight : '',\n                (row.config.background && c === 0) ? 'row-' + row.config.background : '',\n                cell.background ? 'cell-' + cell.background : '',\n                (grid?.model?.header?.rows[0]?.cells[c]?.class) ? grid?.model?.header?.rows[0]?.cells[c]?.class : ''\n              ]\"\n              [attr.cell-index]=\"r + '.' + c\"\n              [attr.row-index]=\"r\"\n              [attr.cell-type]=\"grid?.model?.header?.rows[0]?.cells[c]?.type\"\n            >\n              <axis-datagrid-custom\n                [uiStructure]=\"cell\"\n                [uiData]=\"row\"\n                [name]=\"name\"\n              ></axis-datagrid-custom>\n            </div>\n          </ng-container>\n        </ng-container>\n      </div>\n    </ng-container>\n  </cdk-virtual-scroll-viewport>\n</ng-container>\n\n<ng-template #content let-cell=\"c\" let-row=\"r\" let-index=\"i\" let-subClass=\"subClass\" let-type=\"type\">\n  <ng-container *ngIf=\"type === 'date'\">\n    <div class=\"axis-grid-content\"  \n    [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', \n    (cell.badge || grid?.model?.header?.rows[0].cells[index]?.badge) ? 'axis-badge' : '']\"\n    [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n    [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n    [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\">\n    <span axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\">  {{ cell.content | date: (cell.format ? cell.format : 'MM/dd/yyyy') }} </span>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'checkbox' && !cell.hide\">\n    <div\n      class=\"axis-grid-content\"\n      >\n      <axis-checkbox #check\n        [disabled]=\"grid?.model?.config?.disableAllCheckboxes\u00A0||\u00A0cell.disabled\" \n        id=\"axis-grid-checkbox-{{ row.rowIndex }}\"  (click)=\"preventClickEmit($event)\"\n        data-heading=\"{{ cell['data-heading'] }}\"\n        [attr.data-inline]=\"cell.inline ? true : null\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        [checked]=\"row.selected\"\n        >\n      </axis-checkbox>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'radio' && !cell.hide\">\n    <div class=\"axis-grid-content\">\n      <axis-radio #radio id=\"axis-grid-radio-{{ row.rowIndex }}\"\n        [disabled]=\"grid?.model?.config?.disableAllRadio\u00A0||\u00A0cell.disabled\"  (click)=\"preventClickEmit($event)\"  \n        name=\"axis-grid-radio-{{name}}\"  value=\"axis-grid-radio-{{name}}{{row.rowIndex}}\"\n        [ariaLabel]=\"cell['data-heading']?.trim('') ? cell['data-heading'] : 'Select Row'\"\n        data-heading=\"{{ cell['data-heading'] }}\"  [checked]=\"row.selected\">\n      </axis-radio>\n    </div>\n  </ng-container>\n  <ng-container *ngIf=\"type === 'text' || type === 'number'\">\n    <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n      <ng-container *ngIf=\"!cell.click && !cell.route && !cell.href\">\n        <span [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </span>\n      </ng-container>\n      <ng-container *ngIf=\"cell.click\">\n        <a href (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.route\">\n        <a routerLink=\"/\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n      <ng-container *ngIf=\"cell.href\">\n        <a [attr.href]=\"cell.href\"\n          [attr.target]=\"cell.target ? cell.target : '_blank'\"\n          (click)=\"contentClicked(row, grid.model.header.rows[0].cells[index].content, cell)\"\n          [ngClass]=\"{ 'axis-grid-content': true }\">{{ cell.content }}\n        </a>\n      </ng-container>\n    </ng-container>\n  <ng-container *ngIf=\"!grid?.model?.header?.rows[0]?.cells[index]?.enableHyperlink\">\n    <ng-container *ngIf=\"!cell.badge && !grid?.model.header.rows[0].cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span [ngClass]=\"[subClass ? subClass : '']\"> {{ cell.content }}</span>\n      </div>\n    </ng-container>\n    <ng-container *ngIf=\"cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge\">\n      <div class=\"axis-grid-content\">\n        <span\n          [ngClass]=\"[subClass ? subClass : '', cell.class ? cell.class : '', (cell.badge || grid?.model?.header?.rows[0]?.cells[index]?.badge) ? 'axis-badge' : '']\"\n          [attr.data-size]=\"(cell.badge && cell.badge.size) ? cell.badge.size : null\"\n          [attr.data-bg]=\"(cell.badge && cell.badge.bg) ? cell.badge.bg : null\"\n          axis-tooltip [content]=\"(cell.badge && cell.badge.tooltipContent) ? cell.badge.tooltipContent : ''\"\n          [attr.data-kind]=\"(cell.badge && cell.badge.kind) ? cell.badge.kind : null\"> {{ cell.content }}\n        </span>\n      </div>\n    </ng-container>\n  </ng-container>\n  </ng-container>\n  <ng-container *ngIf=\"cell.subColumns\">\n    <ng-container *ngFor=\"let sub of cell.subColumns;let subIndex=index;\">\n      <ng-container  *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type !== 'custom'\">\n        <ng-container\n        *ngTemplateOutlet=\"content; context: { c: sub, r: row, subClass: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.class, type: grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type }\"\n        ></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type && grid?.model?.header?.rows[0]?.cells[index]?.subColumns[subIndex]?.type === 'custom'\">\n        <axis-datagrid-custom [uiStructure]=\"sub\" [uiData]=\"row\" [name]=\"name\"></axis-datagrid-custom>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: AxisDatagridService }, { type: i0.ChangeDetectorRef }, { type: i2.Router }]; }, propDecorators: { check: [{
                type: ViewChildren,
                args: ['check']
            }], tbody: [{
                type: ViewChild,
                args: ['tbody']
            }], viewPort: [{
                type: ViewChild,
                args: [CdkVirtualScrollViewport, { static: false }]
            }], name: [{
                type: Input
            }] } });

class AgpVirtualScrollDirective {
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.model = null;
        this.canStart = false;
        this.name = null;
        this.grid = null;
        this.typeOfGrid = null;
        this.config = null;
        this.virtualComponent = null;
    }
    ngOnInit() {
        this.prepareDirective();
        this.addSubscriptions();
    }
    prepareDirective() {
        this.name = this.model.config.name;
        const gs = this.gridService;
        this.grid = gs.grids[this.name];
        if (this.grid.model.config.hasOwnProperty('scrollType') &&
            this.grid.model.config.scrollType === 'virtual') {
            this.grid.registeredPlugins['virtualScroll'] = {};
            this.grid.registeredPlugins._sequence.push('virtualScroll');
            this.canStart = true;
            this.typeOfGrid = this.grid.model.config['grid-type'];
            gs.grids[this.name].dataPipeLine[554] = this.virtualScroll.bind(this);
            this.grid.model.config.cssGridClass.push('axis-datagrid-virtual-scroll');
            this.reviewConfig(this.grid.model);
            gs.changeLastChanged(this.name);
            gs.getRows(this.name);
        }
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            const STAGE_2 = gs.ACTIONS.STAGE_2.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    const isFixedColumn = Object.keys(this.grid.registeredPlugins).indexOf('fixedColumn');
                    if (this.canStart && (this.model.config.agpFixed || this.model.config.agpFixedColum || isFixedColumn > -1 || this.model.config.agpSticky)) {
                        this.startDirective();
                        this.createVirtualComponent();
                        this.gridService.sendAction(this.name, 'VIRTUAL_SCROLL_STAGE_3', { name: this.name });
                    }
                    else if (!this.model.config.agpFixed) {
                        Logger.warn('Axis Grid: ', 'Virtual scroll requires the agpFixed.');
                    }
                }
            });
            gs.subscriptions.push(STAGE_2);
        }
    }
    createVirtualComponent() {
        // Add Virtual Scroll Component
        if (!this.virtualComponent) {
            if (this.typeOfGrid === 'table-grid') {
                this.virtualComponent = AgpVirtualScrollTableComponent;
            }
            else {
                this.virtualComponent = AgpVirtualScrollDivComponent;
            }
            const componentRef = this.grid.virtualFrame.createComponent(this.virtualComponent);
            componentRef.instance['name'] = this.name;
            componentRef.changeDetectorRef.detectChanges();
        }
    }
    reviewConfig(model) {
        // Merge config from JSON into plugin's default config
        if (!model.hasOwnProperty('config')) {
            model.config = {};
        }
        if (!model.config.hasOwnProperty('rowHeight')) {
            model.config.rowHeight = 30;
        }
        if (!model.config.hasOwnProperty('scrollType')) {
            model.config.scrollType = 'virtual';
        }
        if (model.config.scrollType !== 'virtual') {
            Logger.error('Axis-grid:', 'You are using the VirtualScroll plugin, but the config is set to another kind of scrollType.', model.config);
        }
        this.config = model.config;
    }
    startDirective() {
        // Listen for GRID_UPDATE event
        if (this.grid.model.config.useApiOnScroll) {
            const gs = this.gridService.grids[this.name];
            const UPDATE_GRID = gs.ACTIONS.UPDATE_GRID.subscribe((res) => {
                if (res) {
                    if (this.grid.model.config.useApiOnScroll) {
                        this.gridService.changeLastChanged(this.name);
                        this.virtualScroll(this.name, res.payload);
                    }
                }
            });
            gs.subscriptions.push(UPDATE_GRID);
        }
    }
    goGetData(e) {
        const gs = this.gridService;
        // If changed to a new row
        if (this.grid.oldScrollPos !== gs.currentTopRow(this.name)) {
            gs.calculate(this.name);
            this.grid.oldScrollPos = gs.currentTopRow(this.name);
            const scrollInfo = {
                eventType: 'scroll',
                scrollType: this.grid.model.config.scrollType,
                eventData: e,
                scrollPos: this.bodyRef.scrollTop,
                from: this.grid.showFrom,
                to: this.grid.showTo,
                getMoreData: true,
                model: this.grid.model,
            };
            gs.sendAction(this.name, 'SCROLL', scrollInfo);
            if (!this.grid.model.config.useApiOnScroll) {
                gs.changeLastChanged(this.name);
                gs.getRows(this.name);
            }
        }
    }
    virtualScroll(_name = '', res = null) {
        const newRows = [];
        if (this.grid.model.lastChanged === 0 ||
            this.grid.model.lastChanged !== this.grid.lastChanged) {
            const isUsingApi = this.grid.model.config.useApiOnScroll;
            if (isUsingApi) {
                if (this.grid.isFirstLoad) {
                    this.grid.isFirstLoad = false;
                    const start = this.grid.model.body.rows.length;
                    const tmpArray = this.grid.model.body.rows.slice(0);
                    tmpArray.forEach((r) => newRows.push(r.rowIndex));
                    const totalRows = this.grid.model.body.length;
                    for (let i = start; i < totalRows; i++) {
                        tmpArray.push({ config: {}, cells: [{}] });
                    }
                    this.grid.viewRows = tmpArray.slice(0);
                }
                else {
                    // If it is not first load
                    if (res) {
                        res.body.rows.forEach((r) => {
                            this.grid.viewRows[r.rowIndex] = Object.assign({}, r);
                            newRows.push(r.rowIndex);
                        });
                        // This is an inefficient code, and we need to find a work around
                        this.grid.viewRows = [...this.grid.viewRows];
                        this.grid.cdkChangeDetectorRef.detectChanges();
                    }
                }
                setTimeout(() => {
                    this.gridService.sendAction(this.name, 'GRID_HAS_CHANGED', {
                        name: this.name,
                        grid: this.grid,
                        newRows,
                    });
                }, 1);
            }
            // If it is not using Api for more data on scroll
            // then it does not change this.grid.viewRows
        }
    }
}
AgpVirtualScrollDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpVirtualScrollDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpVirtualScrollDirective, selector: "[agpVirtualScroll]", inputs: { model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpVirtualScroll]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { model: [{
                type: Input
            }] } });

class AgpVirtualScrollModule {
}
AgpVirtualScrollModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpVirtualScrollModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollModule, declarations: [AgpVirtualScrollDirective,
        AgpVirtualScrollTableComponent,
        AgpVirtualScrollDivComponent], imports: [CommonModule,
        RouterModule,
        ScrollingModule,
        AxisComponentsAllModule,
        AxisDatagridSharedModule], exports: [AgpVirtualScrollDirective] });
AgpVirtualScrollModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollModule, providers: [AxisDatagridService], imports: [CommonModule,
        RouterModule,
        ScrollingModule,
        AxisComponentsAllModule,
        AxisDatagridSharedModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpVirtualScrollModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AgpVirtualScrollDirective,
                        AgpVirtualScrollTableComponent,
                        AgpVirtualScrollDivComponent,
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                        ScrollingModule,
                        AxisComponentsAllModule,
                        AxisDatagridSharedModule,
                    ],
                    providers: [AxisDatagridService],
                    exports: [AgpVirtualScrollDirective]
                }]
        }] });

class FixedColumnDirective {
    constructor(renderer, gridService) {
        this.renderer = renderer;
        this.gridService = gridService;
        this.model = null;
    }
    ngOnInit() {
        this.name = this.model.config.name;
        if (!this.gridService.grids[this.name]) {
            return;
        }
        this.grid = this.gridService.grids[this.name];
        setTimeout(() => { this.addSubscriptions(); }, 0);
    }
    addSubscriptions() {
        if (this.gridService.grids[this.name]) {
            const gs = this.gridService.grids[this.name];
            const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.grid.registeredPlugins['fixedColumn'] = {};
                    this.grid.registeredPlugins._sequence.push('fixedColumn');
                    this.startDirective('STAGE_1');
                }
            });
            gs.subscriptions.push(STAGE_1);
            const SCROLL = gs.ACTIONS.SCROLL.pipe(delay(0)).subscribe((res) => {
                if (res) {
                    this.startDirective('scroll');
                }
            });
            gs.subscriptions.push(SCROLL);
        }
    }
    startDirective(eventType) {
        let fixedColumn = this.grid.model.config.agpFixedColumn;
        if (fixedColumn && (fixedColumn.left || fixedColumn.right)) {
            this.gridType = this.model.config['grid-type'];
            const tableElement = this.grid.model.config.scrollType === 'virtual' && this.gridType === 'table-grid' ?
                document.querySelector('axis-datagrid-virtual-scroll-table table') : this.grid.thisGrid.nativeElement;
            if (tableElement) {
                let tableStyle = document.createElement('style');
                tableStyle.id = `axis-grid-${this.name}-style`;
                const selectors = this.gridType === 'div-grid' ?
                    ['[role="columnheader"]', '.axis-datagrid-cell'] : ['thead th', 'tr td'];
                this.renderer.addClass(tableElement, 'axis-table-fixed-column');
                if (eventType === 'STAGE_1') {
                    setTimeout(() => {
                        this.appendClasses(tableElement, fixedColumn, tableStyle, selectors);
                    }, 1000);
                }
                else {
                    this.appendClasses(tableElement, fixedColumn, tableStyle, selectors);
                }
            }
        }
    }
    // add class
    appendClasses(tableElement, fixedColumn, tableStyle, selectors) {
        const headerCells = tableElement.querySelectorAll(selectors[0]);
        const cells = tableElement.querySelectorAll(selectors[1]);
        this.columnWidths = this.getColumnWidths(headerCells);
        const cellCount = headerCells.length;
        this.renderer.addClass(tableElement, 'axis-table-fixed-column');
        const fixedColumnArray = this.getFixedColumnArray(fixedColumn, cellCount);
        fixedColumnArray.forEach(element => {
            this.applyStylesToCells(element, tableElement, this.columnWidths, cellCount, tableStyle);
            this.appendClass(headerCells, 'axis-fixed-col', element);
            this.appendClass(headerCells, 'axis-fixed-col-th', element);
            this.appendClass(cells, 'axis-fixed-col', element);
        });
        tableElement.parentElement.querySelectorAll('style').forEach((_element, index) => {
            if (index != 0) {
                tableElement.parentElement.removeChild(tableElement.parentElement.querySelectorAll('style')[index]);
            }
        });
        tableElement.parentElement.appendChild(tableStyle);
    }
    appendClass(cells, className, element) {
        cells.forEach(cell => {
            if (parseInt(cell.getAttribute('col-index'), 10) === element) {
                this.renderer.addClass(cell, className);
            }
        });
    }
    // adding styles to each cell
    applyStylesToCells(cellIndex, tableElement, columnWidths, cellCount, tableStyle) {
        if (cellIndex === 0) {
            this.renderer.addClass(tableElement, 'axis-fixed-col-first');
        }
        else if (cellIndex === cellCount - 1) {
            this.renderer.addClass(tableElement, 'axis-fixed-col-last');
        }
        else {
            let left = columnWidths[cellIndex - 1] + 'px';
            if (this.gridType === 'div-grid') {
                tableStyle.innerHTML += `
          div[col-index='${cellIndex}'] {
            left:${left};
          }`;
            }
            else {
                tableStyle.innerHTML += `
          td[col-index='${cellIndex}'],th[col-index='${cellIndex}'] {
            left:${left};
          }`;
            }
        }
    }
    // get the columns to be fixed
    getFixedColumnArray(fixedColumn, cellCount) {
        const fixedColumnArray = [];
        for (let i = 1; i <= fixedColumn.left; i++) {
            fixedColumnArray.push(i - 1);
        }
        for (let i = cellCount; cellCount - fixedColumn.right < i; i--) {
            fixedColumnArray.push(i - 1);
        }
        return fixedColumnArray;
    }
    // to find the column widths of th
    getColumnWidths(headerCells) {
        const columnWidths = [];
        columnWidths.push(Math.ceil(headerCells[0].clientWidth));
        for (let i = 1; i < headerCells.length; i++) {
            const fMarginWidth = headerCells[i - 1].getBoundingClientRect().right;
            const sMarginWidth = headerCells[i].getBoundingClientRect().left;
            const marginDifference = sMarginWidth - fMarginWidth;
            columnWidths.push(Math.ceil(headerCells[i].clientWidth + columnWidths[i - 1] + marginDifference));
        }
        return columnWidths;
    }
}
FixedColumnDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FixedColumnDirective, deps: [{ token: i0.Renderer2 }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
FixedColumnDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: FixedColumnDirective, selector: "[agpFixedColumn]", inputs: { model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FixedColumnDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpFixedColumn]',
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: AxisDatagridService }]; }, propDecorators: { model: [{
                type: Input
            }] } });

class AgpFixedColumnModule {
}
AgpFixedColumnModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedColumnModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpFixedColumnModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedColumnModule, declarations: [FixedColumnDirective], imports: [CommonModule,
        RouterModule,
        AxisDatagridSharedModule], exports: [FixedColumnDirective] });
AgpFixedColumnModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedColumnModule, providers: [AxisDatagridService], imports: [CommonModule,
        RouterModule,
        AxisDatagridSharedModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpFixedColumnModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        FixedColumnDirective,
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                        AxisDatagridSharedModule,
                    ],
                    providers: [AxisDatagridService],
                    exports: [FixedColumnDirective]
                }]
        }] });

// tslint:disable:no-string-literal
class AgpStickyDirective {
    constructor(gridService) {
        this.gridService = gridService;
        this.AgpStickyDirective = null;
        this.model = null;
    }
    ngOnInit() {
        this.name = this.model.config.name;
        if (!this.gridService.grids[this.name]) {
            return;
        }
        const gs = this.gridService.grids[this.name];
        this.grid = gs;
        const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
            if (res) {
                this.grid.registeredPlugins['sticky'] = {};
                this.grid.registeredPlugins._sequence.push('sticky');
                this.startDirective();
            }
        });
        gs.subscriptions.push(STAGE_1);
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpSticky')) {
            config.agpSticky = true;
        }
    }
    startDirective() {
        this.pluginConfig();
        if (this.grid.model.config['agpSticky']) {
            this.grid.model.config.cssGridClass.push('axis-table-sticky');
            this.grid.changeDetection.detectChanges();
        }
    }
}
AgpStickyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStickyDirective, deps: [{ token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpStickyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpStickyDirective, selector: "[agpSticky]", inputs: { AgpStickyDirective: "AgpStickyDirective", model: "model" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStickyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpSticky]',
                }]
        }], ctorParameters: function () { return [{ type: AxisDatagridService }]; }, propDecorators: { AgpStickyDirective: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class AgpStickyModule {
}
AgpStickyModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStickyModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpStickyModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpStickyModule, declarations: [AgpStickyDirective], imports: [CommonModule], exports: [AgpStickyDirective] });
AgpStickyModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStickyModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpStickyModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpStickyDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpStickyDirective],
                }]
        }] });

// tslint:disable:no-string-literal
class AgpMaxHeightDirective {
    set axisMaxHeightFooter(value) {
        if (value === undefined || value === null || value === '') { // don't require a default setting
            return;
        }
        else if (value !== this.heightPrevious) { // if changed, then need to recalculate
            this.height = value; // reset value
            this.heightPrevious = value; // record for future change
            this.checkElement();
        }
    }
    constructor(el, gridService) {
        this.el = el;
        this.gridService = gridService;
        this.model = null;
        this.name = '';
        this.height = 120; // default; allow adjustment on page for spacing below wrapper element
        this.heightPrevious = 120; // initially same as height; enables switching between default and alternative heights
    }
    /* lifecycle hooks */
    ngOnInit() {
        this.name = this.model.config.name;
        if (!this.gridService.grids[this.name]) {
            return;
        }
        const gs = this.gridService.grids[this.name];
        this.grid = gs;
        this.pluginConfig();
        const STAGE_1 = gs.ACTIONS.STAGE_1.pipe(delay(0)).subscribe((res) => {
            if (res && this.grid.model.config['agpMaxHeight']) {
                this.grid.registeredPlugins['agpMaxHeight'] = {};
                this.grid.registeredPlugins._sequence.push('agpMaxHeight');
            }
        });
        gs.subscriptions.push(STAGE_1);
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.checkElement();
        }, 1000);
    }
    /* methods */
    onResize() {
        setTimeout(() => {
            this.checkElement();
        }, 1000);
    }
    checkElement() {
        if (!this.grid.model.config['agpMaxHeight'] || window.innerWidth < 767) { // disable behavior below 767 pixels
            return;
        }
        this.gridConfigTableHeight = this.grid.model.config.gridHeight.split('px');
        if (this.el.nativeElement.querySelectorAll("axis-datagrid-virtual-scroll-div").length > 0) {
            this.divVersionGridHeight();
        }
        else if (this.el.nativeElement.querySelectorAll("axis-datagrid-virtual-scroll-table").length > 0) {
            this.tableVersionGridHeight();
        }
    }
    tableVersionGridHeight() {
        const tableContainer = this.el.nativeElement.querySelectorAll('.axis-grid-virtual-container');
        const rowTableHeight = this.calculateRowHeight() + this.grid.model.config.rowHeight;
        if (rowTableHeight < this.gridConfigTableHeight[0]) {
            tableContainer[0].style["max-height"] = rowTableHeight + "px";
        }
    }
    divVersionGridHeight() {
        const table = this.el.nativeElement.querySelectorAll('.cdk-virtual-scroll-viewport');
        const rowTableHeight = this.calculateRowHeight();
        if (rowTableHeight < this.gridConfigTableHeight[0]) {
            table[0].style.height = rowTableHeight + "px";
        }
    }
    calculateRowHeight() {
        return this.grid.model.config.totalCount * this.grid.model.config.rowHeight;
    }
    pluginConfig() {
        const config = this.grid.model.config;
        if (!config.hasOwnProperty('agpMaxHeight')) {
            config.agpMaxHeight = true;
        }
    }
}
AgpMaxHeightDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMaxHeightDirective, deps: [{ token: i0.ElementRef }, { token: AxisDatagridService }], target: i0.ɵɵFactoryTarget.Directive });
AgpMaxHeightDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: AgpMaxHeightDirective, selector: "[agpMaxHeight]", inputs: { AgpMaxHeightDirective: "AgpMaxHeightDirective", model: "model", axisMaxHeightFooter: "axisMaxHeightFooter" }, host: { listeners: { "window:resize": "onResize()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMaxHeightDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[agpMaxHeight]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AxisDatagridService }]; }, propDecorators: { AgpMaxHeightDirective: [{
                type: Input
            }], model: [{
                type: Input
            }], axisMaxHeightFooter: [{
                type: Input
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', []]
            }] } });

class AgpMaxHeightModule {
}
AgpMaxHeightModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMaxHeightModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgpMaxHeightModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AgpMaxHeightModule, declarations: [AgpMaxHeightDirective], imports: [CommonModule], exports: [AgpMaxHeightDirective] });
AgpMaxHeightModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMaxHeightModule, providers: [AxisDatagridService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AgpMaxHeightModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AgpMaxHeightDirective],
                    imports: [CommonModule],
                    providers: [AxisDatagridService],
                    exports: [AgpMaxHeightDirective],
                }]
        }] });

/*
 * Public API Surface of axis-datagrid
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AgpCellStyleDirective, AgpCellStyleModule, AgpCondensedDirective, AgpCondensedModule, AgpDragColumnDirective, AgpDragColumnModule, AgpFilterDirective, AgpFilterModule, AgpFixedColumnModule, AgpFixedDirective, AgpFixedModule, AgpInCellEditDirective, AgpInCellEditModule, AgpIncrementalScrollDirective, AgpIncrementalScrollModule, AgpKeyboardNavDirective, AgpKeyboardNavModule, AgpMaxHeightDirective, AgpMaxHeightModule, AgpMouseColorDirective, AgpMouseColorModule, AgpResizeDirective, AgpResizeModule, AgpRowStyleDirective, AgpRowStyleModule, AgpSortDirective, AgpSortModule, AgpStickyDirective, AgpStickyModule, AgpStripedDirective, AgpStripedModule, AgpTooltipDirective, AgpTooltipModule, AgpTruncateDirective, AgpTruncateModule, AgpVirtualScrollDirective, AgpVirtualScrollModule, AxisDatagridDivComponent, AxisDatagridModule, AxisDatagridService, AxisDatagridTableComponent, AxisGridGenericCellDirective, FixedColumnDirective };
